import React from 'react';
import JSZip from 'jszip';
import { AssessmentScoreChart } from './AssessmentScoreChart';
import { Button } from './Button';
import { ClientBriefingPanel, useClientBriefing } from './ClientBriefingPanel';
import { ClientContentPanel, useClientContent } from './ClientContentPanel';
import { ClientCustomProperties, getCustomPropertiesError } from './ClientCustomProperties';
import { ClinicalNotesEditor, formatClinicalNotesForPlainText } from './ClinicalNotesEditor';
import { CollapsiblePanel } from './CollapsiblePanel';
import { EngagementChart } from './EngagementChart';
import { FileViewer } from './FileViewer';
import { FunnelWidget } from './FunnelWidget';
import { InlineEditableTimelineField } from './InlineEditableTimelineField';
import { MarkdownText } from './MarkdownText';
import { PhoneNumberInput } from './PhoneNumberInput';
import { ReportAttachmentContentPanel } from './ReportAttachmentContentPanel';
import { escapeHtml, hasRichTextHtml, normalizeRichTextHtml, RichTextEditor } from './RichTextEditor';
import { SessionAudioPlayer } from './SessionAudioPlayer';
import { TableRowEllipsisMenuButton } from './TableRowEllipsisMenuButton';
import { TimelineUploadDialog } from './TimelineUploadDialog';
import { TIMELINE_FILE_ACCEPT } from '../timelineFileUpload';
import { TabsView } from './TabsView';
import { getUserTypeLabel, UserTypeTag } from './UserTypeTag';
import { AssessmentDefinition, Client, ClientActivityPoint, ClientCustomProperty, SubscribeClientBriefing, SubscribeClientContent, FamilySponsorshipStatus, Nullable, ReportAttachmentContent, ReportTemplate, SessionRecordingClient, SpeechNeed, Therapist, TherapySession, TherapySessionParticipant, TherapySessionPlacedTag, TherapySessionReport, TherapySessionReportGenerationRequest, TherapySessionTagDefinition, TherapySessionTranscriptSpeakerAssignment, TherapySessionTranscriptUtterance, TimelineDocument, TimelineFileUploadOptions, UserResponseRecord, UserType } from '../interfaces/Frameworks';
import { Theme } from '../Theme';
import type { ClientPlanCatalogue, ClientPlanOverride, GeneratedClientPlan } from '../interfaces/Frameworks';
import { getClientSpeechNeedNames, getClientSpeechNeeds, isCareRecipientClient } from '../clientSpeechNeeds';
import {
    formatDateTimeLocalValue,
    getFirstFirestoreTimestampDate,
    parseDateTimeLocalValue,
} from '../firestoreTimestamp';

export type ClientRecordViewProps = {
    authenticatedUserType: UserType;
    authenticatedUserId: string;
    canUseAudioScribeAndReports: boolean;
    canUseClientPlans?: boolean;
    canViewBriefingDiagnostics?: boolean;
    selectedClient?: Client;
    selectedTeamMember?: Therapist;
    canUseTimelineDocuments?: boolean;
    onSubscribeClientBriefing?: SubscribeClientBriefing;
    onSubscribeClientContent?: SubscribeClientContent;
    onLoadClientPlanCatalogue?: (clientId: string) => Promise<ClientPlanCatalogue>;
    onSubmitClientPlanOverride?: (clientId: string, goalIds: string[]) => Promise<ClientPlanOverride>;
    onGenerateClientPlan?: (clientId: string, instructions: string) => Promise<GeneratedClientPlan>;
    hasUsedTherapyApp?: boolean;
    onRequestClientBriefing?: (clientId: string, force?: boolean) => Promise<{ status: string }>;
    allClients: Array<Client>;
    activityTimelineByClientId: Record<string, Array<ClientActivityPoint>>;
    sessionMinutesTimelineByClientId: Record<string, Array<ClientActivityPoint>>;
    signUpTimeByClientId: Record<string, string>;
    stageByClientId: Record<string, string>;
    assessmentDefinitions: Array<AssessmentDefinition>;
    selectedClientAssessmentResponses: Array<UserResponseRecord>;
    isLoadingSelectedClientAssessments: boolean;
    therapySessions: Array<TherapySession>;
    therapySessionReports: Array<TherapySessionReport>;
    timelineDocuments: Array<TimelineDocument>;
    therapists: Array<Therapist>;
    currentTherapist: Nullable<Therapist>;
    reportTemplates: Array<ReportTemplate>;
    speechNeeds: Array<SpeechNeed>;
    isEditing: boolean;
    editingClientData: Nullable<Client>;
    defaultCommsCampaignId?: string;
    showFamilySponsorship: boolean;
    familySponsorshipStatus: FamilySponsorshipStatus | null;
    isLoadingFamilySponsorship: boolean;
    familySponsorshipError: string;
    canManageFamilySponsorship: boolean;
    onBack: () => void;
    onImportSessionFile: (
        client: SessionRecordingClient,
        file: File,
        options?: TimelineFileUploadOptions
    ) => Promise<string>;
    onSaveSessionTitle: (sessionId: string, title: string) => Promise<void>;
    onSaveSessionDate: (sessionId: string, date: Date) => Promise<void>;
    onSaveSessionClinicalNotes: (sessionId: string, clinicalNotes: string) => Promise<void>;
    onSaveSessionTranscriptUtterances: (sessionId: string, utterances: Array<TherapySessionTranscriptUtterance>) => Promise<void>;
    onSaveSessionPlacedTags: (
        sessionId: string,
        placedTags: Array<TherapySessionPlacedTag>,
        previousPlacedTags?: Array<TherapySessionPlacedTag>
    ) => Promise<void>;
    onSaveTherapistSessionTags: (therapistId: string, tags: Array<TherapySessionTagDefinition>) => Promise<void>;
    onEnsureSessionWaveform: (sessionId: string) => Promise<void>;
    onRetrySessionTranscription?: (sessionId: string) => Promise<void>;
    onGenerateSessionReport: (request: TherapySessionReportGenerationRequest) => Promise<string>;
    onGetReportAttachmentContent?: (clientId: string, documentId: string) => Promise<ReportAttachmentContent>;
    onSaveReportAttachmentContent?: (clientId: string, documentId: string, sourceVersion: string, text: string) => Promise<void>;
    onSaveSessionReportText: (reportId: string, reportText: string) => Promise<void>;
    onDeleteSessionReport: (report: TherapySessionReport) => Promise<void>;
    onDeleteSession: (session: TherapySession) => Promise<void>;
    onCreateTimelineClinicalNote: (document: Omit<TimelineDocument, 'id' | 'kind'>) => Promise<string>;
    onSaveTimelineClinicalNote: (documentId: string, clinicalNotes: string) => Promise<void>;
    onSaveTimelineDocumentTitle: (documentId: string, title: string) => Promise<void>;
    onSaveTimelineDocumentDate: (documentId: string, date: Date) => Promise<void>;
    onUploadTimelineFile: (
        document: Omit<TimelineDocument, 'id' | 'kind' | 'storagePath' | 'originalName' | 'contentType' | 'sizeBytes'>,
        file: File,
        options?: TimelineFileUploadOptions
    ) => Promise<string>;
    onGetTimelineFileDownloadUrl: (document: TimelineDocument) => Promise<string>;
    onDownloadTimelineFile: (document: TimelineDocument) => Promise<void>;
    onDeleteTimelineDocument: (document: TimelineDocument) => Promise<void>;
    onStartEditing: () => void;
    onDiscardEditing: () => void;
    onSaveClient: () => void;
    onAssignMyClient: () => void;
    onEditClientChange: (field: keyof Client, value: string) => void;
    onEditClientCustomPropertiesChange: (properties: ClientCustomProperty[]) => void;
    onEditClientSpeechNeedsChange: (speechNeeds: string[]) => void;
    onCommsCampaignEnrollmentChange: (isEnrolled: boolean) => void;
    onSelectClient: (client: Client) => void;
    onSetFamilySponsorship: (sponsored: boolean) => Promise<void>;
    onDeleteClient?: (client: Client) => void;
};

type ClientWithOptionalRole = Client & {
    role?: string;
};

type AssessmentPoint = {
    date: string;
    score: number;
    response: UserResponseRecord;
    expandedText: string | null;
};

type AssessmentSection = {
    definition: AssessmentDefinition;
    responses: Array<UserResponseRecord>;
    points: Array<AssessmentPoint>;
};

type TranscriptRole = 'therapist' | 'client' | 'other';
type SpeakerTaggedSegment = {
    role: TranscriptRole;
    text: string;
};
type SessionNotesSaveStatus = 'pending' | 'saving' | 'saved';
type TranscriptAssignmentScope = 'speaker' | 'line';
type TranscriptAssignmentSubmenuState = {
    utteranceKey: string;
    scope: TranscriptAssignmentScope;
};
type TranscriptAssignablePerson = {
    key: string;
    personId: string;
    personType: TherapySessionTranscriptSpeakerAssignment['personType'];
    displayName: string;
    transcriptRole: TranscriptRole;
    menuLabel?: string;
    userType?: UserType;
};
type SessionSeekRequest = {
    timestampMs: number;
    requestId: number;
};
type SessionSummaryPersonContext = {
    key: string;
    name: string;
    role: string;
    age?: string;
    dateOfBirth?: string;
    supportAreas?: string;
    customProperties?: ClientCustomProperty[];
};
type SessionReportPromptSession = {
    kind: 'session';
    key: string;
    session: TherapySession;
    sessionTitle: string;
    utterances: Array<TherapySessionTranscriptUtterance>;
    clinicalNotes: string;
};
type TimelineReportPromptDocument = {
    kind: 'clinical_note' | 'file';
    key: string;
    document: TimelineDocument;
    clinicalNotes?: string;
};
type TimelineReportPromptItem = SessionReportPromptSession | TimelineReportPromptDocument;
type BuildSessionReportPromptParams = {
    items: Array<TimelineReportPromptItem>;
    reportTemplate?: ReportTemplate | null;
    selectedClient: Client;
    relatedClientSet: Set<Client>;
    therapists: Array<Therapist>;
    currentTherapist: Nullable<Therapist>;
    speechNeeds: Array<SpeechNeed>;
};
type TimelineItem =
    | { kind: 'session'; key: string; id: string; dateMs: number; session: TherapySession }
    | { kind: 'document'; key: string; id: string; dateMs: number; document: TimelineDocument };
type TimelineItemRenderContext = {
    renderAudioSession: (item: Extract<TimelineItem, { kind: 'session' }>) => React.ReactNode;
    renderClinicalNote: (item: Extract<TimelineItem, { kind: 'document' }>) => React.ReactNode;
    renderUploadedFile: (item: Extract<TimelineItem, { kind: 'document' }>) => React.ReactNode;
    renderUnknown: (item: TimelineItem) => React.ReactNode;
};
type TimelineItemHandler = {
    canRender: (item: TimelineItem) => boolean;
    render: (item: TimelineItem, context: TimelineItemRenderContext) => React.ReactNode;
};

const EMPTY_SESSION_UTTERANCES: Array<TherapySessionTranscriptUtterance> = [];
// Team timelines never use a Client identity; unused profile-only helpers receive this empty display value.
const EMPTY_CLIENT_DISPLAY: Client = { userType: 'therapist' };
const SESSION_NOTES_AUTOSAVE_DELAY_MS = 900;
const REPORT_TEXT_ZOOM_MIN = 0.8;
const REPORT_TEXT_ZOOM_MAX = 1.5;
const REPORT_TEXT_ZOOM_STEP = 0.1;
const REPORT_TEXT_ZOOM_STORAGE_PREFIX = 'clientRecord.reportTextZoom';
const SESSION_TAG_COLOURS = [
    '#2563eb',
    '#f97316',
    '#16a34a',
    '#dc2626',
    '#7c3aed',
    '#0891b2',
    '#ca8a04',
    '#db2777',
    '#4f46e5',
    '#059669',
    '#ea580c',
    '#be123c',
    '#0d9488',
    '#9333ea',
    '#65a30d',
    '#0284c7',
    '#c026d3',
    '#d97706',
    '#15803d',
    '#e11d48',
    '#6366f1',
    '#0f766e',
    '#a16207',
    '#be185d',
];
// Anonymous role choices let therapists fix speakers without binding to a specific record.
const ANONYMOUS_THERAPIST_TRANSCRIPT_PERSON: TranscriptAssignablePerson = {
    key: 'anonymous:therapist',
    personId: 'anonymous-therapist',
    personType: 'therapist',
    displayName: 'Therapist',
    transcriptRole: 'therapist',
    menuLabel: '',
};
const ANONYMOUS_CLIENT_TRANSCRIPT_PERSON: TranscriptAssignablePerson = {
    key: 'anonymous:client',
    personId: 'anonymous-client',
    personType: 'client',
    displayName: 'Client',
    transcriptRole: 'client',
    menuLabel: '',
};
const OTHER_TRANSCRIPT_PERSON: TranscriptAssignablePerson = {
    key: 'other',
    personId: 'other',
    personType: 'other',
    displayName: 'Other',
    transcriptRole: 'other',
    menuLabel: '',
};
const TIMELINE_ITEM_HANDLERS: Array<TimelineItemHandler> = [
    {
        canRender: (item) => item.kind === 'session',
        render: (item, context) => context.renderAudioSession(item as Extract<TimelineItem, { kind: 'session' }>),
    },
    {
        canRender: (item) => item.kind === 'document' && item.document.kind === 'clinical_note',
        render: (item, context) => context.renderClinicalNote(item as Extract<TimelineItem, { kind: 'document' }>),
    },
    {
        canRender: (item) => item.kind === 'document' && item.document.kind === 'file',
        render: (item, context) => context.renderUploadedFile(item as Extract<TimelineItem, { kind: 'document' }>),
    },
];

function renderReportZoomIcon(kind: 'in' | 'out') {
    // Draw simple zoom glyphs inline because this icon is specific to the report viewer controls.
    return (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
            <circle cx='10.5' cy='10.5' r='5.5' stroke='currentColor' strokeWidth='2' />
            <path d='M15 15L20 20' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
            <path d='M8 10.5H13' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
            {kind === 'in' && (
                <path d='M10.5 8V13' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
            )}
        </svg>
    );
}

function renderTimelineTypeIcon(kind: 'session' | 'clinical_note' | 'file') {
    // Give each supported timeline document type a distinct, compact header icon.
    const labelByKind = {
        session: 'Audio session',
        clinical_note: 'Clinical note',
        file: 'Uploaded file',
    };
    const iconStyle = {
        ...styles.timelineTypeIcon,
        ...(kind === 'session' ? styles.timelineTypeIconSession : {}),
        ...(kind === 'clinical_note' ? styles.timelineTypeIconNote : {}),
        ...(kind === 'file' ? styles.timelineTypeIconFile : {}),
    };

    return (
        <span
            style={iconStyle}
            role='img'
            aria-label={labelByKind[kind]}
            title={labelByKind[kind]}
        >
            {kind === 'session' && (
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                    <path d='M4 12H6.5L8.25 7L11.75 17L13.75 10.5L15.25 14H20' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    <circle cx='18.5' cy='7.5' r='2.5' stroke='currentColor' strokeWidth='2' />
                </svg>
            )}
            {kind === 'clinical_note' && (
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                    <path d='M7 3.75H15.5L19 7.25V20.25H7V3.75Z' stroke='currentColor' strokeWidth='2' strokeLinejoin='round' />
                    <path d='M15.5 3.75V7.25H19' stroke='currentColor' strokeWidth='2' strokeLinejoin='round' />
                    <path d='M10 11H16' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                    <path d='M10 15H14' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                </svg>
            )}
            {kind === 'file' && (
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                    <path d='M6.5 3.75H14.5L18.5 7.75V20.25H6.5V3.75Z' stroke='currentColor' strokeWidth='2' strokeLinejoin='round' />
                    <path d='M14.5 3.75V7.75H18.5' stroke='currentColor' strokeWidth='2' strokeLinejoin='round' />
                    <path d='M9.5 12H15.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                    <path d='M9.5 15.5H15.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                </svg>
            )}
        </span>
    );
}

function normalizeReportTextZoom(zoom: number): number {
    // Clamp stored/user-entered zoom values so old preferences cannot break the report layout.
    if (!Number.isFinite(zoom)) {
        return 1;
    }

    const roundedZoom = Math.round(zoom * 10) / 10;
    return Math.min(REPORT_TEXT_ZOOM_MAX, Math.max(REPORT_TEXT_ZOOM_MIN, roundedZoom));
}

function getReportTextZoomStorageKey(authenticatedUserId: string): string {
    // Scope the preference to the dashboard user while still working before auth data arrives.
    return `${REPORT_TEXT_ZOOM_STORAGE_PREFIX}:${authenticatedUserId || 'anonymous'}`;
}

function readPreferredReportTextZoom(authenticatedUserId: string): number {
    // Read safely because localStorage can be unavailable in private or restricted contexts.
    try {
        const storedZoom = window.localStorage.getItem(getReportTextZoomStorageKey(authenticatedUserId));
        return storedZoom ? normalizeReportTextZoom(Number(storedZoom)) : 1;
    } catch (error) {
        return 1;
    }
}

function savePreferredReportTextZoom(authenticatedUserId: string, zoom: number) {
    // Persist report zoom as a lightweight user preference for the next client/report view.
    try {
        window.localStorage.setItem(
            getReportTextZoomStorageKey(authenticatedUserId),
            normalizeReportTextZoom(zoom).toFixed(1)
        );
    } catch (error) {
        // Ignore storage failures; zoom still works for the current view state.
    }
}

function getClientDisplayName(client: Client): string {
    if (client.givenName && client.familyName) {
        return `${client.givenName} ${client.familyName}`;
    }
    return client.givenName || client.familyName || client.displayName || 'Client record';
}

function getClientRoleLabel(client: Client): string {
    return getUserTypeLabel((client as ClientWithOptionalRole).role || client.userType);
}

// Generate compact local ids for therapist-created tag definitions and placed markers.
function createLocalSessionTagId(prefix: string): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `${prefix}-${crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Pick a stable colour from the tag label so new tags look varied without extra UI.
function getSessionTagColour(label: string, usedColours: Set<string> = new Set()): string {
    let hash = 2166136261;
    label.trim().toLowerCase().split('').forEach((character) => {
        hash ^= character.charCodeAt(0);
        hash = Math.imul(hash, 16777619);
    });

    const startIndex = (hash >>> 0) % SESSION_TAG_COLOURS.length;
    for (let colourOffset = 0; colourOffset < SESSION_TAG_COLOURS.length; colourOffset += 1) {
        const colour = SESSION_TAG_COLOURS[(startIndex + colourOffset) % SESSION_TAG_COLOURS.length];
        if (!usedColours.has(colour)) {
            return colour;
        }
    }

    return SESSION_TAG_COLOURS[startIndex];
}

// Keep therapist tag lists sorted most-recently-used first.
function sortSessionTagsByMostRecent(tags: Array<TherapySessionTagDefinition>): Array<TherapySessionTagDefinition> {
    return [...tags].sort((leftTag, rightTag) => (rightTag.lastUsedAtMs || 0) - (leftTag.lastUsedAtMs || 0));
}

function sortClientsByName(clients: Array<Client>): Array<Client> {
    return [...clients].sort((a, b) => getClientDisplayName(a).localeCompare(getClientDisplayName(b)));
}

// Use the best stable id available when walking client relationship edges.
function getClientGraphId(client: Client): string {
    return client.id || client.userId || '';
}

// Resolve every Client association while remaining compatible with scalar legacy sessions.
function getTherapySessionClientIds(session: TherapySession): Set<string> {
    return new Set([
        ...(session.clientIds || []),
        ...(session.participants || [])
            .filter((participant) => participant.personType === 'client')
            .map((participant) => participant.personId),
        session.clientId || '',
    ].filter(Boolean));
}

// Treat dashboard therapist clients as therapist speakers, and other clients as client-side speakers.
function getTranscriptRoleForClient(client: Client): TranscriptRole {
    return client.userType === 'therapist' ? 'therapist' : 'client';
}

// Collect the connected client relationship graph through carers and care recipients.
function collectRelatedClientGraph(rootClient: Client, allClients: Array<Client>): Set<Client> {
    const clientsById = new Map<string, Client>();
    // Keep the selected record's latest saved details while the client list refreshes.
    [...allClients, rootClient].forEach((client) => {
        const graphId = getClientGraphId(client);
        if (graphId) {
            clientsById.set(graphId, client);
        }
    });

    const rootId = getClientGraphId(rootClient);
    const relatedClientsById = new Map<string, Client>();
    const visitedIds = new Set<string>();
    const pendingIds = rootId ? [rootId] : [];

    if (!rootId) {
        return new Set([rootClient]);
    }

    while (pendingIds.length > 0) {
        const clientId = pendingIds.shift() || '';
        if (!clientId || visitedIds.has(clientId)) {
            continue;
        }

        visitedIds.add(clientId);
        const client = clientsById.get(clientId);
        if (!client) {
            continue;
        }

        relatedClientsById.set(clientId, client);

        const adjacentClientIds = new Set<string>(client.carers || []);
        allClients.forEach((candidateClient) => {
            const candidateId = getClientGraphId(candidateClient);
            if (candidateId && candidateClient.carers?.includes(clientId)) {
                adjacentClientIds.add(candidateId);
            }
        });

        adjacentClientIds.forEach((adjacentClientId) => {
            if (!visitedIds.has(adjacentClientId)) {
                pendingIds.push(adjacentClientId);
            }
        });
    }

    return new Set(relatedClientsById.values());
}

// Convert related Clients into transcript assignment menu entries.
function getClientAssignablePerson(client: Client): TranscriptAssignablePerson {
    const personId = getClientGraphId(client);
    return {
        key: `client:${personId}`,
        personId,
        personType: 'client',
        displayName: getClientDisplayName(client),
        transcriptRole: getTranscriptRoleForClient(client),
        userType: client.userType,
    };
}

// Convert a Therapist document into the same assignment menu shape.
function getTherapistAssignablePerson(therapist: Therapist): TranscriptAssignablePerson {
    return {
        key: `therapist:${therapist.id}`,
        personId: therapist.id,
        personType: 'therapist',
        displayName: therapist.name || therapist.email || 'Therapist',
        transcriptRole: 'therapist',
    };
}

// Reuse recording-time participant snapshots in transcript correction menus.
function getSessionParticipantAssignablePerson(
    participant: TherapySessionParticipant
): TranscriptAssignablePerson {
    const transcriptRole: TranscriptRole = participant.personType === 'therapist' ||
        participant.userType === 'therapist' ? 'therapist' : 'client';
    return {
        key: `${participant.personType}:${participant.personId}`,
        personId: participant.personId,
        personType: participant.personType,
        displayName: participant.displayName,
        transcriptRole,
        userType: participant.userType,
    };
}

// Store the selected person in a compact transcript assignment payload.
function getSpeakerAssignmentForPerson(person: TranscriptAssignablePerson): TherapySessionTranscriptSpeakerAssignment {
    const assignment: TherapySessionTranscriptSpeakerAssignment = {
        personId: person.personId,
        personType: person.personType,
        displayName: person.displayName,
        transcriptRole: person.transcriptRole,
    };

    if (person.userType) {
        assignment.userType = person.userType;
    }

    return assignment;
}

// Apply a selected person to an utterance while keeping timing/text intact.
function assignUtteranceToPerson(
    utterance: TherapySessionTranscriptUtterance,
    person: TranscriptAssignablePerson
): TherapySessionTranscriptUtterance {
    return {
        ...utterance,
        role: person.transcriptRole,
        channel: utterance.channel > 0 ?
            utterance.channel : getTranscriptChannelForRole(person.transcriptRole, utterance.channel),
        speakerAssignment: getSpeakerAssignmentForPerson(person),
    };
}

// Keep same-role people separate when applying an edit to every line from one speaker.
function getUtteranceSpeakerIdentity(utterance: TherapySessionTranscriptUtterance): string {
    if (utterance.channel > 0) {
        return `channel:${utterance.channel}`;
    }

    if (utterance.speakerAssignment) {
        return `person:${utterance.speakerAssignment.personType}:${utterance.speakerAssignment.personId}`;
    }

    return `role:${utterance.role || 'unknown'}`;
}

// Strip optional undefined assignment fields before writing transcript arrays to Firestore.
function sanitizeUtteranceForSave(
    utterance: TherapySessionTranscriptUtterance
): TherapySessionTranscriptUtterance {
    const sanitizedUtterance: TherapySessionTranscriptUtterance = {
        role: utterance.role,
        channel: utterance.channel,
        startMs: utterance.startMs,
        endMs: utterance.endMs,
        text: utterance.text,
    };

    if (utterance.speakerAssignment) {
        sanitizedUtterance.speakerAssignment = {
            personId: utterance.speakerAssignment.personId,
            personType: utterance.speakerAssignment.personType,
            displayName: utterance.speakerAssignment.displayName,
            transcriptRole: utterance.speakerAssignment.transcriptRole,
        };

        if (utterance.speakerAssignment.userType) {
            sanitizedUtterance.speakerAssignment.userType = utterance.speakerAssignment.userType;
        }
    }

    return sanitizedUtterance;
}

function getTherapistName(client: Client, therapists: Array<Therapist>, currentTherapist: Nullable<Therapist>): string {
    if (!client.therapistUserId) {
        return '';
    }

    return therapists.find((therapist) => therapist.id === client.therapistUserId)?.name ||
        (currentTherapist?.id === client.therapistUserId ? currentTherapist.name : '') ||
        '';
}

function SpeechNeedsCheckboxList(props: {
    speechNeeds: Array<SpeechNeed>;
    selectedSpeechNeedIds: string[];
    onToggleSpeechNeed: (speechNeedId: string, isSelected: boolean) => void;
}) {
    const { speechNeeds, selectedSpeechNeedIds, onToggleSpeechNeed } = props;
    const selectedSpeechNeeds = new Set(selectedSpeechNeedIds);

    return (
        <div style={styles.speechNeedsCheckboxList}>
            {speechNeeds.map((speechNeed) => (
                <label key={speechNeed.id} style={styles.speechNeedCheckboxLabel}>
                    <input
                        type='checkbox'
                        checked={selectedSpeechNeeds.has(speechNeed.id)}
                        onChange={(event) => onToggleSpeechNeed(speechNeed.id, event.currentTarget.checked)}
                        style={styles.checkboxInput}
                    />
                    <span>{speechNeed.name}</span>
                </label>
            ))}
        </div>
    );
}

// Normalize Firestore or Auth timestamps into a regular Date.
function getClientTimestampDate(timestamp: unknown): Date | null {
    return getFirstFirestoreTimestampDate(timestamp);
}

// Resolve one shared fallback date from all available engagement timelines.
function getEarliestEngagementDate(...timelines: Array<Array<ClientActivityPoint>>): Date | null {
    const firstDateKey = timelines
        .flatMap((timeline) => timeline.map((point) => point.date))
        .filter((dateKey) => /^\d{4}-\d{2}-\d{2}$/.test(dateKey))
        .sort()[0];
    if (!firstDateKey) {
        return null;
    }

    const [year, month, day] = firstDateKey.split('-').map(Number);
    return new Date(year, month - 1, day);
}

function formatAssessmentResponseDate(dateString: string): string {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        return dateString;
    }

    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

// Normalize Firestore session timestamps into regular Dates for display/sorting.
function getSessionDate(session: TherapySession): Date | null {
    return getFirstFirestoreTimestampDate(session.date, session.startedAt, session.createdAt);
}

// Format session start times for the client sessions transcript view.
function formatSessionDate(session: TherapySession): string {
    const date = getSessionDate(session);
    if (!date) {
        return '';
    }

    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

// Normalize Firestore report timestamps into regular Dates for display/sorting.
function getReportDate(report: TherapySessionReport): Date | null {
    return getFirstFirestoreTimestampDate(report.date, report.createdAt, report.updatedAt);
}

// Format generated report dates for the Reports tab and client summary panel.
function formatReportDate(report: TherapySessionReport): string {
    const date = getReportDate(report);
    if (!date) {
        return '';
    }

    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

// Normalize Firestore timeline timestamps into regular Dates for mixed timeline ordering.
function getTimelineDocumentDate(document: TimelineDocument): Date | null {
    return getFirstFirestoreTimestampDate(document.date, document.createdAt, document.updatedAt);
}

function formatTimelineDocumentDate(document: TimelineDocument): string {
    const date = getTimelineDocumentDate(document);
    if (!date) {
        return '';
    }

    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatFileSize(sizeBytes?: number): string {
    // Keep uploaded file cards readable without exposing raw byte counts.
    if (!sizeBytes || sizeBytes <= 0) {
        return 'Unknown size';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = sizeBytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getReportText(report: TherapySessionReport): string {
    return (report.reportText || '').trim();
}

function isReportHorizontalRuleLine(line: string): boolean {
    // Treat Markdown rule-only lines as visual separators during rich-text conversion.
    return /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/.test(line);
}

function formatInlineMarkdownForRichText(text: string): string {
    // Preserve common inline Markdown styling when a generated report first enters the rich editor.
    return escapeHtml(text)
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match: string, label: string, url: string) =>
            `<a href="${escapeHtml(url)}">${label}</a>`
        )
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>');
}

function isMarkdownBlockStart(line: string): boolean {
    // Keep paragraph grouping from swallowing headings, lists, quotes, and rules.
    const trimmedLine = line.trim();
    return /^(#{1,6})\s+/.test(trimmedLine) ||
        /^[-*]\s+/.test(trimmedLine) ||
        /^\d+\.\s+/.test(trimmedLine) ||
        /^>\s?/.test(trimmedLine) ||
        isReportHorizontalRuleLine(trimmedLine) ||
        trimmedLine.includes('|');
}

function formatMarkdownReportForRichText(markdownText: string): string {
    // Convert existing generated Markdown reports into editor-friendly HTML once.
    const lines = markdownText.split('\n');
    const blocks: string[] = [];
    let lineIndex = 0;

    while (lineIndex < lines.length) {
        const line = lines[lineIndex];
        const trimmedLine = line.trim();

        if (!trimmedLine) {
            lineIndex += 1;
            continue;
        }

        if (isReportHorizontalRuleLine(line)) {
            blocks.push('<hr>');
            lineIndex += 1;
            continue;
        }

        const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            const headingLevel = Math.min(headingMatch[1].length, 3);
            blocks.push(`<h${headingLevel}>${formatInlineMarkdownForRichText(headingMatch[2])}</h${headingLevel}>`);
            lineIndex += 1;
            continue;
        }

        const unorderedListMatch = trimmedLine.match(/^[-*]\s+(.*)$/);
        if (unorderedListMatch) {
            const items: string[] = [];
            while (lineIndex < lines.length) {
                const currentMatch = lines[lineIndex].trim().match(/^[-*]\s+(.*)$/);
                if (!currentMatch) {
                    break;
                }

                items.push(`<li>${formatInlineMarkdownForRichText(currentMatch[1])}</li>`);
                lineIndex += 1;
            }
            blocks.push(`<ul>${items.join('')}</ul>`);
            continue;
        }

        const orderedListMatch = trimmedLine.match(/^\d+\.\s+(.*)$/);
        if (orderedListMatch) {
            const items: string[] = [];
            while (lineIndex < lines.length) {
                const currentMatch = lines[lineIndex].trim().match(/^\d+\.\s+(.*)$/);
                if (!currentMatch) {
                    break;
                }

                items.push(`<li>${formatInlineMarkdownForRichText(currentMatch[1])}</li>`);
                lineIndex += 1;
            }
            blocks.push(`<ol>${items.join('')}</ol>`);
            continue;
        }

        const blockquoteMatch = trimmedLine.match(/^>\s?(.*)$/);
        if (blockquoteMatch) {
            const quoteLines: string[] = [];
            while (lineIndex < lines.length) {
                const currentMatch = lines[lineIndex].trim().match(/^>\s?(.*)$/);
                if (!currentMatch) {
                    break;
                }

                quoteLines.push(formatInlineMarkdownForRichText(currentMatch[1]));
                lineIndex += 1;
            }
            blocks.push(`<blockquote>${quoteLines.map((quoteLine) => `<p>${quoteLine}</p>`).join('')}</blockquote>`);
            continue;
        }

        const paragraphLines = [line];
        lineIndex += 1;
        while (
            lineIndex < lines.length &&
            lines[lineIndex].trim() &&
            !isMarkdownBlockStart(lines[lineIndex])
        ) {
            paragraphLines.push(lines[lineIndex]);
            lineIndex += 1;
        }

        blocks.push(`<p>${paragraphLines.map(formatInlineMarkdownForRichText).join('<br>')}</p>`);
    }

    return normalizeRichTextHtml(blocks.join(''));
}

function getReportEditorText(report: TherapySessionReport): string {
    // Open saved HTML directly and convert legacy Markdown reports for rich editing.
    const reportText = getReportText(report);
    if (!reportText) {
        return '';
    }

    return hasRichTextHtml(reportText)
        ? normalizeRichTextHtml(reportText)
        : formatMarkdownReportForRichText(reportText);
}

function getReportTitle(report: TherapySessionReport): string {
    return report.title || `Report for ${report.clientName || 'client'}`;
}

function getTimelineDocumentTitle(document: TimelineDocument): string {
    if (document.title?.trim()) {
        return document.title.trim();
    }

    if (document.kind === 'file') {
        return document.originalName || 'Uploaded file';
    }

    return 'Clinical note';
}

function escapeXml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function getReportDownloadFileName(report: TherapySessionReport): string {
    const dateLabel = getReportDate(report)?.toISOString().slice(0, 10) || 'undated';
    const safeTitle = getReportTitle(report)
        .replace(/[^a-zA-Z0-9._-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 72) || 'report';

    return `${dateLabel}-${safeTitle}.docx`;
}

type DocxRunOptions = {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    code?: boolean;
    sizeHalfPoints?: number;
    color?: string;
    hyperlinkId?: string;
};

type DocxParagraphOptions = {
    styleId?: string;
    numberingId?: number;
    spacingAfter?: number;
    runsXml?: string;
};

type DocxRelationship = {
    id: string;
    target: string;
    type: 'hyperlink';
};

type DocxBuildContext = {
    relationships: Array<DocxRelationship>;
};

const DOCX_BULLET_NUMBERING_ID = 10;
const DOCX_ORDERED_NUMBERING_ID = 20;
const DOCX_REPORT_FONT_FAMILY = 'Inter';
const DOCX_CODE_FONT_FAMILY = 'Courier New';
const DOCX_HEADING_SIZE_BY_LEVEL: Record<number, number> = {
    1: 32,
    2: 28,
    3: 24,
    4: 22,
    5: 22,
    6: 22,
};

const DOCX_BLOCK_TAGS = new Set([
    'DIV',
    'P',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'UL',
    'OL',
    'LI',
    'BLOCKQUOTE',
    'HR',
]);

function getDocxHyperlinkId(context: DocxBuildContext, url: string): string {
    // Store external hyperlinks in document relationships so Word keeps links clickable.
    const existingRelationship = context.relationships.find((relationship) => relationship.target === url);
    if (existingRelationship) {
        return existingRelationship.id;
    }

    const id = `rId${context.relationships.length + 1}`;
    context.relationships.push({
        id,
        target: url,
        type: 'hyperlink',
    });
    return id;
}

function normalizeDocxHexColor(value: string | null): string | undefined {
    // Word expects six hex digits without a leading hash.
    if (!value) {
        return undefined;
    }

    const trimmedValue = value.trim();
    const hexMatch = trimmedValue.match(/^#?([0-9a-fA-F]{6})$/);
    return hexMatch ? hexMatch[1].toUpperCase() : undefined;
}

function getDocxFontSizeForHtmlFont(size: string | null): number | undefined {
    // Map browser execCommand font sizes to approximate Word half-point sizes.
    const sizeMap: Record<string, number> = {
        '1': 16,
        '2': 20,
        '3': 22,
        '4': 28,
        '5': 36,
        '6': 48,
        '7': 72,
    };
    return size ? sizeMap[size] : undefined;
}

function createDocxBreakRunXml(): string {
    return '<w:r><w:br/></w:r>';
}

function createDocxRunFontsXml(options: DocxRunOptions): string {
    // Match the dashboard report font, except for code spans which stay monospace.
    const fontFamily = options.code ? DOCX_CODE_FONT_FAMILY : DOCX_REPORT_FONT_FAMILY;
    return `<w:rFonts w:ascii="${escapeXml(fontFamily)}" w:hAnsi="${escapeXml(fontFamily)}" w:eastAsia="${escapeXml(fontFamily)}" w:cs="${escapeXml(fontFamily)}"/>`;
}

function createDocxRunXml(text: string, options: DocxRunOptions = {}): string {
    const runProperties = [
        createDocxRunFontsXml(options),
        options.bold ? '<w:b/>' : '',
        options.italic ? '<w:i/>' : '',
        options.underline ? '<w:u w:val="single"/>' : '',
        options.sizeHalfPoints ? `<w:sz w:val="${options.sizeHalfPoints}"/>` : '',
        options.color ? `<w:color w:val="${escapeXml(options.color)}"/>` : '',
    ].filter(Boolean).join('');
    const runXml = `<w:r>${runProperties ? `<w:rPr>${runProperties}</w:rPr>` : ''}<w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;

    if (options.hyperlinkId) {
        return `<w:hyperlink r:id="${escapeXml(options.hyperlinkId)}" w:history="1">${runXml}</w:hyperlink>`;
    }

    return runXml;
}

function createDocxRunsForText(text: string, options: DocxRunOptions = {}): string {
    // Preserve line breaks inside rich text runs.
    return text
        .split('\n')
        .map((textPart, textPartIndex) => [
            textPart ? createDocxRunXml(textPart, options) : '',
            textPartIndex < text.split('\n').length - 1 ? createDocxBreakRunXml() : '',
        ].join(''))
        .join('');
}

function createDocxInlineRuns(
    markdownText: string,
    context: DocxBuildContext,
    baseOptions: DocxRunOptions = {}
): string {
    // Preserve common Markdown emphasis in the generated Word document.
    const runs: string[] = [];
    const inlinePattern = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = inlinePattern.exec(markdownText)) !== null) {
        if (match.index > lastIndex) {
            runs.push(createDocxRunsForText(markdownText.slice(lastIndex, match.index), baseOptions));
        }

        if (match[1] && match[2] && match[3]) {
            const hyperlinkId = getDocxHyperlinkId(context, match[3]);
            runs.push(createDocxRunXml(match[2], mergeDocxRunOptions(baseOptions, {
                hyperlinkId,
                underline: true,
                color: '0563C1',
            })));
        } else if (match[4] && match[5]) {
            runs.push(createDocxRunXml(match[5], mergeDocxRunOptions(baseOptions, { bold: true })));
        } else if (match[6] && match[7]) {
            runs.push(createDocxRunXml(match[7], mergeDocxRunOptions(baseOptions, { italic: true })));
        } else if (match[8] && match[9]) {
            runs.push(createDocxRunXml(match[9], mergeDocxRunOptions(baseOptions, { code: true })));
        }

        lastIndex = inlinePattern.lastIndex;
    }

    if (lastIndex < markdownText.length) {
        runs.push(createDocxRunsForText(markdownText.slice(lastIndex), baseOptions));
    }

    return runs.join('') || createDocxRunXml('');
}

function createDocxParagraphXml(
    text: string,
    options: DocxParagraphOptions = {},
    context: DocxBuildContext
): string {
    const paragraphProperties = [
        options.styleId ? `<w:pStyle w:val="${options.styleId}"/>` : '',
        options.numberingId ? `<w:numPr><w:ilvl w:val="0"/><w:numId w:val="${options.numberingId}"/></w:numPr>` : '',
        `<w:spacing w:after="${options.spacingAfter ?? 160}"/>`,
    ].filter(Boolean).join('');
    const runsXml = options.runsXml ?? createDocxInlineRuns(text, context);

    return `<w:p><w:pPr>${paragraphProperties}</w:pPr>${runsXml || createDocxRunXml('')}</w:p>`;
}

function createDocxBulletParagraphXml(runsXml: string, spacingAfter: number = 160): string {
    // Use an explicit bullet glyph so exported unordered lists cannot be reinterpreted as numbered lists.
    return `<w:p><w:pPr><w:spacing w:after="${spacingAfter}"/><w:ind w:left="720" w:hanging="360"/></w:pPr>${createDocxRunXml('\u2022')}<w:r><w:tab/></w:r>${runsXml || createDocxRunXml('')}</w:p>`;
}

function createDocxHorizontalRuleXml(): string {
    return '<w:p><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="BFBFBF"/></w:pBdr><w:spacing w:after="180"/></w:pPr></w:p>';
}

function mergeDocxRunOptions(baseOptions: DocxRunOptions, nextOptions: DocxRunOptions): DocxRunOptions {
    return {
        ...baseOptions,
        ...Object.fromEntries(
            Object.entries(nextOptions).filter(([_key, value]) => value !== undefined)
        ),
    };
}

function getDocxRunOptionsForElement(
    element: HTMLElement,
    inheritedOptions: DocxRunOptions,
    context: DocxBuildContext
): DocxRunOptions {
    // Translate supported HTML inline tags into Word run properties.
    const tagName = element.tagName;
    const nextOptions: DocxRunOptions = {};

    if (tagName === 'B' || tagName === 'STRONG') {
        nextOptions.bold = true;
    }
    if (tagName === 'I' || tagName === 'EM') {
        nextOptions.italic = true;
    }
    if (tagName === 'U') {
        nextOptions.underline = true;
    }
    if (tagName === 'CODE') {
        nextOptions.code = true;
    }
    if (tagName === 'A') {
        const href = element.getAttribute('href');
        if (href) {
            nextOptions.hyperlinkId = getDocxHyperlinkId(context, href);
            nextOptions.underline = true;
            nextOptions.color = '0563C1';
        }
    }
    if (tagName === 'FONT') {
        nextOptions.sizeHalfPoints = getDocxFontSizeForHtmlFont(element.getAttribute('size'));
        nextOptions.color = normalizeDocxHexColor(element.getAttribute('color'));
    }

    return mergeDocxRunOptions(inheritedOptions, nextOptions);
}

function createDocxInlineRunsFromHtmlNodes(
    nodes: Array<Node>,
    context: DocxBuildContext,
    inheritedOptions: DocxRunOptions = {}
): string {
    // Walk inline HTML nodes and emit Word runs with inherited formatting.
    return nodes.map((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return createDocxRunsForText(node.textContent || '', inheritedOptions);
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return '';
        }

        const element = node as HTMLElement;
        if (element.tagName === 'BR') {
            return createDocxBreakRunXml();
        }
        if (DOCX_BLOCK_TAGS.has(element.tagName)) {
            return createDocxInlineRunsFromHtmlNodes(Array.from(element.childNodes), context, inheritedOptions);
        }

        return createDocxInlineRunsFromHtmlNodes(
            Array.from(element.childNodes),
            context,
            getDocxRunOptionsForElement(element, inheritedOptions, context)
        );
    }).join('');
}

function htmlElementHasBlockChildren(element: HTMLElement): boolean {
    return Array.from(element.children).some((child) => DOCX_BLOCK_TAGS.has(child.tagName));
}

function createDocxParagraphsFromHtmlNodes(
    nodes: Array<Node>,
    context: DocxBuildContext,
    options: { numberingId?: number; styleId?: string } = {}
): string[] {
    // Convert sanitized rich-report HTML blocks into Word paragraphs.
    const paragraphs: string[] = [];

    nodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || '';
            if (text.trim()) {
                paragraphs.push(createDocxParagraphXml('', {
                    runsXml: createDocxRunsForText(text),
                }, context));
            }
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        const element = node as HTMLElement;
        const tagName = element.tagName;
        if (tagName === 'BR') {
            return;
        }
        if (tagName === 'HR') {
            paragraphs.push(createDocxHorizontalRuleXml());
            return;
        }
        if (/^H[1-6]$/.test(tagName)) {
            const headingLevel = Math.min(Number(tagName.slice(1)), 3);
            paragraphs.push(createDocxParagraphXml('', {
                styleId: `Heading${headingLevel}`,
                spacingAfter: 120,
                runsXml: createDocxInlineRunsFromHtmlNodes(Array.from(element.childNodes), context, {
                    bold: true,
                    sizeHalfPoints: DOCX_HEADING_SIZE_BY_LEVEL[headingLevel],
                }),
            }, context));
            return;
        }
        if (tagName === 'UL' || tagName === 'OL') {
            const numberingId = tagName === 'UL' ? DOCX_BULLET_NUMBERING_ID : DOCX_ORDERED_NUMBERING_ID;
            Array.from(element.children).forEach((child) => {
                if (child.tagName === 'LI') {
                    paragraphs.push(...createDocxParagraphsFromHtmlNodes([child], context, { numberingId }));
                }
            });
            return;
        }
        if (tagName === 'LI') {
            const inlineNodes = Array.from(element.childNodes).filter((childNode) =>
                childNode.nodeType !== Node.ELEMENT_NODE ||
                !['UL', 'OL'].includes((childNode as HTMLElement).tagName)
            );
            const runsXml = createDocxInlineRunsFromHtmlNodes(inlineNodes, context);
            paragraphs.push(options.numberingId === DOCX_BULLET_NUMBERING_ID ?
                createDocxBulletParagraphXml(runsXml) :
                createDocxParagraphXml('', {
                    numberingId: options.numberingId,
                    runsXml,
                }, context)
            );
            Array.from(element.children)
                .filter((child) => child.tagName === 'UL' || child.tagName === 'OL')
                .forEach((child) => paragraphs.push(...createDocxParagraphsFromHtmlNodes([child], context)));
            return;
        }
        if (tagName === 'BLOCKQUOTE') {
            paragraphs.push(...createDocxParagraphsFromHtmlNodes(
                Array.from(element.childNodes),
                context,
                { styleId: 'BlockQuote' }
            ));
            return;
        }
        if (tagName === 'P' || tagName === 'DIV') {
            if (htmlElementHasBlockChildren(element)) {
                paragraphs.push(...createDocxParagraphsFromHtmlNodes(Array.from(element.childNodes), context, options));
                return;
            }

            const runsXml = createDocxInlineRunsFromHtmlNodes(Array.from(element.childNodes), context);
            if (runsXml || element.textContent?.trim()) {
                paragraphs.push(createDocxParagraphXml('', {
                    styleId: options.styleId,
                    runsXml,
                }, context));
            }
            return;
        }

        paragraphs.push(createDocxParagraphXml('', {
            styleId: options.styleId,
            runsXml: createDocxInlineRunsFromHtmlNodes([element], context),
        }, context));
    });

    return paragraphs;
}

function createDocxHtmlBodyParagraphs(reportText: string, context: DocxBuildContext): string[] {
    // Parse saved editor HTML and preserve supported formatting in the Word document.
    const container = document.createElement('div');
    container.innerHTML = normalizeRichTextHtml(reportText);
    return createDocxParagraphsFromHtmlNodes(Array.from(container.childNodes), context);
}

function createDocxMarkdownBodyParagraphs(reportText: string, context: DocxBuildContext): string[] {
    // Preserve generated Markdown structure for reports that have not been rich-text edited.
    const lines = reportText.split('\n');
    const paragraphs: string[] = [];

    lines.forEach((line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) {
            return;
        }

        const headingMatch = trimmedLine.match(/^(#{1,3})\s+(.*)$/);
        if (headingMatch) {
            const headingLevel = Math.min(3, headingMatch[1].length);
            const styleId = `Heading${headingLevel}`;
            paragraphs.push(createDocxParagraphXml('', {
                styleId,
                spacingAfter: 120,
                runsXml: createDocxInlineRuns(headingMatch[2], context, {
                    bold: true,
                    sizeHalfPoints: DOCX_HEADING_SIZE_BY_LEVEL[headingLevel],
                }),
            }, context));
            return;
        }

        const unorderedListMatch = trimmedLine.match(/^[-*]\s+(.*)$/);
        if (unorderedListMatch) {
            paragraphs.push(createDocxBulletParagraphXml(createDocxInlineRuns(unorderedListMatch[1], context)));
            return;
        }

        const orderedListMatch = trimmedLine.match(/^\d+\.\s+(.*)$/);
        if (orderedListMatch) {
            paragraphs.push(createDocxParagraphXml(orderedListMatch[1], { numberingId: DOCX_ORDERED_NUMBERING_ID }, context));
            return;
        }

        paragraphs.push(createDocxParagraphXml(trimmedLine, {}, context));
    });

    return paragraphs;
}

function createDocxBodyParagraphs(report: TherapySessionReport, context: DocxBuildContext): string {
    const title = getReportTitle(report);
    const dateLabel = formatReportDate(report);
    const reportText = getReportText(report);
    const paragraphs = [
        createDocxParagraphXml('', {
            styleId: 'Heading1',
            spacingAfter: 120,
            runsXml: createDocxRunXml(title, {
                bold: true,
                sizeHalfPoints: DOCX_HEADING_SIZE_BY_LEVEL[1],
            }),
        }, context),
        ...(dateLabel ? [createDocxParagraphXml(dateLabel, { spacingAfter: 240 }, context)] : []),
    ];
    paragraphs.push(...(
        hasRichTextHtml(reportText)
            ? createDocxHtmlBodyParagraphs(reportText, context)
            : createDocxMarkdownBodyParagraphs(reportText, context)
    ));

    return paragraphs.join('');
}

function createDocxDocumentXml(report: TherapySessionReport, context: DocxBuildContext): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    ${createDocxBodyParagraphs(report, context)}
    <w:sectPr>
      <w:pgSz w:w="12240" w:h="15840"/>
      <w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440" w:header="720" w:footer="720" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`;
}

function createDocxStylesXml(): string {
    const reportFontXml = `<w:rFonts w:ascii="${DOCX_REPORT_FONT_FAMILY}" w:hAnsi="${DOCX_REPORT_FONT_FAMILY}" w:eastAsia="${DOCX_REPORT_FONT_FAMILY}" w:cs="${DOCX_REPORT_FONT_FAMILY}"/>`;
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults>
    <w:rPrDefault>
      <w:rPr>${reportFontXml}<w:sz w:val="22"/></w:rPr>
    </w:rPrDefault>
  </w:docDefaults>
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:rPr>${reportFontXml}<w:sz w:val="22"/></w:rPr>
  </w:style>
  <w:style w:type="paragraph" w:styleId="Heading1">
    <w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/>
    <w:pPr><w:spacing w:before="240" w:after="120"/></w:pPr>
    <w:rPr>${reportFontXml}<w:b/><w:sz w:val="32"/></w:rPr>
  </w:style>
  <w:style w:type="paragraph" w:styleId="Heading2">
    <w:name w:val="heading 2"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/>
    <w:pPr><w:spacing w:before="220" w:after="100"/></w:pPr>
    <w:rPr>${reportFontXml}<w:b/><w:sz w:val="28"/></w:rPr>
  </w:style>
  <w:style w:type="paragraph" w:styleId="Heading3">
    <w:name w:val="heading 3"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/>
    <w:pPr><w:spacing w:before="180" w:after="80"/></w:pPr>
    <w:rPr>${reportFontXml}<w:b/><w:sz w:val="24"/></w:rPr>
  </w:style>
  <w:style w:type="paragraph" w:styleId="BlockQuote">
    <w:name w:val="Block Quote"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/>
    <w:pPr><w:ind w:left="360"/><w:spacing w:before="80" w:after="120"/></w:pPr>
    <w:rPr>${reportFontXml}<w:i/><w:color w:val="404040"/></w:rPr>
  </w:style>
</w:styles>`;
}

function createDocxNumberingXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:numbering xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:abstractNum w:abstractNumId="${DOCX_BULLET_NUMBERING_ID}">
    <w:nsid w:val="4E3B7A10"/>
    <w:multiLevelType w:val="singleLevel"/>
    <w:lvl w:ilvl="0">
      <w:start w:val="1"/>
      <w:numFmt w:val="bullet"/>
      <w:lvlText w:val="&#xF0B7;"/>
      <w:lvlJc w:val="left"/>
      <w:pPr><w:ind w:left="720" w:hanging="360"/></w:pPr>
      <w:rPr><w:rFonts w:ascii="Symbol" w:hAnsi="Symbol" w:hint="default"/></w:rPr>
    </w:lvl>
  </w:abstractNum>
  <w:abstractNum w:abstractNumId="${DOCX_ORDERED_NUMBERING_ID}">
    <w:nsid w:val="4E3B7A20"/>
    <w:multiLevelType w:val="singleLevel"/>
    <w:lvl w:ilvl="0">
      <w:start w:val="1"/>
      <w:numFmt w:val="decimal"/>
      <w:lvlText w:val="%1."/>
      <w:lvlJc w:val="left"/>
      <w:pPr><w:ind w:left="720" w:hanging="360"/></w:pPr>
    </w:lvl>
  </w:abstractNum>
  <w:num w:numId="${DOCX_BULLET_NUMBERING_ID}"><w:abstractNumId w:val="${DOCX_BULLET_NUMBERING_ID}"/></w:num>
  <w:num w:numId="${DOCX_ORDERED_NUMBERING_ID}"><w:abstractNumId w:val="${DOCX_ORDERED_NUMBERING_ID}"/></w:num>
</w:numbering>`;
}

function createDocxDocumentRelationshipsXml(context: DocxBuildContext): string {
    // Attach hyperlinks referenced from document.xml.
    const relationshipsXml = context.relationships.map((relationship) => (
        `<Relationship Id="${escapeXml(relationship.id)}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" Target="${escapeXml(relationship.target)}" TargetMode="External"/>`
    )).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${relationshipsXml}
</Relationships>`;
}

async function downloadReportDocx(report: TherapySessionReport): Promise<void> {
    // Build a minimal DOCX package so generated reports can be kept outside the dashboard.
    const context: DocxBuildContext = { relationships: [] };
    const documentXml = createDocxDocumentXml(report, context);
    const zip = new JSZip();
    zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/word/numbering.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.numbering+xml"/>
</Types>`);
    zip.folder('_rels')?.file('.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`);
    const wordFolder = zip.folder('word');
    wordFolder?.file('document.xml', documentXml);
    wordFolder?.file('styles.xml', createDocxStylesXml());
    wordFolder?.file('numbering.xml', createDocxNumberingXml());
    wordFolder?.folder('_rels')?.file('document.xml.rels', createDocxDocumentRelationshipsXml(context));

    const blob = await zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = getReportDownloadFileName(report);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
}

// Calculate an age label from a date of birth, falling back to the legacy childAge field.
function getClientAgeLabel(client: Client, referenceDate: Date): string {
    if (client.dateOfBirth) {
        const birthDate = new Date(client.dateOfBirth);
        if (!isNaN(birthDate.getTime())) {
            let age = referenceDate.getFullYear() - birthDate.getFullYear();
            const hasHadBirthdayThisYear =
                referenceDate.getMonth() > birthDate.getMonth() ||
                (referenceDate.getMonth() === birthDate.getMonth() && referenceDate.getDate() >= birthDate.getDate());
            if (!hasHadBirthdayThisYear) {
                age -= 1;
            }

            if (age >= 0) {
                return `${age}`;
            }
        }
    }

    return typeof client.childAge === 'number' ? `${client.childAge}` : '';
}

// Keep millisecond offsets readable in prompts sent to language models.
function formatPromptTimestamp(timeMs: number | undefined): string {
    const totalSeconds = Math.max(0, Math.floor((timeMs || 0) / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// Convert saved duration milliseconds into a compact prompt value.
function formatPromptDuration(durationMs: number | undefined): string {
    if (!durationMs || durationMs <= 0) {
        return '';
    }

    return formatPromptTimestamp(durationMs);
}

// Create a concise person line from each related client record.
function getSessionSummaryPersonForClient(
    client: Client,
    selectedClient: Client,
    speechNeeds: Array<SpeechNeed>,
    referenceDate: Date
): SessionSummaryPersonContext {
    const isSessionClient = getClientGraphId(client) === getClientGraphId(selectedClient);
    const userTypeLabel = getUserTypeLabel(client.userType);
    const role = isSessionClient ? `Session client, ${userTypeLabel}` : userTypeLabel;

    return {
        key: `client:${getClientGraphId(client)}`,
        name: getClientDisplayName(client),
        role,
        age: getClientAgeLabel(client, referenceDate),
        dateOfBirth: client.dateOfBirth?.trim(),
        supportAreas: getClientSpeechNeedNames(client, speechNeeds),
        customProperties: client.customProperties,
    };
}

// Gather known people from the client graph, therapist record, and any saved speaker assignments.
function getSessionSummaryPeople(params: BuildSessionReportPromptParams): Array<SessionSummaryPersonContext> {
    const sessionItems = params.items.filter((item): item is SessionReportPromptSession => item.kind === 'session');
    const referenceDate = sessionItems
        .map((sessionContext) => getSessionDate(sessionContext.session))
        .filter((date): date is Date => !!date)
        .sort((leftDate, rightDate) => rightDate.getTime() - leftDate.getTime())[0] || new Date();
    const peopleByKey = new Map<string, SessionSummaryPersonContext>();
    const addPerson = (person: SessionSummaryPersonContext | null) => {
        if (!person || !person.name.trim()) {
            return;
        }

        if (!peopleByKey.has(person.key)) {
            peopleByKey.set(person.key, person);
        }
    };

    Array.from(params.relatedClientSet).forEach((client) => {
        addPerson(getSessionSummaryPersonForClient(client, params.selectedClient, params.speechNeeds, referenceDate));
    });

    const assignedTherapist = params.therapists.find((therapist) => therapist.id === params.selectedClient.therapistUserId) ||
        (params.currentTherapist?.id === params.selectedClient.therapistUserId ? params.currentTherapist : null) ||
        params.currentTherapist ||
        null;
    if (assignedTherapist) {
        addPerson({
            key: `therapist:${assignedTherapist.id}`,
            name: assignedTherapist.name || assignedTherapist.email || 'Therapist',
            role: 'Therapist',
        });
    }

    sessionItems.forEach((sessionContext) => {
        (sessionContext.session.participants || []).forEach((participant) => {
            addPerson({
                key: `${participant.personType}:${participant.personId}`,
                name: participant.displayName,
                role: participant.personType === 'therapist' ?
                    'Therapist' : getUserTypeLabel(participant.userType || 'care_recipient'),
            });
        });

        sessionContext.utterances.forEach((utterance) => {
            const assignment = utterance.speakerAssignment;
            if (!assignment || assignment.personType !== 'other') {
                return;
            }

            addPerson({
                key: `speaker:${assignment.personType}:${assignment.personId}`,
                name: assignment.displayName,
                role: 'Other transcript speaker',
            });
        });
    });

    return Array.from(peopleByKey.values());
}

// Format people for the opening section of the AI summary prompt.
function formatSessionSummaryPeopleSection(people: Array<SessionSummaryPersonContext>): string {
    if (people.length === 0) {
        return '- No named people available.';
    }

    return people.map((person) => {
        const details = [`role: ${person.role}`];
        if (person.age) {
            details.push(`age: ${person.age}`);
        }
        // Include the recorded birth date alongside age when it is available.
        if (person.dateOfBirth) {
            details.push(`date of birth: ${person.dateOfBirth}`);
        }
        if (person.supportAreas) {
            details.push(`support areas: ${person.supportAreas}`);
        }

        // Attach filled custom text fields to their person, preserving multiline values.
        const customPropertyLines = (person.customProperties || [])
            .filter((property) => property.name.trim() && property.value.trim())
            .map((property) =>
                `    - ${property.name.trim()}: ${property.value.trim().replace(/\r?\n/g, '\n      ')}`
            );

        return [
            `- ${person.name}: ${details.join('; ')}`,
            ...(customPropertyLines.length > 0 ? ['  Custom properties:', ...customPropertyLines] : []),
        ].join('\n');
    }).join('\n');
}

// Keep tag markers available to the model as clinical event cues.
function formatSessionSummaryTags(session: TherapySession): string {
    const placedTags = [...(session.placedTags || [])]
        .sort((leftTag, rightTag) => leftTag.timestampMs - rightTag.timestampMs);
    if (placedTags.length === 0) {
        return 'No session tags recorded.';
    }

    return placedTags
        .map((tag) => `- ${formatPromptTimestamp(tag.timestampMs)}: ${tag.label}`)
        .join('\n');
}

// Render the whole dialogue transcript with speaker labels and timestamps.
function formatSessionSummaryTranscript(utterances: Array<TherapySessionTranscriptUtterance>): string {
    if (utterances.length === 0) {
        return 'No transcript available.';
    }

    return utterances
        .map((utterance) => {
            const speakerLabel = formatUtteranceSpeakerLabel(utterance);
            const startTime = formatPromptTimestamp(utterance.startMs);
            const endTime = formatPromptTimestamp(utterance.endMs);
            return `[${startTime}-${endTime}] ${speakerLabel}: ${utterance.text}`;
        })
        .join('\n');
}

function formatTimelineFileForPrompt(document: TimelineDocument): string {
    // Identify selected sources; the server resolves each file and attaches its extracted evidence.
    return [
        `- Timeline document ID: ${document.id}`,
        `- Title: ${document.title || document.originalName || 'Uploaded file'}`,
        `- Date/time: ${formatTimelineDocumentDate(document) || 'Unknown'}`,
        `- Original filename: ${document.originalName || 'Unknown'}`,
        `- Content type: ${document.contentType || 'Unknown'}`,
        `- File size: ${formatFileSize(document.sizeBytes)}`,
    ].join('\n');
}

function formatTimelineClinicalNoteForPrompt(document: TimelineDocument, clinicalNotes: string): string {
    // Standalone clinical notes are first-class timeline evidence for report generation.
    return [
        `- Title: ${document.title || 'Clinical note'}`,
        `- Date/time: ${formatTimelineDocumentDate(document) || 'Unknown'}`,
        '',
        'Clinical note:',
        clinicalNotes.trim() || 'No clinical note text recorded.',
    ].join('\n');
}

function formatSessionForPrompt(sessionContext: SessionReportPromptSession, index: number): string {
    // Preserve the rich audio-session context that reports already relied on.
    const sessionDate = formatSessionDate(sessionContext.session) || 'Unknown';
    const sessionDuration = formatPromptDuration(sessionContext.session.durationMs) || 'Unknown';
    const clinicalNotes = sessionContext.clinicalNotes.trim() || 'No clinical notes recorded.';
    const transcript = formatSessionSummaryTranscript(sessionContext.utterances);
    const tags = formatSessionSummaryTags(sessionContext.session);
    const recordedByLine = sessionContext.session.recordedByEmail ?
        `- Recorded by: ${sessionContext.session.recordedByEmail}` :
        null;

    return [
        `Timeline item ${index + 1}: Audio session`,
        `- Title: ${sessionContext.sessionTitle}`,
        `- Date/time: ${sessionDate}`,
        `- Duration: ${sessionDuration}`,
        `- Transcript status: ${sessionContext.session.transcriptStatus || 'Unknown'}`,
        recordedByLine,
        '',
        'Session tags:',
        tags,
        '',
        'Clinical notes:',
        clinicalNotes,
        '',
        'Dialogue transcript:',
        transcript,
    ].filter((line): line is string => line !== null).join('\n');
}

function formatTimelineReportItemForPrompt(item: TimelineReportPromptItem, index: number): string {
    if (item.kind === 'session') {
        return formatSessionForPrompt(item, index);
    }

    if (item.kind === 'clinical_note') {
        return [
            `Timeline item ${index + 1}: Standalone clinical note`,
            formatTimelineClinicalNoteForPrompt(item.document, item.clinicalNotes || ''),
        ].join('\n');
    }

    return [
        `Timeline item ${index + 1}: Uploaded file`,
        formatTimelineFileForPrompt(item.document),
    ].join('\n');
}

// Build a complete model prompt from all locally available selected timeline context.
function buildSessionReportPrompt(params: BuildSessionReportPromptParams): string {
    const peopleSection = formatSessionSummaryPeopleSection(getSessionSummaryPeople(params));
    const reportTemplateSection = params.reportTemplate ?
        `Selected report template: ${params.reportTemplate.name}${params.reportTemplate.originalName ? ` (${params.reportTemplate.originalName})` : ''}.` :
        'Selected report template: None.';
    const reportTemplatePromptExtraInstructions = params.reportTemplate?.promptExtraInstructions?.trim() || '';
    const timelineItemSections = params.items.map(formatTimelineReportItemForPrompt);

    const promptSections = [
        'People involved:',
        peopleSection,
        '',
        `Please generate a clinically useful therapy report/summary from the ${params.items.length} selected client timeline item${params.items.length === 1 ? '' : 's'} below. Use a professional tone suitable for a speech and language therapy record. Include key activities, client responses, clinical observations, progress across sessions or notes, risks or concerns, and suggested follow-up actions where supported by the evidence.`,
        '',
        reportTemplateSection,
        '',
        'Selected timeline information:',
        timelineItemSections.join('\n\n---\n\n'),
    ];

    if (reportTemplatePromptExtraInstructions) {
        promptSections.push(
            '',
            'Report template special instructions:',
            reportTemplatePromptExtraInstructions
        );
    }

    return promptSections.join('\n');
}

// Turn AssemblyAI speaker hints into the role labels used by the UI.
function normalizeTranscriptRole(role: string | undefined, channel?: number): TranscriptRole {
    const normalizedRole = (role || '').toLowerCase();
    if (normalizedRole.includes('therapist')) {
        return 'therapist';
    }

    if (normalizedRole.includes('client')) {
        return 'client';
    }

    if (normalizedRole === 'unknown' || normalizedRole === 'other') {
        return 'other';
    }

    if (channel === 1) {
        return 'therapist';
    }

    if (channel === 2) {
        return 'client';
    }

    return 'other';
}

// Keep therapist/client channels stable without treating unknown speakers as therapist.
function getTranscriptChannelForRole(role: TranscriptRole, fallbackChannel?: number): number {
    if (role === 'therapist') {
        return 1;
    }

    if (role === 'client') {
        return 2;
    }

    return fallbackChannel && fallbackChannel > 2 ? fallbackChannel : 0;
}

// Clean model artifacts while preserving slash-wrapped phoneme notation.
function cleanTranscriptText(text: string): string {
    return text
        .replace(/\\n/g, ' ')
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\\//g, '/')
        .replace(/\[Speaker\s*:\s*[^\]]+\]/gi, ' ')
        .replace(/\s+([,.;:!?])/g, '$1')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

// Split text like "[Speaker:CLIENT] ..." into individual speaker segments.
function splitSpeakerTaggedText(text: string, fallbackRole: TranscriptRole): Array<SpeakerTaggedSegment> {
    const speakerTagExpression = /\[Speaker\s*:\s*([^\]]+)\]/gi;
    const segments: Array<SpeakerTaggedSegment> = [];
    let currentRole = fallbackRole;
    let cursor = 0;
    let match = speakerTagExpression.exec(text);

    while (match) {
        const segmentText = cleanTranscriptText(text.slice(cursor, match.index));
        if (segmentText) {
            segments.push({ role: currentRole, text: segmentText });
        }

        currentRole = normalizeTranscriptRole(match[1], undefined);
        cursor = match.index + match[0].length;
        match = speakerTagExpression.exec(text);
    }

    const finalSegmentText = cleanTranscriptText(text.slice(cursor));
    if (finalSegmentText) {
        segments.push({ role: currentRole, text: finalSegmentText });
    }

    return segments;
}

// Assign approximate timings when a single utterance contains multiple speaker-tagged turns.
function normalizeTranscriptUtterances(
    utterances: Array<TherapySessionTranscriptUtterance>
): Array<TherapySessionTranscriptUtterance> {
    const normalizedUtterances: Array<TherapySessionTranscriptUtterance> = [];

    utterances.forEach((utterance) => {
        const fallbackRole = normalizeTranscriptRole(utterance.role, utterance.channel);
        const segments = splitSpeakerTaggedText(utterance.text || '', fallbackRole);
        const effectiveSegments = segments.length > 0 ?
            segments :
            [{ role: fallbackRole, text: cleanTranscriptText(utterance.text || '') }];
        const visibleSegments = effectiveSegments.filter((segment) => segment.text);
        const totalCharacters = visibleSegments.reduce((sum, segment) => sum + segment.text.length, 0);
        const durationMs = Math.max(0, (utterance.endMs || 0) - (utterance.startMs || 0));
        let cursorMs = utterance.startMs || 0;

        visibleSegments.forEach((segment, segmentIndex) => {
            const isLastSegment = segmentIndex === visibleSegments.length - 1;
            const segmentDurationMs = totalCharacters > 0 ?
                Math.round(durationMs * (segment.text.length / totalCharacters)) :
                0;
            const endMs = isLastSegment ? (utterance.endMs || cursorMs) : cursorMs + segmentDurationMs;

            const normalizedUtterance: TherapySessionTranscriptUtterance = {
                role: segment.role,
                channel: visibleSegments.length === 1 && utterance.channel > 0 ?
                    utterance.channel : getTranscriptChannelForRole(segment.role, utterance.channel),
                startMs: cursorMs,
                endMs,
                text: segment.text,
            };

            if (utterance.speakerAssignment) {
                normalizedUtterance.speakerAssignment = utterance.speakerAssignment;
            }

            normalizedUtterances.push(normalizedUtterance);
            cursorMs = endMs;
        });
    });

    return normalizedUtterances;
}

// Prefer normalized metadata while still supporting older top-level transcript fields.
function getSessionUtterances(session: TherapySession): Array<TherapySessionTranscriptUtterance> {
    const utterances = session.metadata?.utterances?.length ?
        session.metadata.utterances :
        session.transcriptUtterances || EMPTY_SESSION_UTTERANCES;

    if (utterances.length === 0) {
        return EMPTY_SESSION_UTTERANCES;
    }

    return normalizeTranscriptUtterances(utterances);
}

// Match transcript colour treatment to the inferred speaker role or channel.
function getUtteranceSpeakerKind(utterance: TherapySessionTranscriptUtterance): TranscriptRole {
    if (utterance.speakerAssignment?.transcriptRole) {
        return utterance.speakerAssignment.transcriptRole;
    }

    return normalizeTranscriptRole(utterance.role, utterance.channel);
}

// Keep labels clinical and compact in the transcript view.
function formatUtteranceSpeakerLabel(utterance: TherapySessionTranscriptUtterance): string {
    if (utterance.speakerAssignment?.displayName) {
        return utterance.speakerAssignment.displayName;
    }

    const speakerKind = getUtteranceSpeakerKind(utterance);
    if (speakerKind === 'therapist') {
        return 'Therapist';
    }

    if (speakerKind === 'client') {
        return 'Client';
    }

    return utterance.role || `Speaker ${utterance.channel}`;
}

// Explain the current transcript state before AssemblyAI metadata is ready.
function getTranscriptPlaceholder(session: TherapySession): string {
    if (session.transcriptStatus === 'processing' || session.transcriptStatus === 'pending') {
        return 'Transcript processing...';
    }

    if (session.transcriptStatus === 'error') {
        return session.transcriptError || 'Transcript could not be processed.';
    }

    return 'Transcript not available yet.';
}

// Decide whether an utterance contains the audio player's current cursor.
function isUtteranceActiveAtTime(utterance: TherapySessionTranscriptUtterance, currentTimeMs?: number): boolean {
    if (typeof currentTimeMs !== 'number' || currentTimeMs < 0) {
        return false;
    }

    const startMs = Math.max(0, utterance.startMs || 0);
    const endMs = Math.max(startMs, utterance.endMs || startMs);
    return currentTimeMs >= startMs && currentTimeMs <= endMs;
}

// Generate stable keys for utterance rows so playback can scroll to them.
function getUtteranceRenderKey(
    sessionId: string,
    utterance: TherapySessionTranscriptUtterance,
    utteranceIndex: number
): string {
    return `${sessionId}-${utterance.startMs}-${utteranceIndex}`;
}

function evaluateAssessmentScore(fScore: string, data: UserResponseRecord['data']): number | null {
    if (!fScore || !data) {
        return null;
    }

    try {
        // The scoring formula is stored in Firestore as a TypeScript/JS expression over `data`.
        // eslint-disable-next-line no-new-func
        const scoreEvaluator = new Function('data', `"use strict"; return Number(${fScore});`);
        const result = scoreEvaluator(data);
        return Number.isFinite(result) ? result : null;
    } catch (error) {
        console.error('CLIENT_RECORD_VIEW: Failed to evaluate assessment score expression:', error);
        return null;
    }
}

function expandAssessmentText(fToText: string | undefined, data: UserResponseRecord['data']): string | null {
    if (!fToText || !data) {
        return null;
    }

    try {
        const templateLiteralBody = fToText.replace(/`/g, '\\`');
        // The assessment text formatter is stored in Firestore as a template-literal body over `data`.
        // eslint-disable-next-line no-new-func
        const textEvaluator = new Function('data', `"use strict"; return \`${templateLiteralBody}\`;`);
        const result = textEvaluator(data);
        return typeof result === 'string' ? result : String(result ?? '');
    } catch (error) {
        console.error('CLIENT_RECORD_VIEW: Failed to expand assessment text:', error);
        return null;
    }
}

function AssessmentSectionView(props: { section: AssessmentSection }) {
    const { section } = props;
    const [selectedPointIndex, setSelectedPointIndex] = React.useState<number | null>(null);
    const sectionRef = React.useRef<HTMLElement | null>(null);
    const selectedPoint = selectedPointIndex != null ? section.points[selectedPointIndex] : null;

    React.useEffect(() => {
        const handleDocumentMouseDown = (event: MouseEvent) => {
            if (!sectionRef.current) {
                return;
            }

            if (!sectionRef.current.contains(event.target as Node)) {
                setSelectedPointIndex(null);
            }
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, []);

    return (
        <section ref={sectionRef} style={styles.assessmentSection}>
            <h4 style={styles.assessmentTitle}>{section.definition.title}</h4>
            <div style={styles.assessmentSplit}>
                <div style={styles.assessmentChartColumn}>
                    <AssessmentScoreChart
                        points={section.points}
                        activePointIndex={selectedPointIndex}
                        onHoverPoint={setSelectedPointIndex}
                    />
                </div>
                <div style={styles.assessmentTextColumn}>
                    {selectedPoint ? (
                        <div style={styles.assessmentTextContent}>
                            <div style={styles.assessmentDateLabel}>
                                {formatAssessmentResponseDate(selectedPoint.date)}
                            </div>
                            {selectedPoint.expandedText ? (
                                <MarkdownText text={selectedPoint.expandedText} />
                            ) : (
                                <div style={styles.helperText}>No detail text available for this submission.</div>
                            )}
                        </div>
                    ) : (
                        <div style={styles.helperText}>Hover a datapoint to view the response details.</div>
                    )}
                </div>
            </div>
        </section>
    );
}

export function ClientRecordView(props: ClientRecordViewProps) {
    const {
        selectedClient = EMPTY_CLIENT_DISPLAY,
        selectedTeamMember,
        canUseTimelineDocuments = true,
        authenticatedUserId,
        canUseAudioScribeAndReports,
        allClients,
        activityTimelineByClientId,
        sessionMinutesTimelineByClientId,
        signUpTimeByClientId,
        stageByClientId,
        assessmentDefinitions,
        selectedClientAssessmentResponses,
        isLoadingSelectedClientAssessments,
        therapySessions,
        therapySessionReports,
        timelineDocuments,
        therapists,
        currentTherapist,
        reportTemplates,
        speechNeeds,
        isEditing,
        editingClientData,
        defaultCommsCampaignId,
        showFamilySponsorship,
        familySponsorshipStatus,
        isLoadingFamilySponsorship,
        familySponsorshipError,
        canManageFamilySponsorship,
        onBack,
        onImportSessionFile,
        onSaveSessionTitle,
        onSaveSessionDate,
        onSaveSessionClinicalNotes,
        onSaveSessionTranscriptUtterances,
        onSaveSessionPlacedTags,
        onSaveTherapistSessionTags,
        onEnsureSessionWaveform,
        onRetrySessionTranscription,
        onGenerateSessionReport,
        onSaveSessionReportText,
        onDeleteSessionReport,
        onDeleteSession,
        onCreateTimelineClinicalNote,
        onSaveTimelineClinicalNote,
        onSaveTimelineDocumentTitle,
        onSaveTimelineDocumentDate,
        onUploadTimelineFile,
        onGetTimelineFileDownloadUrl,
        onDownloadTimelineFile,
        onDeleteTimelineDocument,
        onStartEditing,
        onDiscardEditing,
        onSaveClient,
        onAssignMyClient,
        onEditClientChange,
        onEditClientCustomPropertiesChange,
        onEditClientSpeechNeedsChange,
        onCommsCampaignEnrollmentChange,
        onSelectClient,
        onSetFamilySponsorship,
        onDeleteClient,
    } = props;
    const [selectedTabsItemIndex, setSelectedTabsItemIndex] = React.useState(0);
    // Content owns its editor state and renders its action into the same slot as other tabs.
    const [contentHeaderActionContainer, setContentHeaderActionContainer] = React.useState<HTMLDivElement | null>(null);
    const [activeSessionPlaybackMsById, setActiveSessionPlaybackMsById] = React.useState<Record<string, number>>({});
    const [sessionSeekRequestsById, setSessionSeekRequestsById] = React.useState<Record<string, SessionSeekRequest>>({});
    const [collapsedSessionIds, setCollapsedSessionIds] = React.useState<Set<string>>(() => new Set());
    const [collapsedTimelineDocumentIds, setCollapsedTimelineDocumentIds] = React.useState<Set<string>>(() => new Set());
    const [expandedTranscriptIds, setExpandedTranscriptIds] = React.useState<Set<string>>(() => new Set());
    const [sessionNotesById, setSessionNotesById] = React.useState<Record<string, string>>({});
    const [savingSessionNotesById, setSavingSessionNotesById] = React.useState<Record<string, boolean>>({});
    const [sessionNotesErrorsById, setSessionNotesErrorsById] = React.useState<Record<string, string>>({});
    const [sessionNotesSaveStatusById, setSessionNotesSaveStatusById] = React.useState<Record<string, SessionNotesSaveStatus>>({});
    const [openSessionOptionsMenuId, setOpenSessionOptionsMenuId] = React.useState<string | null>(null);
    const [hoveredTranscriptOptionsKey, setHoveredTranscriptOptionsKey] = React.useState<string | null>(null);
    const [openTranscriptOptionsMenuKey, setOpenTranscriptOptionsMenuKey] = React.useState<string | null>(null);
    const [openTranscriptAssignmentSubmenu, setOpenTranscriptAssignmentSubmenu] = React.useState<TranscriptAssignmentSubmenuState | null>(null);
    const [hoveredTranscriptPersonMenuKey, setHoveredTranscriptPersonMenuKey] = React.useState<string | null>(null);
    const [deletingSessionIds, setDeletingSessionIds] = React.useState<Record<string, boolean>>({});
    const [retryingTranscriptIds, setRetryingTranscriptIds] = React.useState<Record<string, boolean>>({});
    const [transcriptRetryErrorsById, setTranscriptRetryErrorsById] = React.useState<Record<string, string>>({});
    const [selectedReportTimelineItemKeys, setSelectedReportTimelineItemKeys] = React.useState<Set<string>>(() => new Set());
    const [showGenerateReportDialog, setShowGenerateReportDialog] = React.useState(false);
    const [selectedReportTemplateId, setSelectedReportTemplateId] = React.useState('');
    const [isGeneratingClientReport, setIsGeneratingClientReport] = React.useState(false);
    const [isCreatingTimelineNote, setIsCreatingTimelineNote] = React.useState(false);
    const [isUploadingTimelineFile, setIsUploadingTimelineFile] = React.useState(false);
    const [uploadDialogContext, setUploadDialogContext] = React.useState<{ context: string; kind: 'document' | 'media' } | null>(null);
    const [uploadedTimelineFile, setUploadedTimelineFile] = React.useState<{ id: string; name: string; context: string; kind: 'document' | 'media' } | null>(null);
    const uploadedTimelineFileRef = React.useRef<HTMLElement | null>(null);
    const [clientReportGenerationError, setClientReportGenerationError] = React.useState('');
    const [reportTextZoom, setReportTextZoom] = React.useState(() =>
        readPreferredReportTextZoom(authenticatedUserId)
    );
    const [deletingReportIds, setDeletingReportIds] = React.useState<Record<string, boolean>>({});
    const [reportTextById, setReportTextById] = React.useState<Record<string, string>>({});
    const [savingReportTextById, setSavingReportTextById] = React.useState<Record<string, boolean>>({});
    const [reportTextErrorsById, setReportTextErrorsById] = React.useState<Record<string, string>>({});
    const [reportTextSaveStatusById, setReportTextSaveStatusById] = React.useState<Record<string, SessionNotesSaveStatus>>({});
    const [timelineNotesById, setTimelineNotesById] = React.useState<Record<string, string>>({});
    const [savingTimelineNotesById, setSavingTimelineNotesById] = React.useState<Record<string, boolean>>({});
    const [timelineNotesErrorsById, setTimelineNotesErrorsById] = React.useState<Record<string, string>>({});
    const [timelineNotesSaveStatusById, setTimelineNotesSaveStatusById] = React.useState<Record<string, SessionNotesSaveStatus>>({});
    const [deletingTimelineDocumentIds, setDeletingTimelineDocumentIds] = React.useState<Record<string, boolean>>({});
    const [timelineFileUrlsById, setTimelineFileUrlsById] = React.useState<Record<string, string>>({});
    const [timelineFileUrlErrorsById, setTimelineFileUrlErrorsById] = React.useState<Record<string, string>>({});
    const sessionNotesAutosaveTimersRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const sessionNotesLatestDraftsRef = React.useRef<Record<string, string>>({});
    const sessionNotesSaveVersionsRef = React.useRef<Record<string, number>>({});
    const timelineNotesAutosaveTimersRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const timelineNotesLatestDraftsRef = React.useRef<Record<string, string>>({});
    const timelineNotesSaveVersionsRef = React.useRef<Record<string, number>>({});
    const reportTextAutosaveTimersRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const reportTextLatestDraftsRef = React.useRef<Record<string, string>>({});
    const reportTextSaveVersionsRef = React.useRef<Record<string, number>>({});
    const transcriptBodyRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
    const transcriptUtteranceRefs = React.useRef<Record<string, Record<string, HTMLDivElement | null>>>({});
    const lastAutoScrolledUtteranceKeyBySessionRef = React.useRef<Record<string, string>>({});
    const uploadTimelineFileInputRef = React.useRef<HTMLInputElement | null>(null);
    const timelineCollapseDefaultsClientRef = React.useRef<string | null>(null);
    const timelineCollapseDefaultedItemKeysRef = React.useRef<Set<string>>(new Set());

    // Keep Team identities explicit through filtering, navigation state, and document creation.
    const isTeamTimeline = !!selectedTeamMember;
    const timelineSubjectId = selectedTeamMember?.id || selectedClient.id || selectedClient.userId || '';
    const timelineSubjectKey = `${isTeamTimeline ? 'therapist' : 'client'}:${timelineSubjectId}`;
    const timelineProviderId = selectedTeamMember?.healthcareProviderId || selectedClient.providerId || currentTherapist?.healthcareProviderId;
    const timelineTabIndex = isTeamTimeline ? 0 : 1;
    const uploadContextKey = [authenticatedUserId, timelineProviderId, timelineSubjectKey].join(':');
    React.useEffect(() => {
        // Discard pending selection when the destination or document access changes.
        setUploadDialogContext(null);
        setUploadedTimelineFile(null);
    }, [uploadContextKey, canUseTimelineDocuments, canUseAudioScribeAndReports]);
    const canGenerateClientReports = canUseAudioScribeAndReports && !isTeamTimeline;
    // Exact recipient eligibility avoids showing clinical briefings on carer or Team records.
    const canUseClientBriefing = canGenerateClientReports && canUseTimelineDocuments &&
        selectedClient.userType === 'care_recipient';
    const clientBriefing = useClientBriefing({
        clientId: selectedClient.id || '',
        providerId: timelineProviderId || '',
        userId: authenticatedUserId,
        enabled: canUseClientBriefing,
        subscribe: props.onSubscribeClientBriefing,
        request: props.onRequestClientBriefing,
    });
    // Provider plan access controls subscriptions as well as the Content tab and editor.
    const canUseClientContent = !!props.canUseClientPlans && !isTeamTimeline &&
        (selectedClient.userType === 'carer' || selectedClient.userType === 'care_recipient');
    const clientContent = useClientContent({
        clientId: selectedClient.id || '',
        providerId: timelineProviderId || '',
        userId: authenticatedUserId,
        enabled: canUseClientContent,
        subscribe: props.onSubscribeClientContent,
    });
    const showClientContent = canUseClientContent && (props.hasUsedTherapyApp || clientContent.hasPublishedContent);
    const clientDataToRender = isEditing && editingClientData ? editingClientData : selectedClient;
    const displayName = selectedTeamMember ?
        selectedTeamMember.name || selectedTeamMember.email || 'Team member' : getClientDisplayName(clientDataToRender);
    const selectedClientId = selectedClient.id;
    const clientDocumentId = selectedClient.id || clientDataToRender.id || '';
    const selectedSpeechNeedIds = getClientSpeechNeeds(clientDataToRender);
    const supportAreaLabel = getClientSpeechNeedNames(clientDataToRender, speechNeeds);
    const therapistName = getTherapistName(clientDataToRender, therapists, currentTherapist);
    const authSignUpDate = getClientTimestampDate(clientDataToRender.authCreationTime);
    const activitySignUpDate = getClientTimestampDate(signUpTimeByClientId[selectedClient.id || '']);
    const accountCreatedDate = getClientTimestampDate(clientDataToRender.createdAt) || authSignUpDate;
    const accountCreatedLabel = accountCreatedDate?.toLocaleDateString() || '';
    const therapistOptions = [...therapists]
        .sort((a, b) => a.name.localeCompare(b.name));
    const canEditCommsCampaign = !!defaultCommsCampaignId || !!clientDataToRender.commsCampaignId;
    const isCarer = clientDataToRender.userType === 'carer';
    const canSetSpeechNeeds = isCareRecipientClient(clientDataToRender);
    const showSpeechNeeds = canSetSpeechNeeds && (isEditing || !!supportAreaLabel);
    const showMyClientButton = !isEditing &&
        !!authenticatedUserId &&
        clientDataToRender.therapistUserId !== authenticatedUserId;
    const handleToggleSpeechNeed = (speechNeedId: string, isSelected: boolean) => {
        const nextSpeechNeeds = new Set(selectedSpeechNeedIds);
        if (isSelected) {
            nextSpeechNeeds.add(speechNeedId);
        } else {
            nextSpeechNeeds.delete(speechNeedId);
        }

        onEditClientSpeechNeedsChange(
            speechNeeds
                .map((speechNeed) => speechNeed.id)
                .filter((speechNeedId) => nextSpeechNeeds.has(speechNeedId))
        );
    };

    const relatedCarers = sortClientsByName(
        (selectedClient.carers || [])
            .map((carerId) => allClients.find((client) => client.id === carerId))
            .filter((client): client is Client => !!client)
    );
    const topicsCompletedPoints = activityTimelineByClientId[selectedClient.id || ''] || [];
    const sessionMinutesPoints = sessionMinutesTimelineByClientId[selectedClient.id || ''] || [];
    const earliestEngagementDate = getEarliestEngagementDate(sessionMinutesPoints, topicsCompletedPoints);
    const engagementStartDate = authSignUpDate || activitySignUpDate || accountCreatedDate || earliestEngagementDate;
    const currentStage = stageByClientId[selectedClient.id || ''] || 'referral_created_not_signed_in';
    const assessmentSections = assessmentDefinitions
        .map((definition) => {
            const responses = selectedClientAssessmentResponses
                .filter((response) => response.key === definition.id)
                .sort((leftResponse, rightResponse) => new Date(leftResponse.date).getTime() - new Date(rightResponse.date).getTime());
            const points = responses
                .map((response) => ({
                    date: response.date,
                    score: evaluateAssessmentScore(definition.fScore, response.data),
                    response,
                    expandedText: expandAssessmentText(definition.fToText, response.data),
                }))
                .filter((point): point is AssessmentPoint => point.score !== null);

            return {
                definition,
                responses,
                points,
            };
        })
        .filter((section) => section.responses.length > 0);

    const careRecipients = selectedClientId
        ? sortClientsByName(
            allClients.filter((client) =>
                client.id !== selectedClientId &&
                !!client.carers?.includes(selectedClientId)
            )
        )
        : [];
    const relatedClientSet = React.useMemo(() => {
        // Keep the whole family/client graph available for transcript speaker assignment.
        return selectedTeamMember ? new Set<Client>() : collectRelatedClientGraph(selectedClient, allClients);
    }, [allClients, selectedClient, selectedTeamMember]);
    // Match group sessions through every Client association, with scalar legacy fallback.
    const clientSessionIds = new Set([selectedClient.id, selectedClient.userId].filter((id): id is string => !!id));
    const clientSessions = therapySessions
        .filter((session) => selectedTeamMember ?
            (session.therapistIds || []).includes(selectedTeamMember.id) ||
                (session.participants || []).some((participant) =>
                    participant.personType === 'therapist' && participant.personId === selectedTeamMember.id
                ) :
            Array.from(getTherapySessionClientIds(session)).some((clientId) => clientSessionIds.has(clientId)))
        .sort((leftSession, rightSession) => {
            const leftTime = getSessionDate(leftSession)?.getTime() || 0;
            const rightTime = getSessionDate(rightSession)?.getTime() || 0;
            return rightTime - leftTime;
        });
    const transcriptAssignablePeople = React.useMemo(() => {
        // Offer session participants and related people before anonymous role choices.
        const selectedClientGraphId = getClientGraphId(selectedClient);
        const relatedClients = Array.from(relatedClientSet);
        const selectedClientPerson = selectedTeamMember ? getTherapistAssignablePerson(selectedTeamMember) :
            selectedClientGraphId ? getClientAssignablePerson(selectedClient) : null;
        const assignedTherapist = therapists.find((therapist) => therapist.id === selectedClient.therapistUserId) ||
            (currentTherapist?.id === selectedClient.therapistUserId ? currentTherapist : null) ||
            currentTherapist ||
            null;
        const therapistPerson = assignedTherapist ? getTherapistAssignablePerson(assignedTherapist) : null;
        const otherRelatedClientPeople = sortClientsByName(
            relatedClients.filter((client) => getClientGraphId(client) !== selectedClientGraphId)
        ).map(getClientAssignablePerson);
        const recordedSessionPeople = clientSessions.flatMap((session) =>
            (session.participants || []).map(getSessionParticipantAssignablePerson)
        );
        const peopleByKey = new Map<string, TranscriptAssignablePerson>();

        [
            selectedClientPerson,
            therapistPerson,
            ...otherRelatedClientPeople,
            ...recordedSessionPeople,
            ANONYMOUS_THERAPIST_TRANSCRIPT_PERSON,
            ANONYMOUS_CLIENT_TRANSCRIPT_PERSON,
            OTHER_TRANSCRIPT_PERSON,
        ]
            .filter((person): person is TranscriptAssignablePerson => !!person)
            .forEach((person) => {
                // Keep live Client/Team names ahead of older recording-time snapshots.
                if (!peopleByKey.has(person.key)) {
                    peopleByKey.set(person.key, person);
                }
            });

        return Array.from(peopleByKey.values());
    }, [clientSessions, currentTherapist, relatedClientSet, selectedClient, selectedTeamMember, therapists]);
    const clientReports = therapySessionReports
        .filter((report) => !!report.clientId && clientSessionIds.has(report.clientId))
        .sort((leftReport, rightReport) => {
            const leftTime = getReportDate(leftReport)?.getTime() || 0;
            const rightTime = getReportDate(rightReport)?.getTime() || 0;
            return rightTime - leftTime;
        });
    const clientTimelineDocuments = timelineDocuments
        .filter((document) => canUseTimelineDocuments && (selectedTeamMember ?
            document.therapistId === selectedTeamMember.id ||
                (document.therapistIds || []).includes(selectedTeamMember.id) ||
                (document.participants || []).some((participant) =>
                    participant.personType === 'therapist' && participant.personId === selectedTeamMember.id
                ) :
            !!document.clientId && clientSessionIds.has(document.clientId)))
        .sort((leftDocument, rightDocument) => {
            const leftTime = getTimelineDocumentDate(leftDocument)?.getTime() || 0;
            const rightTime = getTimelineDocumentDate(rightDocument)?.getTime() || 0;
            return rightTime - leftTime;
        });
    const timelineItems: Array<TimelineItem> = [
        // Audio sessions stay out of the timeline when the role cannot use audio scribe.
        ...(canUseAudioScribeAndReports ? clientSessions.map((session): TimelineItem => ({
            kind: 'session',
            key: `session:${session.id}`,
            id: session.id,
            dateMs: getSessionDate(session)?.getTime() || 0,
            session,
        })) : []),
        ...clientTimelineDocuments.map((document): TimelineItem => ({
            kind: 'document',
            key: `document:${document.id}`,
            id: document.id,
            dateMs: getTimelineDocumentDate(document)?.getTime() || 0,
            document,
        })),
    ].sort((leftItem, rightItem) => rightItem.dateMs - leftItem.dateMs);
    const timelineItemsKey = timelineItems.map((item) => item.key).join('|');
    // Briefly confirm successful uploads and reveal their entry after the timeline refresh.
    React.useEffect(() => {
        if (!uploadedTimelineFile) return;
        const timer = window.setTimeout(() => setUploadedTimelineFile(null), 6000);
        return () => window.clearTimeout(timer);
    }, [uploadedTimelineFile]);
    React.useEffect(() => {
        if (uploadedTimelineFile?.context === uploadContextKey &&
            timelineItemsKey.split('|').includes((uploadedTimelineFile.kind === 'media' ? 'session:' : 'document:') + uploadedTimelineFile.id)) {
            uploadedTimelineFileRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
        }
    }, [uploadedTimelineFile, uploadContextKey, timelineItemsKey]);
    const selectedReportTimelineItems = timelineItems.filter((item) => selectedReportTimelineItemKeys.has(item.key));
    const sortedReportTemplates = [...reportTemplates].sort((leftTemplate, rightTemplate) =>
        leftTemplate.name.localeCompare(rightTemplate.name)
    );
    const selectedReportTemplate = sortedReportTemplates.find((reportTemplate) =>
        reportTemplate.id === selectedReportTemplateId
    ) || null;
    // Keep the Back button outside the tabbed client record content.
    const tabs = isTeamTimeline ? [{
        label: 'Timeline',
        id: 'timeline',
        onSelect: () => setSelectedTabsItemIndex(0),
    }] : [
        {
            label: 'Details',
            id: 'record',
            onSelect: () => setSelectedTabsItemIndex(0),
        },
        {
            label: 'Timeline',
            id: 'timeline',
            onSelect: () => setSelectedTabsItemIndex(1),
        },
    ];
    if (canGenerateClientReports) {
        tabs.push({
            label: 'Reports',
            id: 'reports',
            onSelect: () => setSelectedTabsItemIndex(2),
        });
    }
    // App content is available independently of the service's audio and report features.
    if (showClientContent) {
        const contentTabIndex = tabs.length;
        tabs.push({
            label: 'Content',
            id: 'content',
            onSelect: () => setSelectedTabsItemIndex(contentTabIndex),
        });
    }
    // Clamp stale tab state immediately while the access-change effect catches up.
    const visibleSelectedTabsItemIndex = Math.min(selectedTabsItemIndex, tabs.length - 1);
    const selectedTabId = tabs[visibleSelectedTabsItemIndex]?.id || 'record';

    React.useEffect(() => {
        // Remove report-only UI state if access changes while this client record is open.
        if (canGenerateClientReports) {
            return;
        }

        setSelectedTabsItemIndex((previousIndex) => Math.min(previousIndex, 1));
        setSelectedReportTimelineItemKeys(new Set());
        setShowGenerateReportDialog(false);
        setSelectedReportTemplateId('');
        setClientReportGenerationError('');
    }, [canGenerateClientReports]);

    React.useEffect(() => {
        // Reset to the main record tab when navigating between client records.
        setSelectedTabsItemIndex(0);
        setActiveSessionPlaybackMsById({});
        setCollapsedSessionIds(new Set());
        setCollapsedTimelineDocumentIds(new Set());
        setExpandedTranscriptIds(new Set());
        setSessionNotesById({});
        setSavingSessionNotesById({});
        setSessionNotesErrorsById({});
        setSessionNotesSaveStatusById({});
        setOpenSessionOptionsMenuId(null);
        setHoveredTranscriptOptionsKey(null);
        setOpenTranscriptOptionsMenuKey(null);
        setOpenTranscriptAssignmentSubmenu(null);
        setHoveredTranscriptPersonMenuKey(null);
        setDeletingSessionIds({});
        setSelectedReportTimelineItemKeys(new Set());
        setShowGenerateReportDialog(false);
        setSelectedReportTemplateId('');
        setIsGeneratingClientReport(false);
        setIsCreatingTimelineNote(false);
        setIsUploadingTimelineFile(false);
        setClientReportGenerationError('');
        setReportTextZoom(readPreferredReportTextZoom(authenticatedUserId));
        setDeletingReportIds({});
        setReportTextById({});
        setSavingReportTextById({});
        setReportTextErrorsById({});
        setReportTextSaveStatusById({});
        setSessionSeekRequestsById({});
        setTimelineNotesById({});
        setSavingTimelineNotesById({});
        setTimelineNotesErrorsById({});
        setTimelineNotesSaveStatusById({});
        setDeletingTimelineDocumentIds({});
        setTimelineFileUrlsById({});
        setTimelineFileUrlErrorsById({});
        timelineCollapseDefaultsClientRef.current = null;
        timelineCollapseDefaultedItemKeysRef.current = new Set();
        sessionNotesLatestDraftsRef.current = {};
        sessionNotesSaveVersionsRef.current = {};
        timelineNotesLatestDraftsRef.current = {};
        timelineNotesSaveVersionsRef.current = {};
        reportTextLatestDraftsRef.current = {};
        reportTextSaveVersionsRef.current = {};
        transcriptBodyRefs.current = {};
        transcriptUtteranceRefs.current = {};
        lastAutoScrolledUtteranceKeyBySessionRef.current = {};
        Object.values(sessionNotesAutosaveTimersRef.current).forEach(clearTimeout);
        sessionNotesAutosaveTimersRef.current = {};
        Object.values(timelineNotesAutosaveTimersRef.current).forEach(clearTimeout);
        timelineNotesAutosaveTimersRef.current = {};
        Object.values(reportTextAutosaveTimersRef.current).forEach(clearTimeout);
        reportTextAutosaveTimersRef.current = {};
    }, [authenticatedUserId, timelineSubjectKey]);

    React.useEffect(() => {
        // On the first Timeline visit, start every visible item collapsed; later additions collapse too.
        const clientKey = timelineSubjectKey;
        if (selectedTabId !== 'timeline' || !timelineSubjectId || timelineItems.length === 0) {
            return;
        }

        const isFirstTimelineVisitForClient = timelineCollapseDefaultsClientRef.current !== clientKey;
        const defaultedItemKeys = timelineCollapseDefaultedItemKeysRef.current;
        const itemsToCollapse = timelineItems.filter((item) =>
            isFirstTimelineVisitForClient || !defaultedItemKeys.has(item.key)
        );
        if (itemsToCollapse.length === 0) {
            return;
        }

        setCollapsedSessionIds((previousCollapsedIds) => {
            const nextCollapsedIds = new Set(previousCollapsedIds);
            itemsToCollapse.forEach((item) => {
                if (item.kind === 'session') {
                    nextCollapsedIds.add(item.id);
                }
            });
            return nextCollapsedIds;
        });
        setCollapsedTimelineDocumentIds((previousCollapsedIds) => {
            const nextCollapsedIds = new Set(previousCollapsedIds);
            itemsToCollapse.forEach((item) => {
                if (item.kind === 'document') {
                    nextCollapsedIds.add(item.id);
                }
            });
            return nextCollapsedIds;
        });
        timelineCollapseDefaultsClientRef.current = clientKey;
        timelineCollapseDefaultedItemKeysRef.current = new Set(timelineItems.map((item) => item.key));
    }, [selectedTabId, timelineSubjectId, timelineSubjectKey, timelineItems, timelineItemsKey]);

    React.useEffect(() => {
        // Drop selected report timeline items that are no longer present after refresh/delete.
        const visibleTimelineItemKeys = new Set(timelineItemsKey ? timelineItemsKey.split('|') : []);
        setSelectedReportTimelineItemKeys((previousItemKeys) => {
            const nextItemKeys = new Set(
                Array.from(previousItemKeys).filter((itemKey) => visibleTimelineItemKeys.has(itemKey))
            );
            return nextItemKeys.size === previousItemKeys.size ? previousItemKeys : nextItemKeys;
        });
    }, [timelineItemsKey]);

    React.useEffect(() => {
        // Resolve file URLs lazily for timeline previews without storing signed URLs in Firestore.
        let isCancelled = false;
        clientTimelineDocuments
            .filter((document) => document.kind === 'file' && !!document.storagePath)
            .forEach((document) => {
                if (timelineFileUrlsById[document.id] || timelineFileUrlErrorsById[document.id]) {
                    return;
                }

                void onGetTimelineFileDownloadUrl(document)
                    .then((downloadUrl) => {
                        if (isCancelled) {
                            return;
                        }

                        setTimelineFileUrlsById((previousUrls) => ({
                            ...previousUrls,
                            [document.id]: downloadUrl,
                        }));
                    })
                    .catch((error) => {
                        if (isCancelled) {
                            return;
                        }

                        setTimelineFileUrlErrorsById((previousErrors) => ({
                            ...previousErrors,
                            [document.id]: error instanceof Error ? error.message : 'Could not load file preview.',
                        }));
                    });
            });

        return () => {
            isCancelled = true;
        };
    }, [clientTimelineDocuments, onGetTimelineFileDownloadUrl, timelineFileUrlErrorsById, timelineFileUrlsById]);

    React.useEffect(() => {
        // Clear any pending autosave timers when the client record view unmounts.
        return () => {
            Object.values(sessionNotesAutosaveTimersRef.current).forEach(clearTimeout);
            sessionNotesAutosaveTimersRef.current = {};
            Object.values(timelineNotesAutosaveTimersRef.current).forEach(clearTimeout);
            timelineNotesAutosaveTimersRef.current = {};
            Object.values(reportTextAutosaveTimersRef.current).forEach(clearTimeout);
            reportTextAutosaveTimersRef.current = {};
        };
    }, []);

    const handleSessionPlaybackTimeChange = React.useCallback((sessionId: string, currentTimeMs: number) => {
        // Store rounded cursor time per player so transcript highlighting follows playback.
        setActiveSessionPlaybackMsById((previousPlaybackTimes) => ({
            ...previousPlaybackTimes,
            [sessionId]: currentTimeMs,
        }));
    }, []);

    const handleSessionNotesMarkerClick = React.useCallback((sessionId: string, timestampMs: number) => {
        // Timestamp pills in clinical notes seek the matching session waveform/audio cursor.
        setSessionSeekRequestsById((previousSeekRequests) => ({
            ...previousSeekRequests,
            [sessionId]: {
                timestampMs,
                requestId: (previousSeekRequests[sessionId]?.requestId || 0) + 1,
            },
        }));
    }, []);

    const handleToggleTranscript = React.useCallback((sessionId: string) => {
        // Transcripts start collapsed and can be opened per session.
        setHoveredTranscriptOptionsKey(null);
        setOpenTranscriptOptionsMenuKey(null);
        setOpenTranscriptAssignmentSubmenu(null);
        setHoveredTranscriptPersonMenuKey(null);
        setExpandedTranscriptIds((previousExpandedIds) => {
            const nextExpandedIds = new Set(previousExpandedIds);
            if (nextExpandedIds.has(sessionId)) {
                nextExpandedIds.delete(sessionId);
            } else {
                nextExpandedIds.add(sessionId);
            }

            delete lastAutoScrolledUtteranceKeyBySessionRef.current[sessionId];
            return nextExpandedIds;
        });
    }, []);

    const handleRetrySessionTranscription = React.useCallback(async (sessionId: string) => {
        // Keep retry progress and failures local while the session list refreshes from Firestore.
        if (!onRetrySessionTranscription || retryingTranscriptIds[sessionId]) {
            return;
        }

        setTranscriptRetryErrorsById((previousErrors) => {
            const nextErrors = {...previousErrors};
            delete nextErrors[sessionId];
            return nextErrors;
        });
        setRetryingTranscriptIds((previousRetryingIds) => ({
            ...previousRetryingIds,
            [sessionId]: true,
        }));

        try {
            await onRetrySessionTranscription(sessionId);
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleRetrySessionTranscription(): Retry failed:', error);
            setTranscriptRetryErrorsById((previousErrors) => ({
                ...previousErrors,
                [sessionId]: error instanceof Error ? error.message : 'Could not retry the transcript.',
            }));
        } finally {
            setRetryingTranscriptIds((previousRetryingIds) => {
                const nextRetryingIds = {...previousRetryingIds};
                delete nextRetryingIds[sessionId];
                return nextRetryingIds;
            });
        }
    }, [onRetrySessionTranscription, retryingTranscriptIds]);

    const handleToggleSessionCollapsed = React.useCallback((sessionId: string) => {
        // Collapse the bulky session body while keeping the header actions visible.
        setOpenSessionOptionsMenuId(null);
        setHoveredTranscriptOptionsKey(null);
        setOpenTranscriptOptionsMenuKey(null);
        setOpenTranscriptAssignmentSubmenu(null);
        setHoveredTranscriptPersonMenuKey(null);
        setCollapsedSessionIds((previousCollapsedIds) => {
            const nextCollapsedIds = new Set(previousCollapsedIds);
            if (nextCollapsedIds.has(sessionId)) {
                nextCollapsedIds.delete(sessionId);
            } else {
                nextCollapsedIds.add(sessionId);
            }

            return nextCollapsedIds;
        });
    }, []);

    const handleToggleTimelineDocumentCollapsed = React.useCallback((documentId: string) => {
        // Collapse non-audio timeline documents while keeping selection and row actions visible.
        setOpenSessionOptionsMenuId(null);
        setCollapsedTimelineDocumentIds((previousCollapsedIds) => {
            const nextCollapsedIds = new Set(previousCollapsedIds);
            if (nextCollapsedIds.has(documentId)) {
                nextCollapsedIds.delete(documentId);
            } else {
                nextCollapsedIds.add(documentId);
            }

            return nextCollapsedIds;
        });
    }, []);

    const handleTranscriptAssignmentMenuClick = React.useCallback((
        session: TherapySession,
        utterances: Array<TherapySessionTranscriptUtterance>,
        utteranceIndex: number,
        scope: TranscriptAssignmentScope,
        person: TranscriptAssignablePerson
    ) => {
        // Persist either one line or every line from the same diarized speaker.
        const sourceSpeakerIdentity = getUtteranceSpeakerIdentity(utterances[utteranceIndex]);
        const nextUtterances = utterances.map((utterance, candidateIndex) => {
            const shouldAssign = scope === 'line' ?
                candidateIndex === utteranceIndex :
                getUtteranceSpeakerIdentity(utterance) === sourceSpeakerIdentity;

            return shouldAssign ? assignUtteranceToPerson(utterance, person) : utterance;
        });

        setOpenTranscriptOptionsMenuKey(null);
        setOpenTranscriptAssignmentSubmenu(null);
        setHoveredTranscriptOptionsKey(null);
        setHoveredTranscriptPersonMenuKey(null);

        void onSaveSessionTranscriptUtterances(session.id, nextUtterances.map(sanitizeUtteranceForSave)).catch((error) => {
            console.error('CLIENT_RECORD_VIEW: handleTranscriptAssignmentMenuClick(): Error saving transcript assignment:', error);
            window.alert(error instanceof Error ? error.message : 'Could not save the transcript speaker assignment.');
        });
    }, [onSaveSessionTranscriptUtterances]);

    const handleCreateSessionTag = React.useCallback(async (label: string) => {
        // Add a new tag to the current therapist's personal MRU tag list.
        const trimmedLabel = label.trim();
        if (!trimmedLabel) {
            return;
        }

        if (!currentTherapist?.id) {
            throw new Error('No therapist record is available for saving tags.');
        }

        const nowMs = Date.now();
        const existingTags = currentTherapist.sessionTags || [];
        const existingTag = existingTags.find((tag) => tag.label.toLowerCase() === trimmedLabel.toLowerCase());
        const nextTag: TherapySessionTagDefinition = existingTag ? {
            ...existingTag,
            lastUsedAtMs: nowMs,
        } : {
            id: createLocalSessionTagId('tag'),
            label: trimmedLabel,
            color: getSessionTagColour(trimmedLabel, new Set(existingTags.map((tag) => tag.color))),
            createdAtMs: nowMs,
            lastUsedAtMs: nowMs,
        };
        const nextTags = sortSessionTagsByMostRecent([
            nextTag,
            ...existingTags.filter((tag) => tag.id !== nextTag.id),
        ]);

        await onSaveTherapistSessionTags(currentTherapist.id, nextTags);
    }, [currentTherapist, onSaveTherapistSessionTags]);

    const handlePlaceSessionTag = React.useCallback(async (
        session: TherapySession,
        tag: TherapySessionTagDefinition,
        timestampMs: number
    ) => {
        // Include the displayed baseline so rapid clicks merge without replacing other pending markers.
        if (!currentTherapist?.id) {
            throw new Error('No therapist record is available for placing tags.');
        }

        const nowMs = Date.now();
        const nextPlacedTag: TherapySessionPlacedTag = {
            id: createLocalSessionTagId('placed-tag'),
            tagId: tag.id,
            label: tag.label,
            color: tag.color,
            timestampMs: Math.max(0, Math.round(timestampMs)),
            createdAtMs: nowMs,
        };
        const nextPlacedTags = [...(session.placedTags || []), nextPlacedTag]
            .sort((leftTag, rightTag) => leftTag.timestampMs - rightTag.timestampMs);
        const nextTherapistTag: TherapySessionTagDefinition = {
            ...tag,
            lastUsedAtMs: nowMs,
        };
        const nextTherapistTags = sortSessionTagsByMostRecent([
            nextTherapistTag,
            ...(currentTherapist.sessionTags || []).filter((candidateTag) => candidateTag.id !== tag.id),
        ]);

        await onSaveSessionPlacedTags(session.id, nextPlacedTags, session.placedTags || []);
        // A recency update is secondary; its failure must not report the saved marker as failed.
        void onSaveTherapistSessionTags(currentTherapist.id, nextTherapistTags).catch((error) => {
            console.warn('CLIENT_RECORD_VIEW: Could not update label recency:', error);
        });
    }, [currentTherapist, onSaveSessionPlacedTags, onSaveTherapistSessionTags]);

    const handleRemoveSessionPlacedTag = React.useCallback(async (session: TherapySession, placedTagId: string) => {
        // Remove one timestamped tag marker from the session waveform.
        const nextPlacedTags = (session.placedTags || []).filter((placedTag) => placedTag.id !== placedTagId);
        await onSaveSessionPlacedTags(session.id, nextPlacedTags, session.placedTags || []);
    }, [onSaveSessionPlacedTags]);

    React.useEffect(() => {
        // Keep expanded transcript scroll boxes aligned with the current playback line.
        clientSessions.forEach((session) => {
            if (!expandedTranscriptIds.has(session.id)) {
                delete lastAutoScrolledUtteranceKeyBySessionRef.current[session.id];
                return;
            }

            const currentPlaybackMs = activeSessionPlaybackMsById[session.id];
            if (typeof currentPlaybackMs !== 'number') {
                return;
            }

            const utterances = getSessionUtterances(session);
            const activeUtteranceIndex = utterances.findIndex((utterance) =>
                isUtteranceActiveAtTime(utterance, currentPlaybackMs)
            );
            if (activeUtteranceIndex < 0) {
                return;
            }

            const activeUtterance = utterances[activeUtteranceIndex];
            const utteranceKey = getUtteranceRenderKey(session.id, activeUtterance, activeUtteranceIndex);
            if (lastAutoScrolledUtteranceKeyBySessionRef.current[session.id] === utteranceKey) {
                return;
            }

            const transcriptBody = transcriptBodyRefs.current[session.id];
            const utteranceElement = transcriptUtteranceRefs.current[session.id]?.[utteranceKey];
            if (!transcriptBody || !utteranceElement) {
                return;
            }

            const transcriptRect = transcriptBody.getBoundingClientRect();
            const utteranceRect = utteranceElement.getBoundingClientRect();
            const targetScrollTop = transcriptBody.scrollTop +
                (utteranceRect.top - transcriptRect.top) -
                (transcriptBody.clientHeight / 2) +
                (utteranceElement.clientHeight / 2);

            lastAutoScrolledUtteranceKeyBySessionRef.current[session.id] = utteranceKey;
            transcriptBody.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: 'smooth',
            });
        });
    }, [activeSessionPlaybackMsById, clientSessions, expandedTranscriptIds]);

    const handleSaveSessionNotes = React.useCallback(async (sessionId: string, clinicalNotes: string) => {
        // Save the therapist's clinical notes back to the Sessions document.
        const saveVersion = (sessionNotesSaveVersionsRef.current[sessionId] || 0) + 1;
        sessionNotesSaveVersionsRef.current[sessionId] = saveVersion;
        setSavingSessionNotesById((previousSavingNotes) => ({
            ...previousSavingNotes,
            [sessionId]: true,
        }));
        setSessionNotesErrorsById((previousErrors) => {
            const nextErrors = { ...previousErrors };
            delete nextErrors[sessionId];
            return nextErrors;
        });
        setSessionNotesSaveStatusById((previousStatuses) => ({
            ...previousStatuses,
            [sessionId]: 'saving',
        }));

        try {
            await onSaveSessionClinicalNotes(sessionId, clinicalNotes);
            if (sessionNotesLatestDraftsRef.current[sessionId] === clinicalNotes) {
                setSessionNotesSaveStatusById((previousStatuses) => ({
                    ...previousStatuses,
                    [sessionId]: 'saved',
                }));
            }
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleSaveSessionNotes(): Error saving notes:', error);
            if (sessionNotesLatestDraftsRef.current[sessionId] === clinicalNotes) {
                setSessionNotesErrorsById((previousErrors) => ({
                    ...previousErrors,
                    [sessionId]: error instanceof Error ? error.message : 'Could not save notes.',
                }));
                setSessionNotesSaveStatusById((previousStatuses) => ({
                    ...previousStatuses,
                    [sessionId]: 'pending',
                }));
            }
        } finally {
            if (sessionNotesSaveVersionsRef.current[sessionId] === saveVersion) {
                setSavingSessionNotesById((previousSavingNotes) => {
                    const nextSavingNotes = { ...previousSavingNotes };
                    delete nextSavingNotes[sessionId];
                    return nextSavingNotes;
                });
            }
        }
    }, [onSaveSessionClinicalNotes]);

    const handleSessionNotesChange = React.useCallback((sessionId: string, clinicalNotes: string, savedClinicalNotes: string) => {
        // Keep in-progress notes local and auto-save shortly after typing pauses.
        sessionNotesLatestDraftsRef.current[sessionId] = clinicalNotes;
        setSessionNotesById((previousNotes) => ({
            ...previousNotes,
            [sessionId]: clinicalNotes,
        }));
        setSessionNotesErrorsById((previousErrors) => {
            if (!previousErrors[sessionId]) {
                return previousErrors;
            }

            const nextErrors = { ...previousErrors };
            delete nextErrors[sessionId];
            return nextErrors;
        });

        if (sessionNotesAutosaveTimersRef.current[sessionId]) {
            clearTimeout(sessionNotesAutosaveTimersRef.current[sessionId]);
        }

        if (clinicalNotes === savedClinicalNotes) {
            setSessionNotesSaveStatusById((previousStatuses) => {
                const nextStatuses = { ...previousStatuses };
                delete nextStatuses[sessionId];
                return nextStatuses;
            });
            return;
        }

        setSessionNotesSaveStatusById((previousStatuses) => ({
            ...previousStatuses,
            [sessionId]: 'pending',
        }));
        sessionNotesAutosaveTimersRef.current[sessionId] = setTimeout(() => {
            delete sessionNotesAutosaveTimersRef.current[sessionId];
            void handleSaveSessionNotes(sessionId, clinicalNotes);
        }, SESSION_NOTES_AUTOSAVE_DELAY_MS);
    }, [handleSaveSessionNotes]);

    const handleSessionNotesBlur = React.useCallback((sessionId: string, clinicalNotes: string, savedClinicalNotes: string) => {
        // Save immediately on blur so recent edits are not left waiting on the debounce.
        if (sessionNotesAutosaveTimersRef.current[sessionId]) {
            clearTimeout(sessionNotesAutosaveTimersRef.current[sessionId]);
            delete sessionNotesAutosaveTimersRef.current[sessionId];
        }

        if (clinicalNotes !== savedClinicalNotes) {
            void handleSaveSessionNotes(sessionId, clinicalNotes);
        }
    }, [handleSaveSessionNotes]);

    const handleSaveTimelineNotes = React.useCallback(async (documentId: string, clinicalNotes: string) => {
        // Save standalone clinical notes back to the TimelineDocuments document.
        const saveVersion = (timelineNotesSaveVersionsRef.current[documentId] || 0) + 1;
        timelineNotesSaveVersionsRef.current[documentId] = saveVersion;
        setSavingTimelineNotesById((previousSavingNotes) => ({
            ...previousSavingNotes,
            [documentId]: true,
        }));
        setTimelineNotesErrorsById((previousErrors) => {
            const nextErrors = { ...previousErrors };
            delete nextErrors[documentId];
            return nextErrors;
        });
        setTimelineNotesSaveStatusById((previousStatuses) => ({
            ...previousStatuses,
            [documentId]: 'saving',
        }));

        try {
            await onSaveTimelineClinicalNote(documentId, clinicalNotes);
            if (timelineNotesLatestDraftsRef.current[documentId] === clinicalNotes) {
                setTimelineNotesSaveStatusById((previousStatuses) => ({
                    ...previousStatuses,
                    [documentId]: 'saved',
                }));
            }
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleSaveTimelineNotes(): Error saving timeline note:', error);
            if (timelineNotesLatestDraftsRef.current[documentId] === clinicalNotes) {
                setTimelineNotesErrorsById((previousErrors) => ({
                    ...previousErrors,
                    [documentId]: error instanceof Error ? error.message : 'Could not save note.',
                }));
                setTimelineNotesSaveStatusById((previousStatuses) => ({
                    ...previousStatuses,
                    [documentId]: 'pending',
                }));
            }
        } finally {
            if (timelineNotesSaveVersionsRef.current[documentId] === saveVersion) {
                setSavingTimelineNotesById((previousSavingNotes) => {
                    const nextSavingNotes = { ...previousSavingNotes };
                    delete nextSavingNotes[documentId];
                    return nextSavingNotes;
                });
            }
        }
    }, [onSaveTimelineClinicalNote]);

    const handleTimelineNotesChange = React.useCallback((documentId: string, clinicalNotes: string, savedClinicalNotes: string) => {
        // Keep standalone note edits local and auto-save after typing pauses.
        timelineNotesLatestDraftsRef.current[documentId] = clinicalNotes;
        setTimelineNotesById((previousNotes) => ({
            ...previousNotes,
            [documentId]: clinicalNotes,
        }));
        setTimelineNotesErrorsById((previousErrors) => {
            if (!previousErrors[documentId]) {
                return previousErrors;
            }

            const nextErrors = { ...previousErrors };
            delete nextErrors[documentId];
            return nextErrors;
        });

        if (timelineNotesAutosaveTimersRef.current[documentId]) {
            clearTimeout(timelineNotesAutosaveTimersRef.current[documentId]);
        }

        if (clinicalNotes === savedClinicalNotes) {
            setTimelineNotesSaveStatusById((previousStatuses) => {
                const nextStatuses = { ...previousStatuses };
                delete nextStatuses[documentId];
                return nextStatuses;
            });
            return;
        }

        setTimelineNotesSaveStatusById((previousStatuses) => ({
            ...previousStatuses,
            [documentId]: 'pending',
        }));
        timelineNotesAutosaveTimersRef.current[documentId] = setTimeout(() => {
            delete timelineNotesAutosaveTimersRef.current[documentId];
            void handleSaveTimelineNotes(documentId, clinicalNotes);
        }, SESSION_NOTES_AUTOSAVE_DELAY_MS);
    }, [handleSaveTimelineNotes]);

    const handleTimelineNotesBlur = React.useCallback((documentId: string, clinicalNotes: string, savedClinicalNotes: string) => {
        // Save standalone note edits immediately when the editor loses focus.
        if (timelineNotesAutosaveTimersRef.current[documentId]) {
            clearTimeout(timelineNotesAutosaveTimersRef.current[documentId]);
            delete timelineNotesAutosaveTimersRef.current[documentId];
        }

        if (clinicalNotes !== savedClinicalNotes) {
            void handleSaveTimelineNotes(documentId, clinicalNotes);
        }
    }, [handleSaveTimelineNotes]);

    const handleSaveReportText = React.useCallback(async (reportId: string, reportText: string) => {
        // Save rich report edits back to the Reports document.
        const saveVersion = (reportTextSaveVersionsRef.current[reportId] || 0) + 1;
        reportTextSaveVersionsRef.current[reportId] = saveVersion;
        setSavingReportTextById((previousSavingReports) => ({
            ...previousSavingReports,
            [reportId]: true,
        }));
        setReportTextErrorsById((previousErrors) => {
            const nextErrors = { ...previousErrors };
            delete nextErrors[reportId];
            return nextErrors;
        });
        setReportTextSaveStatusById((previousStatuses) => ({
            ...previousStatuses,
            [reportId]: 'saving',
        }));

        try {
            await onSaveSessionReportText(reportId, reportText);
            if (reportTextLatestDraftsRef.current[reportId] === reportText) {
                setReportTextSaveStatusById((previousStatuses) => ({
                    ...previousStatuses,
                    [reportId]: 'saved',
                }));
            }
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleSaveReportText(): Error saving report text:', error);
            if (reportTextLatestDraftsRef.current[reportId] === reportText) {
                setReportTextErrorsById((previousErrors) => ({
                    ...previousErrors,
                    [reportId]: error instanceof Error ? error.message : 'Could not save report.',
                }));
                setReportTextSaveStatusById((previousStatuses) => ({
                    ...previousStatuses,
                    [reportId]: 'pending',
                }));
            }
        } finally {
            if (reportTextSaveVersionsRef.current[reportId] === saveVersion) {
                setSavingReportTextById((previousSavingReports) => {
                    const nextSavingReports = { ...previousSavingReports };
                    delete nextSavingReports[reportId];
                    return nextSavingReports;
                });
            }
        }
    }, [onSaveSessionReportText]);

    const handleReportTextChange = React.useCallback((reportId: string, reportText: string, savedReportText: string) => {
        // Keep report edits local and auto-save after typing pauses.
        reportTextLatestDraftsRef.current[reportId] = reportText;
        setReportTextById((previousReports) => ({
            ...previousReports,
            [reportId]: reportText,
        }));
        setReportTextErrorsById((previousErrors) => {
            if (!previousErrors[reportId]) {
                return previousErrors;
            }

            const nextErrors = { ...previousErrors };
            delete nextErrors[reportId];
            return nextErrors;
        });

        if (reportTextAutosaveTimersRef.current[reportId]) {
            clearTimeout(reportTextAutosaveTimersRef.current[reportId]);
        }

        if (reportText === savedReportText) {
            setReportTextSaveStatusById((previousStatuses) => {
                const nextStatuses = { ...previousStatuses };
                delete nextStatuses[reportId];
                return nextStatuses;
            });
            return;
        }

        setReportTextSaveStatusById((previousStatuses) => ({
            ...previousStatuses,
            [reportId]: 'pending',
        }));
        reportTextAutosaveTimersRef.current[reportId] = setTimeout(() => {
            delete reportTextAutosaveTimersRef.current[reportId];
            void handleSaveReportText(reportId, reportText);
        }, SESSION_NOTES_AUTOSAVE_DELAY_MS);
    }, [handleSaveReportText]);

    const handleReportTextBlur = React.useCallback((reportId: string, reportText: string, savedReportText: string) => {
        // Save report edits immediately when focus leaves the editor.
        if (reportTextAutosaveTimersRef.current[reportId]) {
            clearTimeout(reportTextAutosaveTimersRef.current[reportId]);
            delete reportTextAutosaveTimersRef.current[reportId];
        }

        if (reportText !== savedReportText) {
            void handleSaveReportText(reportId, reportText);
        }
    }, [handleSaveReportText]);

    const handleDeleteSession = React.useCallback(async (session: TherapySession) => {
        // Confirm before deleting the session record and any linked audio.
        const confirmed = window.confirm('Delete this session? This will remove the session and its audio recording.');
        if (!confirmed) {
            setOpenSessionOptionsMenuId(null);
            return;
        }

        setOpenSessionOptionsMenuId(null);
        if (sessionNotesAutosaveTimersRef.current[session.id]) {
            clearTimeout(sessionNotesAutosaveTimersRef.current[session.id]);
            delete sessionNotesAutosaveTimersRef.current[session.id];
        }
        delete sessionNotesLatestDraftsRef.current[session.id];
        delete sessionNotesSaveVersionsRef.current[session.id];
        setSessionNotesById((previousNotes) => {
            const nextNotes = { ...previousNotes };
            delete nextNotes[session.id];
            return nextNotes;
        });
        setSessionNotesErrorsById((previousErrors) => {
            const nextErrors = { ...previousErrors };
            delete nextErrors[session.id];
            return nextErrors;
        });
        setSessionNotesSaveStatusById((previousStatuses) => {
            const nextStatuses = { ...previousStatuses };
            delete nextStatuses[session.id];
            return nextStatuses;
        });
        setSelectedReportTimelineItemKeys((previousItemKeys) => {
            const nextItemKeys = new Set(previousItemKeys);
            nextItemKeys.delete(`session:${session.id}`);
            return nextItemKeys;
        });
        setDeletingSessionIds((previousDeletingSessionIds) => ({
            ...previousDeletingSessionIds,
            [session.id]: true,
        }));

        try {
            await onDeleteSession(session);
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleDeleteSession(): Error deleting session:', error);
            window.alert(error instanceof Error ? error.message : 'Could not delete the session.');
        } finally {
            setDeletingSessionIds((previousDeletingSessionIds) => {
                const nextDeletingSessionIds = { ...previousDeletingSessionIds };
                delete nextDeletingSessionIds[session.id];
                return nextDeletingSessionIds;
            });
        }
    }, [onDeleteSession]);

    const getSessionTitle = React.useCallback((session: TherapySession) => {
        // Show the recorded roster for group sessions until the therapist edits the title.
        const participantNames = Array.from(new Set(
            (session.participants || []).map((participant) => participant.displayName).filter(Boolean)
        ));
        const defaultPeopleLabel = participantNames.length > 0 ?
            participantNames.join(', ') : session.clientName || displayName;
        return session.title || `Session with ${defaultPeopleLabel}`;
    }, [displayName]);

    const handleCreateTimelineClinicalNote = React.useCallback(async () => {
        // Associate new notes with the actual Client or Team record displayed by this timeline.
        if (!timelineSubjectId || !canUseTimelineDocuments || isCreatingTimelineNote) {
            return;
        }

        setIsCreatingTimelineNote(true);
        try {
            await onCreateTimelineClinicalNote({
                ...(selectedTeamMember ?
                    { therapistId: selectedTeamMember.id, therapistName: displayName } :
                    { clientId: selectedClient.id, clientName: displayName }),
                title: `Clinical note for ${displayName}`,
                healthcareProviderId: timelineProviderId,
                providerId: timelineProviderId,
                clinicalNotes: '',
            });
            setSelectedTabsItemIndex(timelineTabIndex);
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleCreateTimelineClinicalNote(): Error creating note:', error);
            window.alert(error instanceof Error ? error.message : 'Could not create the clinical note.');
        } finally {
            setIsCreatingTimelineNote(false);
        }
    }, [canUseTimelineDocuments, displayName, isCreatingTimelineNote, onCreateTimelineClinicalNote, selectedClient.id, selectedTeamMember, timelineProviderId, timelineSubjectId, timelineTabIndex]);

    const handleUploadTimelineFile = React.useCallback(async (file: File | null | undefined) => {
        // Use typed Team ownership for files without creating a Client association.
        if (!file || !timelineSubjectId || !canUseTimelineDocuments || isUploadingTimelineFile) {
            return;
        }

        setIsUploadingTimelineFile(true);
        try {
            await onUploadTimelineFile({
                ...(selectedTeamMember ?
                    { therapistId: selectedTeamMember.id, therapistName: displayName } :
                    { clientId: selectedClient.id, clientName: displayName }),
                title: file.name,
                healthcareProviderId: timelineProviderId,
                providerId: timelineProviderId,
            }, file);
            setSelectedTabsItemIndex(timelineTabIndex);
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleUploadTimelineFile(): Error uploading timeline file:', error);
            window.alert(error instanceof Error ? error.message : 'Could not upload the timeline file.');
        } finally {
            setIsUploadingTimelineFile(false);
        }
    }, [canUseTimelineDocuments, displayName, isUploadingTimelineFile, onUploadTimelineFile, selectedClient.id, selectedTeamMember, timelineProviderId, timelineSubjectId, timelineTabIndex]);

    const handleDeleteTimelineDocument = React.useCallback(async (document: TimelineDocument) => {
        // Confirm before deleting a non-audio timeline document and any attached file.
        const confirmed = window.confirm(`Delete "${getTimelineDocumentTitle(document)}"?`);
        if (!confirmed) {
            return;
        }

        setSelectedReportTimelineItemKeys((previousItemKeys) => {
            const nextItemKeys = new Set(previousItemKeys);
            nextItemKeys.delete(`document:${document.id}`);
            return nextItemKeys;
        });
        setCollapsedTimelineDocumentIds((previousCollapsedIds) => {
            const nextCollapsedIds = new Set(previousCollapsedIds);
            nextCollapsedIds.delete(document.id);
            return nextCollapsedIds;
        });
        if (timelineNotesAutosaveTimersRef.current[document.id]) {
            clearTimeout(timelineNotesAutosaveTimersRef.current[document.id]);
            delete timelineNotesAutosaveTimersRef.current[document.id];
        }
        setDeletingTimelineDocumentIds((previousDeletingIds) => ({
            ...previousDeletingIds,
            [document.id]: true,
        }));

        try {
            await onDeleteTimelineDocument(document);
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleDeleteTimelineDocument(): Error deleting document:', error);
            window.alert(error instanceof Error ? error.message : 'Could not delete the timeline document.');
        } finally {
            setDeletingTimelineDocumentIds((previousDeletingIds) => {
                const nextDeletingIds = { ...previousDeletingIds };
                delete nextDeletingIds[document.id];
                return nextDeletingIds;
            });
        }
    }, [onDeleteTimelineDocument]);

    const handleDownloadTimelineFile = React.useCallback((document: TimelineDocument) => {
        // Download uploaded client timeline files through the data layer.
        void onDownloadTimelineFile(document).catch((error) => {
            console.error('CLIENT_RECORD_VIEW: handleDownloadTimelineFile(): Error downloading file:', error);
            window.alert(error instanceof Error ? error.message : 'Could not download the timeline file.');
        });
    }, [onDownloadTimelineFile]);

    const handleToggleReportTimelineItemSelection = React.useCallback((itemKey: string, isSelected: boolean) => {
        // Track the timeline items the therapist wants to combine into the next report.
        setSelectedReportTimelineItemKeys((previousItemKeys) => {
            const nextItemKeys = new Set(previousItemKeys);
            if (isSelected) {
                nextItemKeys.add(itemKey);
            } else {
                nextItemKeys.delete(itemKey);
            }

            return nextItemKeys;
        });
        setClientReportGenerationError('');
    }, []);

    const handleOpenGenerateReportDialog = React.useCallback(() => {
        // Collect report options before starting the selected timeline report generation.
        if (!canGenerateClientReports || selectedReportTimelineItems.length === 0 || isGeneratingClientReport) {
            return;
        }

        setSelectedReportTemplateId('');
        setClientReportGenerationError('');
        setShowGenerateReportDialog(true);
    }, [canGenerateClientReports, isGeneratingClientReport, selectedReportTimelineItems.length]);

    const handleGenerateSelectedSessionsReport = React.useCallback((reportTemplate: ReportTemplate | null) => {
        // Build one combined prompt from all checked timeline items and send it to Functions.
        const itemsToReport = timelineItems.filter((item) => selectedReportTimelineItemKeys.has(item.key));
        if (!canGenerateClientReports || itemsToReport.length === 0 || isGeneratingClientReport) {
            return;
        }

        const reportTimelineContexts: Array<TimelineReportPromptItem> = itemsToReport.map((item) => {
            if (item.kind === 'session') {
                return {
                    kind: 'session',
                    key: item.key,
                    session: item.session,
                    sessionTitle: getSessionTitle(item.session),
                    utterances: getSessionUtterances(item.session),
                    clinicalNotes: formatClinicalNotesForPlainText(sessionNotesById[item.session.id] ?? item.session.clinicalNotes ?? ''),
                };
            }

            return {
                kind: item.document.kind,
                key: item.key,
                document: item.document,
                clinicalNotes: formatClinicalNotesForPlainText(
                    timelineNotesById[item.document.id] ?? item.document.clinicalNotes ?? ''
                ),
            } as TimelineReportPromptDocument;
        });
        const reportSessionContexts = reportTimelineContexts
            .filter((item): item is SessionReportPromptSession => item.kind === 'session');
        const prompt = buildSessionReportPrompt({
            items: reportTimelineContexts,
            reportTemplate,
            selectedClient,
            relatedClientSet,
            therapists,
            currentTherapist,
            speechNeeds,
        });

        setIsGeneratingClientReport(true);
        setClientReportGenerationError('');

        void onGenerateSessionReport({
            clientId: selectedClient.id || selectedClient.userId || '',
            clientName: displayName,
            healthcareProviderId: selectedClient.providerId || currentTherapist?.healthcareProviderId,
            sessionIds: reportSessionContexts.map((sessionContext) => sessionContext.session.id),
            sessionTitles: reportSessionContexts.map((sessionContext) => sessionContext.sessionTitle),
            timelineItemIds: itemsToReport.map((item) => item.id),
            timelineItemTitles: reportTimelineContexts.map((item) =>
                item.kind === 'session' ? item.sessionTitle : getTimelineDocumentTitle(item.document)
            ),
            timelineItemKinds: reportTimelineContexts.map((item) => item.kind),
            prompt,
            reportTemplateId: reportTemplate?.id,
            reportTemplateName: reportTemplate?.name,
            reportTemplateStoragePath: reportTemplate?.storagePath,
            reportTemplateOriginalName: reportTemplate?.originalName,
            reportTemplateContentType: reportTemplate?.contentType,
            reportTemplatePromptExtraInstructions: reportTemplate?.promptExtraInstructions || '',
        })
            .then(() => {
                setShowGenerateReportDialog(false);
                setSelectedReportTemplateId('');
                setSelectedReportTimelineItemKeys(new Set());
                setSelectedTabsItemIndex(canUseAudioScribeAndReports ? 2 : 1);
            })
            .catch((error) => {
                console.error('CLIENT_RECORD_VIEW: handleGenerateSelectedSessionsReport(): Error generating report:', error);
                setClientReportGenerationError(error instanceof Error ? error.message : 'Could not generate the report.');
            })
            .finally(() => {
                setIsGeneratingClientReport(false);
            });
    }, [
        canUseAudioScribeAndReports,
        canGenerateClientReports,
        currentTherapist,
        displayName,
        getSessionTitle,
        isGeneratingClientReport,
        onGenerateSessionReport,
        relatedClientSet,
        selectedClient,
        selectedReportTimelineItemKeys,
        sessionNotesById,
        speechNeeds,
        therapists,
        timelineItems,
        timelineNotesById,
    ]);

    const handleSaveSessionTitleDraft = React.useCallback(async (session: TherapySession, draft: string) => {
        // Keep the existing default session title when an edit is cleared.
        const fallbackTitle = getSessionTitle({ ...session, title: undefined });
        const nextTitle = draft.trim() || fallbackTitle;
        if (nextTitle !== getSessionTitle(session)) {
            await onSaveSessionTitle(session.id, nextTitle);
        }
    }, [getSessionTitle, onSaveSessionTitle]);

    const handleSaveSessionDateDraft = React.useCallback(async (session: TherapySession, draft: string) => {
        // Native datetime-local values represent the clinician's local wall-clock time.
        const currentDate = getSessionDate(session);
        if (draft === formatDateTimeLocalValue(currentDate)) {
            return;
        }
        const nextDate = parseDateTimeLocalValue(draft);
        if (!nextDate) {
            throw new Error('Choose a valid session date and time.');
        }
        await onSaveSessionDate(session.id, nextDate);
    }, [onSaveSessionDate]);

    const handleSaveTimelineDocumentTitleDraft = React.useCallback(async (
        document: TimelineDocument,
        draft: string
    ) => {
        // Empty edits return to each document kind's stable fallback title.
        const fallbackTitle = getTimelineDocumentTitle({ ...document, title: undefined });
        const nextTitle = draft.trim() || fallbackTitle;
        if (nextTitle !== getTimelineDocumentTitle(document)) {
            await onSaveTimelineDocumentTitle(document.id, nextTitle);
        }
    }, [onSaveTimelineDocumentTitle]);

    const handleSaveTimelineDocumentDateDraft = React.useCallback(async (
        document: TimelineDocument,
        draft: string
    ) => {
        // Save only the editable timeline date, leaving creation/upload timestamps untouched.
        const currentDate = getTimelineDocumentDate(document);
        if (draft === formatDateTimeLocalValue(currentDate)) {
            return;
        }
        const nextDate = parseDateTimeLocalValue(draft);
        if (!nextDate) {
            throw new Error('Choose a valid document date and time.');
        }
        await onSaveTimelineDocumentDate(document.id, nextDate);
    }, [onSaveTimelineDocumentDate]);

    const handleReportZoomIn = React.useCallback(() => {
        // Nudge report text up in small steps so the markdown layout stays stable.
        setReportTextZoom((currentZoom) => {
            const nextZoom = normalizeReportTextZoom(currentZoom + REPORT_TEXT_ZOOM_STEP);
            savePreferredReportTextZoom(authenticatedUserId, nextZoom);
            return nextZoom;
        });
    }, [authenticatedUserId]);

    const handleReportZoomOut = React.useCallback(() => {
        // Keep the report readable while allowing denser text for long reports.
        setReportTextZoom((currentZoom) => {
            const nextZoom = normalizeReportTextZoom(currentZoom - REPORT_TEXT_ZOOM_STEP);
            savePreferredReportTextZoom(authenticatedUserId, nextZoom);
            return nextZoom;
        });
    }, [authenticatedUserId]);

    const handleDownloadReport = React.useCallback((report: TherapySessionReport) => {
        // Download completed generated reports as Word documents.
        void downloadReportDocx(report).catch((error) => {
            console.error('CLIENT_RECORD_VIEW: handleDownloadReport(): Error downloading report:', error);
            window.alert(error instanceof Error ? error.message : 'Could not download the report.');
        });
    }, []);

    const handleDeleteReport = React.useCallback(async (report: TherapySessionReport) => {
        // Confirm before deleting a generated report from Firestore.
        const confirmed = window.confirm(`Delete report "${getReportTitle(report)}"?`);
        if (!confirmed) {
            return;
        }

        try {
            if (reportTextAutosaveTimersRef.current[report.id]) {
                clearTimeout(reportTextAutosaveTimersRef.current[report.id]);
                delete reportTextAutosaveTimersRef.current[report.id];
            }
            delete reportTextLatestDraftsRef.current[report.id];
            delete reportTextSaveVersionsRef.current[report.id];
            setDeletingReportIds((currentDeletingReportIds) => ({
                ...currentDeletingReportIds,
                [report.id]: true,
            }));
            await onDeleteSessionReport(report);
            setReportTextById((previousReports) => {
                const nextReports = { ...previousReports };
                delete nextReports[report.id];
                return nextReports;
            });
            setReportTextErrorsById((previousErrors) => {
                const nextErrors = { ...previousErrors };
                delete nextErrors[report.id];
                return nextErrors;
            });
            setReportTextSaveStatusById((previousStatuses) => {
                const nextStatuses = { ...previousStatuses };
                delete nextStatuses[report.id];
                return nextStatuses;
            });
        } catch (error) {
            console.error('CLIENT_RECORD_VIEW: handleDeleteReport(): Error deleting report:', error);
            window.alert(error instanceof Error ? error.message : 'Could not delete the report.');
        } finally {
            setDeletingReportIds((currentDeletingReportIds) => {
                const { [report.id]: _removedReportId, ...remainingDeletingReportIds } = currentDeletingReportIds;
                return remainingDeletingReportIds;
            });
        }
    }, [onDeleteSessionReport]);

    const detailsHeaderActions = isEditing ? (
        <div style={styles.actionButtons}>
            <Button
                onClick={onDiscardEditing}
                label='Discard'
                icon={require('./assets/icons/close_normal.svg').default}
                type='secondary'
            />
            <Button
                onClick={onSaveClient}
                label='Save'
                icon={require('./assets/icons/save_selected.svg').default}
                type='primary'
                disabled={!!getCustomPropertiesError(clientDataToRender.customProperties || [])}
            />
        </div>
    ) : (
        <div style={styles.actionButtons}>
            {showMyClientButton && (
                <Button
                    onClick={onAssignMyClient}
                    label='My client'
                    icon={require('./assets/icons/bookmark_normal.svg').default}
                    type='secondary'
                />
            )}
            <Button
                onClick={onStartEditing}
                label='Edit'
                icon={require('./assets/icons/edit_normal.svg').default}
                type='secondary'
            />
        </div>
    );
    const timelineHeaderActions = (
        <div style={styles.actionButtons}>
            {canGenerateClientReports && selectedReportTimelineItems.length > 0 && (
                <Button
                    onClick={handleOpenGenerateReportDialog}
                    label={isGeneratingClientReport ? 'Generating report...' : 'Generate report'}
                    type='secondary'
                    disabled={isGeneratingClientReport}
                />
            )}
            {canUseTimelineDocuments && (
                <>
                    <Button
                        onClick={() => void handleCreateTimelineClinicalNote()}
                        label={isCreatingTimelineNote ? 'Creating...' : 'New note'}
                        icon={require('./assets/icons/add_normal.svg').default}
                        type='secondary'
                        disabled={!timelineSubjectId || isCreatingTimelineNote}
                    />
                    {isTeamTimeline && <input
                        ref={uploadTimelineFileInputRef}
                        type='file'
                        accept={TIMELINE_FILE_ACCEPT}
                        style={styles.hiddenFileInput}
                        onChange={(event) => {
                            const file = event.currentTarget.files?.[0];
                            event.currentTarget.value = '';
                            void handleUploadTimelineFile(file);
                        }}
                    />}
                    <Button
                        onClick={() => isTeamTimeline ? uploadTimelineFileInputRef.current?.click() : setUploadDialogContext({ context: uploadContextKey, kind: 'document' })}
                        label={isUploadingTimelineFile ? 'Uploading...' : 'Upload file'}
                        icon={require('./assets/icons/file_upload_normal.svg').default}
                        type='secondary'
                        disabled={!timelineSubjectId || isUploadingTimelineFile}
                    />
                </>
            )}
            {/* Imported recordings remain Client-specific; live capture starts from the global header. */}
            {canGenerateClientReports && (
                <Button
                    onClick={() => setUploadDialogContext({ context: uploadContextKey, kind: 'media' })}
                    label='Import audio/video'
                    icon={require('./assets/icons/media_import_normal.svg').default}
                    type='secondary'
                    disabled={!selectedClient.id}
                />
            )}
        </div>
    );
    const reportsHeaderActions = (
        <div style={styles.actionButtons}>
            <button
                type='button'
                onClick={handleReportZoomOut}
                style={{
                    ...styles.reportZoomButton,
                    ...(reportTextZoom <= REPORT_TEXT_ZOOM_MIN ? styles.reportZoomButtonDisabled : {}),
                }}
                aria-label='Zoom out reports'
                title='Zoom out'
                disabled={reportTextZoom <= REPORT_TEXT_ZOOM_MIN}
            >
                {renderReportZoomIcon('out')}
            </button>
            <button
                type='button'
                onClick={handleReportZoomIn}
                style={{
                    ...styles.reportZoomButton,
                    ...(reportTextZoom >= REPORT_TEXT_ZOOM_MAX ? styles.reportZoomButtonDisabled : {}),
                }}
                aria-label='Zoom in reports'
                title='Zoom in'
                disabled={reportTextZoom >= REPORT_TEXT_ZOOM_MAX}
            >
                {renderReportZoomIcon('in')}
            </button>
        </div>
    );

    const headerActions = selectedTabId === 'record' ?
        detailsHeaderActions :
        (selectedTabId === 'timeline' ?
            timelineHeaderActions :
            (selectedTabId === 'reports' ? reportsHeaderActions :
                selectedTabId === 'content' ? <div ref={setContentHeaderActionContainer} style={styles.actionButtons} /> : null));

    // Keep the existing client record content inside the first tab.
    const recordTabContent = (
        <>
            <div style={styles.panelsLayout}>
                <div style={styles.topRow}>
                    <div style={styles.detailsColumn}>
                        <CollapsiblePanel
                            title='Personal details'
                            contentStyle={styles.clientDetailsContent}
                        >
                            <div style={styles.detailsList}>
                                <div style={styles.detailRow}>
                                    <label style={styles.detailLabel}>Given name</label>
                                    {isEditing ? (
                                        <input
                                            type='text'
                                            value={clientDataToRender.givenName || ''}
                                            onChange={(event) => onEditClientChange('givenName', event.target.value)}
                                            style={styles.detailInput}
                                        />
                                    ) : (
                                        <div style={styles.detailValue}>{clientDataToRender.givenName || ''}</div>
                                    )}
                                </div>

                                <div style={styles.detailRow}>
                                    <label style={styles.detailLabel}>Family name</label>
                                    {isEditing ? (
                                        <input
                                            type='text'
                                            value={clientDataToRender.familyName || ''}
                                            onChange={(event) => onEditClientChange('familyName', event.target.value)}
                                            style={styles.detailInput}
                                        />
                                    ) : (
                                        <div style={styles.detailValue}>{clientDataToRender.familyName || ''}</div>
                                    )}
                                </div>

                                {showSpeechNeeds && (
                                    <div style={styles.detailRow}>
                                        <label style={styles.detailLabel}>Support areas</label>
                                        {isEditing ? (
                                            <SpeechNeedsCheckboxList
                                                speechNeeds={speechNeeds}
                                                selectedSpeechNeedIds={selectedSpeechNeedIds}
                                                onToggleSpeechNeed={handleToggleSpeechNeed}
                                            />
                                        ) : (
                                            <div style={styles.detailValue}>{supportAreaLabel}</div>
                                        )}
                                    </div>
                                )}

                                <div style={styles.detailRow}>
                                    <label style={styles.detailLabel}>User type</label>
                                    {isEditing ? (
                                    <select
                                        value={clientDataToRender.userType}
                                        onChange={(event) => onEditClientChange('userType', event.target.value)}
                                        style={styles.detailSelect}
                                        >
                                            <option value='care_recipient'>Care recipient</option>
                                            <option value='carer'>Carer</option>
                                            <option value='therapist'>Therapist</option>
                                        </select>
                                    ) : (
                                        <div style={styles.detailValue}>
                                            <UserTypeTag userType={clientDataToRender.userType} />
                                        </div>
                                    )}
                                </div>

                                <div style={styles.detailRow}>
                                    <label style={styles.detailLabel}>Comms campaign</label>
                                    {isEditing ? (
                                        <label style={styles.checkboxLabel}>
                                            <input
                                                type='checkbox'
                                                checked={!!clientDataToRender.commsCampaignId}
                                                onChange={(event) => onCommsCampaignEnrollmentChange(event.target.checked)}
                                                style={styles.checkboxInput}
                                                disabled={!canEditCommsCampaign}
                                            />
                                            Enrolled
                                        </label>
                                    ) : (
                                        <div style={styles.detailValue}>{clientDataToRender.commsCampaignId ? 'YES' : 'NO'}</div>
                                    )}
                                </div>

                                <div style={styles.detailRow}>
                                    <label style={styles.detailLabel}>Therapist</label>
                                    {isEditing ? (
                                        <select
                                            value={clientDataToRender.therapistUserId || ''}
                                            onChange={(event) => onEditClientChange('therapistUserId', event.target.value)}
                                            style={styles.detailSelect}
                                        >
                                            <option value=''>-- No therapist selected --</option>
                                            {therapistOptions.map((therapist) => (
                                                <option key={therapist.id} value={therapist.id}>
                                                    {therapist.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div style={styles.detailValue}>{therapistName || 'Not assigned'}</div>
                                    )}
                                </div>

                                {(isEditing || !!clientDataToRender.dateOfBirth) && (
                                    <div style={styles.detailRow}>
                                        <label style={styles.detailLabel}>Date of birth</label>
                                        {isEditing ? (
                                            <input
                                                type='date'
                                                value={clientDataToRender.dateOfBirth || ''}
                                                onChange={(event) => onEditClientChange('dateOfBirth', event.target.value)}
                                                style={styles.detailInput}
                                            />
                                        ) : (
                                            <div style={styles.detailValue}>{clientDataToRender.dateOfBirth || ''}</div>
                                        )}
                                    </div>
                                )}

                                {(isEditing || !!clientDataToRender.phoneNumber) && (
                                    <div style={styles.detailRow}>
                                        <label style={styles.detailLabel}>Phone number</label>
                                        {isEditing ? (
                                            <PhoneNumberInput
                                                value={clientDataToRender.phoneNumber || ''}
                                                // Store edited numbers with the selected international dial code.
                                                onChange={(phoneNumber) => onEditClientChange('phoneNumber', phoneNumber)}
                                                containerStyle={styles.detailPhoneInputGroup}
                                            />
                                        ) : (
                                            <div style={styles.detailValue}>{clientDataToRender.phoneNumber || ''}</div>
                                        )}
                                    </div>
                                )}

                                {(isEditing || !!clientDataToRender.email) && (
                                    <div style={styles.detailRow}>
                                        <label style={styles.detailLabel}>Email address</label>
                                        {isEditing ? (
                                            <input
                                                type='email'
                                                value={clientDataToRender.email || ''}
                                                onChange={(event) => onEditClientChange('email', event.target.value)}
                                                style={styles.detailInput}
                                            />
                                        ) : (
                                            <div style={styles.detailValue}>{clientDataToRender.email || ''}</div>
                                        )}
                                    </div>
                                )}

                                {!!clientDataToRender.friendlyId && (
                                    <div style={styles.detailRow}>
                                        <div style={styles.detailLabel}>Friendly ID</div>
                                        <div style={styles.detailValue}>{clientDataToRender.friendlyId}</div>
                                    </div>
                                )}

                                {!!clientDocumentId && (
                                    <div style={styles.detailRow}>
                                        <div style={styles.detailLabel}>ID</div>
                                        <div style={styles.detailValue}>{clientDocumentId}</div>
                                    </div>
                                )}

                                {!!accountCreatedLabel && (
                                    <div style={styles.detailRow}>
                                        <div style={styles.detailLabel}>Account created</div>
                                        <div style={styles.detailValue}>{accountCreatedLabel}</div>
                                    </div>
                                )}

                                <div style={styles.detailRow}>
                                    <label style={styles.detailLabel}>Notes</label>
                                    {isEditing ? (
                                        <textarea
                                            value={clientDataToRender.notes || ''}
                                            onChange={(event) => onEditClientChange('notes', event.target.value)}
                                            style={styles.detailTextarea}
                                        />
                                    ) : (
                                        <div style={styles.detailValue}>{clientDataToRender.notes || ''}</div>
                                    )}
                                </div>
                            </div>
                        </CollapsiblePanel>
                        <ClientCustomProperties
                            properties={clientDataToRender.customProperties || []}
                            isEditing={isEditing}
                            onChange={onEditClientCustomPropertiesChange}
                        />
                    </div>

                    {(relatedCarers.length > 0 || careRecipients.length > 0) && (
                        <div style={styles.relationshipColumn}>
                            {relatedCarers.length > 0 && (
                                <CollapsiblePanel title='Carers'>
                                    <div style={styles.relatedList}>
                                        {relatedCarers.map((carer) => (
                                            <button
                                                key={carer.id || getClientDisplayName(carer)}
                                                type='button'
                                                onClick={() => onSelectClient(carer)}
                                                style={styles.relatedItemButton}
                                            >
                                                <div style={styles.relatedItemContent}>
                                                    <div style={styles.relatedName}>{getClientDisplayName(carer)}</div>
                                                    <div style={styles.relatedMeta}>{getClientRoleLabel(carer)}</div>
                                                </div>
                                                <span style={styles.relatedChevron} aria-hidden='true'>
                                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                                                        <polyline
                                                            points='9 6 15 12 9 18'
                                                            stroke='currentColor'
                                                            strokeWidth='2'
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                        />
                                                    </svg>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </CollapsiblePanel>
                            )}

                            {careRecipients.length > 0 && (
                                <CollapsiblePanel title='Care recipients'>
                                    <div style={styles.relatedList}>
                                        {careRecipients.map((careRecipient) => (
                                            <button
                                                key={careRecipient.id || getClientDisplayName(careRecipient)}
                                                type='button'
                                                onClick={() => onSelectClient(careRecipient)}
                                                style={styles.relatedItemButton}
                                            >
                                                <div style={styles.relatedItemContent}>
                                                    <div style={styles.relatedName}>{getClientDisplayName(careRecipient)}</div>
                                                </div>
                                                <span style={styles.relatedChevron} aria-hidden='true'>
                                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                                                        <polyline
                                                            points='9 6 15 12 9 18'
                                                            stroke='currentColor'
                                                            strokeWidth='2'
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                        />
                                                    </svg>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </CollapsiblePanel>
                            )}
                        </div>
                    )}
                </div>

                {showFamilySponsorship && canSetSpeechNeeds && (
                    <CollapsiblePanel title='Sponsored mobile access'>
                        <div style={styles.sponsorshipContent}>
                            <div style={styles.sponsorshipSummary}>
                                <strong>
                                    {isLoadingFamilySponsorship ? 'Checking sponsorship…' :
                                        familySponsorshipStatus?.sponsored ? 'Sponsored by this clinic' : 'Not sponsored'}
                                </strong>
                                <span style={styles.helperText}>
                                    Sponsorship covers this care recipient and all linked carers. Turning it off keeps carers signed in,
                                    but their next premium topic opens the existing personal-subscription paywall.
                                </span>
                                {!!familySponsorshipStatus?.activeCarerIds.length && (
                                    <span style={styles.helperText}>
                                        {familySponsorshipStatus.activeCarerIds.length} linked carer
                                        {familySponsorshipStatus.activeCarerIds.length === 1 ? '' : 's'} currently sponsored.
                                    </span>
                                )}
                                {relatedCarers.length === 0 && (
                                    <span style={styles.helperText}>Link at least one carer before enabling sponsorship.</span>
                                )}
                                {familySponsorshipError && (
                                    <span style={styles.sponsorshipError} role='alert'>{familySponsorshipError}</span>
                                )}
                            </div>
                            {canManageFamilySponsorship ? (
                                <Button
                                    onClick={() => { void onSetFamilySponsorship(!familySponsorshipStatus?.sponsored); }}
                                    label={familySponsorshipStatus?.sponsored ? 'Remove sponsorship' : 'Sponsor mobile access'}
                                    type={familySponsorshipStatus?.sponsored ? 'secondary' : 'primary'}
                                    disabled={isLoadingFamilySponsorship || (!familySponsorshipStatus?.sponsored && relatedCarers.length === 0)}
                                />
                            ) : (
                                <span style={styles.helperText}>Only an Admin can change sponsored mobile access.</span>
                            )}
                        </div>
                    </CollapsiblePanel>
                )}

                <div style={styles.fullWidthPanels}>
                    {isCarer && (
                        <CollapsiblePanel title='Engagement'>
                            <div style={styles.engagementContent}>
                                <FunnelWidget currentStage={currentStage} width={860} height={52} />
                                {/* Keep both engagement trends visible together for quick comparison. */}
                                <div style={styles.engagementChartsRow}>
                                    <div style={styles.engagementChartColumn}>
                                        <EngagementChart
                                            activityPoints={sessionMinutesPoints}
                                            allTimeStartDate={engagementStartDate}
                                            title='Time in the app'
                                            totalUnitLabel='minutes'
                                        />
                                    </div>
                                    <div style={styles.engagementChartColumn}>
                                        <EngagementChart
                                            activityPoints={topicsCompletedPoints}
                                            allTimeStartDate={engagementStartDate}
                                        />
                                    </div>
                                </div>
                            </div>
                        </CollapsiblePanel>
                    )}

                    {isCarer && (
                        <CollapsiblePanel title='Assessments'>
                            <div style={styles.assessmentsContent}>
                                {isLoadingSelectedClientAssessments ? (
                                    <div style={styles.helperText}>Loading assessments...</div>
                                ) : assessmentSections.length === 0 ? (
                                    <div style={styles.helperText}>No completed assessments yet.</div>
                                ) : (
                                    assessmentSections.map((section) => (
                                        <AssessmentSectionView key={section.definition.id} section={section} />
                                    ))
                                )}
                            </div>
                        </CollapsiblePanel>
                    )}

                    {canUseClientBriefing && (
                        <CollapsiblePanel title='Summary'>
                            <ClientBriefingPanel
                                state={clientBriefing.state}
                                canViewBriefingDiagnostics={props.canViewBriefingDiagnostics}
                                requestPending={clientBriefing.requestPending}
                                requestError={clientBriefing.requestError}
                                requestedStatus={clientBriefing.requestedStatus}
                                onRefresh={props.onRequestClientBriefing ? clientBriefing.refresh : undefined}
                            />
                        </CollapsiblePanel>
                    )}
                </div>
            </div>

            {onDeleteClient && (
                <div style={styles.deleteSection}>
                    <Button
                        onClick={() => {
                            if (window.confirm('Are you sure you want to delete this client?')) {
                                onDeleteClient(selectedClient);
                                onBack();
                            }
                        }}
                        label='Delete client'
                        type='secondary'
                        style={styles.deleteButton}
                    />
                </div>
            )}
        </>
    );

    // Render each transcript as speaker-coloured paragraphs under the session date.
    const renderSessionTranscript = (
        session: TherapySession,
        utterances: Array<TherapySessionTranscriptUtterance>
    ) => {
        if (utterances.length === 0) {
            const isRetryingTranscript = !!retryingTranscriptIds[session.id];
            const retryError = transcriptRetryErrorsById[session.id];
            return (
                <div style={styles.transcriptPlaceholderContainer}>
                    <p style={styles.sessionPlaceholderText}>
                        {isRetryingTranscript ? 'Transcript processing...' : getTranscriptPlaceholder(session)}
                    </p>
                    {session.source !== 'import' && session.transcriptStatus === 'error' && onRetrySessionTranscription && (
                        <button
                            type='button'
                            onClick={() => void handleRetrySessionTranscription(session.id)}
                            style={{
                                ...styles.retryTranscriptButton,
                                ...(isRetryingTranscript ? styles.retryTranscriptButtonDisabled : {}),
                            }}
                            disabled={isRetryingTranscript}
                        >
                            {isRetryingTranscript ? 'Retrying...' : 'Retry transcription'}
                        </button>
                    )}
                    {!!retryError && (
                        <p style={styles.transcriptRetryError}>{retryError}</p>
                    )}
                </div>
            );
        }

        return utterances.map((utterance, utteranceIndex) => {
            const speakerKind = getUtteranceSpeakerKind(utterance);
            const isActiveUtterance = isUtteranceActiveAtTime(
                utterance,
                activeSessionPlaybackMsById[session.id]
            );
            const paragraphStyle = {
                ...styles.utteranceParagraph,
                ...(speakerKind === 'therapist' ? styles.therapistUtterance : {}),
                ...(speakerKind === 'client' ? styles.clientUtterance : {}),
                ...(isActiveUtterance ? styles.activeUtterance : {}),
            };
            const speakerStyle = {
                ...styles.utteranceSpeakerLabel,
                ...(speakerKind === 'therapist' ? styles.therapistSpeakerLabel : {}),
                ...(speakerKind === 'client' ? styles.clientSpeakerLabel : {}),
            };
            const utteranceKey = getUtteranceRenderKey(session.id, utterance, utteranceIndex);
            const isTranscriptOptionsOpen = openTranscriptOptionsMenuKey === utteranceKey;
            const activeAssignmentScope = openTranscriptAssignmentSubmenu?.utteranceKey === utteranceKey ?
                openTranscriptAssignmentSubmenu.scope :
                null;
            const areTranscriptOptionsVisible = hoveredTranscriptOptionsKey === utteranceKey || isTranscriptOptionsOpen;
            const transcriptOptionsButtonStyle = {
                ...styles.utteranceOptionsButton,
                ...(areTranscriptOptionsVisible ? styles.utteranceOptionsButtonVisible : {}),
            };
            const assignAllMenuItemStyle = {
                ...styles.utteranceOptionsMenuItemWithSubmenu,
                ...(activeAssignmentScope === 'speaker' ? styles.utteranceOptionsMenuItemHighlighted : {}),
            };
            const assignLineMenuItemStyle = {
                ...styles.utteranceOptionsMenuItemWithSubmenu,
                ...(activeAssignmentScope === 'line' ? styles.utteranceOptionsMenuItemHighlighted : {}),
            };
            const speakerLabel = formatUtteranceSpeakerLabel(utterance);

            // Render each people submenu with a fixed scope so line clicks cannot become all-line clicks.
            const renderPeopleAssignmentMenu = (scope: TranscriptAssignmentScope, top: string) => {
                if (activeAssignmentScope !== scope) {
                    return null;
                }

                const peopleMenuStyle = {
                    ...styles.utterancePeopleMenu,
                    top,
                };

                return (
                    <div style={peopleMenuStyle} role='menu'>
                        {transcriptAssignablePeople.map((person) => {
                            const personMenuKey = `${utteranceKey}:${scope}:${person.key}`;
                            const isCurrentAssignment = utterance.speakerAssignment?.personId === person.personId &&
                                utterance.speakerAssignment?.personType === person.personType &&
                                utterance.speakerAssignment?.transcriptRole === person.transcriptRole;
                            const personMenuItemStyle = {
                                ...styles.utterancePersonMenuItem,
                                ...((hoveredTranscriptPersonMenuKey === personMenuKey || isCurrentAssignment) ?
                                    styles.utteranceOptionsMenuItemHighlighted :
                                    {}),
                            };

                            return (
                                <button
                                    key={person.key}
                                    type='button'
                                    onMouseEnter={() => setHoveredTranscriptPersonMenuKey(personMenuKey)}
                                    onMouseLeave={() => setHoveredTranscriptPersonMenuKey(null)}
                                    onFocus={() => setHoveredTranscriptPersonMenuKey(personMenuKey)}
                                    onBlur={() => setHoveredTranscriptPersonMenuKey(null)}
                                    onClick={() => handleTranscriptAssignmentMenuClick(
                                        session,
                                        utterances,
                                        utteranceIndex,
                                        scope,
                                        person
                                    )}
                                    style={personMenuItemStyle}
                                    role='menuitem'
                                >
                                    <span>{person.displayName}</span>
                                    <span style={styles.utterancePersonMeta}>
                                        {person.menuLabel || (person.personType === 'other' ? 'Other' : getUserTypeLabel(person.userType || person.personType))}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                );
            };

            return (
                <div
                    key={utteranceKey}
                    onMouseEnter={() => setHoveredTranscriptOptionsKey(utteranceKey)}
                    onMouseLeave={() => setHoveredTranscriptOptionsKey(null)}
                    ref={(element) => {
                        if (!transcriptUtteranceRefs.current[session.id]) {
                            transcriptUtteranceRefs.current[session.id] = {};
                        }

                        if (element) {
                            transcriptUtteranceRefs.current[session.id][utteranceKey] = element;
                        } else {
                            delete transcriptUtteranceRefs.current[session.id][utteranceKey];
                        }
                    }}
                    style={paragraphStyle}
                >
                    <span style={speakerStyle}>{speakerLabel}</span>
                    <span style={styles.utteranceText}>{utterance.text}</span>
                    <div style={styles.utteranceOptionsContainer}>
                        <button
                            type='button'
                            onClick={() => {
                                setOpenSessionOptionsMenuId(null);
                                setOpenTranscriptAssignmentSubmenu(null);
                                setHoveredTranscriptPersonMenuKey(null);
                                setOpenTranscriptOptionsMenuKey((openMenuKey) =>
                                    openMenuKey === utteranceKey ? null : utteranceKey
                                );
                            }}
                            onFocus={() => setHoveredTranscriptOptionsKey(utteranceKey)}
                            onBlur={() => setHoveredTranscriptOptionsKey(null)}
                            style={transcriptOptionsButtonStyle}
                            aria-label='Transcript line options'
                            aria-haspopup='menu'
                            aria-expanded={isTranscriptOptionsOpen}
                            tabIndex={areTranscriptOptionsVisible ? 0 : -1}
                        >
                            <span style={styles.utteranceOptionsEllipsis}>…</span>
                        </button>
                        {isTranscriptOptionsOpen && (
                            <div style={styles.utteranceOptionsMenu} role='menu'>
                                <button
                                    type='button'
                                    onClick={() => setOpenTranscriptAssignmentSubmenu({ utteranceKey, scope: 'line' })}
                                    onFocus={() => setOpenTranscriptAssignmentSubmenu({ utteranceKey, scope: 'line' })}
                                    style={assignLineMenuItemStyle}
                                    role='menuitem'
                                    aria-haspopup='menu'
                                    aria-expanded={activeAssignmentScope === 'line'}
                                >
                                    <span>Assign this line to person</span>
                                    <span style={styles.utteranceSubmenuChevron}>›</span>
                                </button>
                                {renderPeopleAssignmentMenu('line', '6px')}
                                <button
                                    type='button'
                                    onClick={() => setOpenTranscriptAssignmentSubmenu({ utteranceKey, scope: 'speaker' })}
                                    onFocus={() => setOpenTranscriptAssignmentSubmenu({ utteranceKey, scope: 'speaker' })}
                                    style={assignAllMenuItemStyle}
                                    role='menuitem'
                                    aria-haspopup='menu'
                                    aria-expanded={activeAssignmentScope === 'speaker'}
                                >
                                    <span>{`Assign all the lines from ${speakerLabel} to person`}</span>
                                    <span style={styles.utteranceSubmenuChevron}>›</span>
                                </button>
                                {renderPeopleAssignmentMenu('speaker', '42px')}
                            </div>
                        )}
                    </div>
                </div>
            );
        });
    };

    const renderAudioSessionTimelineItem = (item: Extract<TimelineItem, { kind: 'session' }>) => {
        // Render existing audio sessions through the timeline handler without changing session behaviour.
        const { session } = item;
        const isNewImport = uploadedTimelineFile?.kind === 'media' && uploadedTimelineFile.id === session.id;
        const importIsProcessing = session.transcriptStatus === 'pending' || session.transcriptStatus === 'processing' || !!retryingTranscriptIds[session.id];
        const importFailed = session.transcriptStatus === 'error' && !importIsProcessing;
        const utterances = getSessionUtterances(session);
        const isTranscriptExpanded = expandedTranscriptIds.has(session.id);
        const savedClinicalNotes = session.clinicalNotes || '';
        const clinicalNotesDraft = sessionNotesById[session.id] ?? savedClinicalNotes;
        const isSavingClinicalNotes = !!savingSessionNotesById[session.id];
        const hasClinicalNotesChanges = clinicalNotesDraft !== savedClinicalNotes;
        const sessionNotesError = sessionNotesErrorsById[session.id];
        const sessionNotesStatus = sessionNotesSaveStatusById[session.id];
        const isDeletingSession = !!deletingSessionIds[session.id];
        const sessionTitle = getSessionTitle(session);
        const sessionDate = getSessionDate(session);
        const isSessionCollapsed = collapsedSessionIds.has(session.id);
        const isSessionSelectedForReport = selectedReportTimelineItemKeys.has(item.key);
        const sessionHeaderStyle = {
            ...styles.sessionTranscriptHeader,
            ...(isSessionCollapsed ? styles.sessionTranscriptHeaderCollapsed : {}),
        };
        const sessionNotesStatusLabel = sessionNotesError ?
            'Could not save' :
            isSavingClinicalNotes || sessionNotesStatus === 'saving' ?
                'Saving...' :
                sessionNotesStatus === 'pending' || hasClinicalNotesChanges ?
                    'Unsaved changes' :
                    sessionNotesStatus === 'saved' ?
                        'Saved' :
                        '';

        return (
            <section key={item.key} ref={isNewImport ? uploadedTimelineFileRef : undefined}
                style={{ ...styles.sessionTranscriptItem, ...(isNewImport ? { outline: '2px solid #817ee4', outlineOffset: '2px' } : {}) }}>
                <div style={sessionHeaderStyle}>
                    <div style={styles.sessionTitleGroup}>
                        {canGenerateClientReports && (
                            <input
                                type='checkbox'
                                checked={isSessionSelectedForReport}
                                onChange={(event) => handleToggleReportTimelineItemSelection(item.key, event.currentTarget.checked)}
                                style={styles.sessionSelectCheckbox}
                                aria-label={`Select ${sessionTitle} for report`}
                            />
                        )}
                        {renderTimelineTypeIcon('session')}
                        <h2 style={styles.sessionDateTitle}>
                            <InlineEditableTimelineField
                                displayValue={formatSessionDate(session) || 'Session date unavailable'}
                                editValue={formatDateTimeLocalValue(sessionDate)}
                                inputType='datetime-local'
                                inputStep={60}
                                ariaLabel={`Session date for ${sessionTitle}`}
                                displayStyle={styles.sessionDateButton}
                                inputStyle={styles.sessionDateInput}
                                onSave={(draft) => handleSaveSessionDateDraft(session, draft)}
                                saveErrorMessage='Could not save the session date.'
                            />
                        </h2>
                        <span style={styles.sessionTitleSeparator}>{' – '}</span>
                        <InlineEditableTimelineField
                            displayValue={sessionTitle}
                            editValue={sessionTitle}
                            ariaLabel={`Session title for ${sessionTitle}`}
                            displayStyle={styles.sessionTitleButton}
                            inputStyle={styles.sessionTitleInput}
                            onSave={(draft) => handleSaveSessionTitleDraft(session, draft)}
                            saveErrorMessage='Could not save the session title.'
                        />
                    </div>
                    <div style={styles.sessionHeaderActions}>
                        <div style={styles.sessionOptionsContainer}>
                            <button
                                type='button'
                                onClick={() => {
                                    setOpenTranscriptOptionsMenuKey(null);
                                    setOpenTranscriptAssignmentSubmenu(null);
                                    setOpenSessionOptionsMenuId((openMenuId) =>
                                        openMenuId === session.id ? null : session.id
                                    );
                                }}
                                style={styles.sessionOptionsButton}
                                aria-label='Session options'
                                aria-haspopup='menu'
                                aria-expanded={openSessionOptionsMenuId === session.id}
                                disabled={isDeletingSession}
                            >
                                <span style={styles.sessionOptionsEllipsis}>…</span>
                            </button>
                            {openSessionOptionsMenuId === session.id && (
                                <div style={styles.sessionOptionsMenu} role='menu'>
                                    <button
                                        type='button'
                                        onClick={() => void handleDeleteSession(session)}
                                        style={styles.sessionOptionsMenuItem}
                                        role='menuitem'
                                        disabled={isDeletingSession}
                                    >
                                        {isDeletingSession ? 'Deleting...' : 'Delete session'}
                                    </button>
                                </div>
                            )}
                        </div>
                        <button
                            type='button'
                            onClick={() => handleToggleSessionCollapsed(session.id)}
                            style={styles.sessionCollapseButton}
                            aria-label={isSessionCollapsed ? 'Expand session' : 'Collapse session'}
                            aria-expanded={!isSessionCollapsed}
                            title={isSessionCollapsed ? 'Expand session' : 'Collapse session'}
                        >
                            <span
                                style={{
                                    ...styles.sessionCollapseIcon,
                                    transform: isSessionCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                                }}
                            >
                                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                                    <polyline
                                        points='6 9 12 15 18 9'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
                {session.source === 'import' && (
                    <div className='timeline-import-details'>
                        {session.originalFileName && <span className='timeline-import-filename'>{session.originalFileName}</span>}
                        {importIsProcessing && <span className='timeline-import-status' role='status'>Processing recording…</span>}
                        {importFailed && <>
                            <span className='timeline-import-status is-error'>Processing failed</span>
                            {(session.storagePath || session.importMediaStoragePath) && onRetrySessionTranscription ? (
                                <button type='button' style={styles.retryTranscriptButton}
                                    onClick={() => void handleRetrySessionTranscription(session.id)}>Retry processing</button>
                            ) : <span className='timeline-import-error'>The source file is unavailable. Import it again to retry.</span>}
                        </>}
                        {session.transcriptStatus === 'skipped' && <span className='timeline-import-error'>Transcript unavailable{session.transcriptError ? ': ' + session.transcriptError : '.'}</span>}
                        {transcriptRetryErrorsById[session.id] && <span className='timeline-import-error' role='alert'>{transcriptRetryErrorsById[session.id]}</span>}
                    </div>
                )}
                {!isSessionCollapsed && (
                    <>
                        <SessionAudioPlayer
                            session={session}
                            utterances={utterances}
                            availableTags={sortSessionTagsByMostRecent(currentTherapist?.sessionTags || [])}
                            onCreateTag={handleCreateSessionTag}
                            onPlaceTag={handlePlaceSessionTag}
                            onRemovePlacedTag={handleRemoveSessionPlacedTag}
                            onPlaybackTimeChange={handleSessionPlaybackTimeChange}
                            onEnsureWaveform={onEnsureSessionWaveform}
                            seekRequest={sessionSeekRequestsById[session.id]}
                        />
                        <div style={styles.sessionNotesSection}>
                            <div style={styles.sessionNotesHeader}>
                                <label htmlFor={`session-notes-${session.id}`} style={styles.sessionNotesLabel}>
                                    Clinical notes
                                </label>
                                {!!sessionNotesStatusLabel && (
                                    <span style={{
                                        ...styles.sessionNotesStatus,
                                        ...(sessionNotesError ? styles.sessionNotesStatusError : {}),
                                    }}>
                                        {sessionNotesStatusLabel}
                                    </span>
                                )}
                            </div>
                            <ClinicalNotesEditor
                                value={clinicalNotesDraft}
                                onChange={(nextClinicalNotes) => handleSessionNotesChange(
                                    session.id,
                                    nextClinicalNotes,
                                    savedClinicalNotes
                                )}
                                onBlur={(nextClinicalNotes) => handleSessionNotesBlur(session.id, nextClinicalNotes, savedClinicalNotes)}
                                markerTimestampMs={activeSessionPlaybackMsById[session.id] || 0}
                                onMarkerClick={(timestampMs) => handleSessionNotesMarkerClick(session.id, timestampMs)}
                                placeholder='Add clinical notes for this session.'
                            />
                            {!!sessionNotesError && (
                                <div style={styles.sessionNotesError}>{sessionNotesError}</div>
                            )}
                        </div>
                        <div style={styles.transcriptToggleRow}>
                            <button
                                type='button'
                                onClick={() => handleToggleTranscript(session.id)}
                                style={styles.transcriptToggleButton}
                                aria-expanded={isTranscriptExpanded}
                            >
                                <span style={styles.transcriptToggleChevron}>
                                    {isTranscriptExpanded ? '▾' : '▸'}
                                </span>
                                <span>{isTranscriptExpanded ? 'Hide transcript' : 'Show transcript'}</span>
                                {utterances.length > 0 && (
                                    <span style={styles.transcriptTurnCount}>{utterances.length} turns</span>
                                )}
                            </button>
                        </div>
                        {isTranscriptExpanded && (
                            <div
                                ref={(element) => {
                                    transcriptBodyRefs.current[session.id] = element;
                                }}
                                style={styles.sessionTranscriptBody}
                            >
                                {renderSessionTranscript(session, utterances)}
                            </div>
                        )}
                    </>
                )}
            </section>
        );
    };

    const renderClinicalNoteTimelineItem = (item: Extract<TimelineItem, { kind: 'document' }>) => {
        // Render standalone clinical notes as editable autosaving timeline documents.
        const { document } = item;
        const savedClinicalNotes = document.clinicalNotes || '';
        const clinicalNotesDraft = timelineNotesById[document.id] ?? savedClinicalNotes;
        const isSavingClinicalNotes = !!savingTimelineNotesById[document.id];
        const hasClinicalNotesChanges = clinicalNotesDraft !== savedClinicalNotes;
        const notesError = timelineNotesErrorsById[document.id];
        const notesStatus = timelineNotesSaveStatusById[document.id];
        const isDeletingDocument = !!deletingTimelineDocumentIds[document.id];
        const isDocumentCollapsed = collapsedTimelineDocumentIds.has(document.id);
        const title = getTimelineDocumentTitle(document);
        const documentDate = getTimelineDocumentDate(document);
        const menuId = `document:${document.id}`;
        const documentHeaderStyle = {
            ...styles.sessionTranscriptHeader,
            ...(isDocumentCollapsed ? styles.sessionTranscriptHeaderCollapsed : {}),
        };
        const notesStatusLabel = notesError ?
            'Could not save' :
            isSavingClinicalNotes || notesStatus === 'saving' ?
                'Saving...' :
                notesStatus === 'pending' || hasClinicalNotesChanges ?
                    'Unsaved changes' :
                    notesStatus === 'saved' ?
                        'Saved' :
                        '';

        return (
            <section key={item.key} style={styles.sessionTranscriptItem}>
                <div style={documentHeaderStyle}>
                    <div style={styles.sessionTitleGroup}>
                        {canGenerateClientReports && (
                            <input
                                type='checkbox'
                                checked={selectedReportTimelineItemKeys.has(item.key)}
                                onChange={(event) => handleToggleReportTimelineItemSelection(item.key, event.currentTarget.checked)}
                                style={styles.sessionSelectCheckbox}
                                aria-label={`Select ${title} for report`}
                            />
                        )}
                        {renderTimelineTypeIcon('clinical_note')}
                        <h2 style={styles.sessionDateTitle}>
                            <InlineEditableTimelineField
                                displayValue={formatTimelineDocumentDate(document) || 'Document date unavailable'}
                                editValue={formatDateTimeLocalValue(documentDate)}
                                inputType='datetime-local'
                                inputStep={60}
                                ariaLabel={`Clinical note date for ${title}`}
                                displayStyle={styles.sessionDateButton}
                                inputStyle={styles.sessionDateInput}
                                onSave={(draft) => handleSaveTimelineDocumentDateDraft(document, draft)}
                                saveErrorMessage='Could not save the clinical note date.'
                            />
                        </h2>
                        <span style={styles.sessionTitleSeparator}>{' – '}</span>
                        <InlineEditableTimelineField
                            displayValue={title}
                            editValue={title}
                            ariaLabel={`Clinical note title for ${title}`}
                            displayStyle={styles.sessionTitleButton}
                            inputStyle={styles.sessionTitleInput}
                            onSave={(draft) => handleSaveTimelineDocumentTitleDraft(document, draft)}
                            saveErrorMessage='Could not save the clinical note title.'
                        />
                    </div>
                    <div style={styles.sessionHeaderActions}>
                        <div style={styles.sessionOptionsContainer}>
                            <button
                                type='button'
                                onClick={() => setOpenSessionOptionsMenuId((openMenuId) =>
                                    openMenuId === menuId ? null : menuId
                                )}
                                style={styles.sessionOptionsButton}
                                aria-label='Timeline note options'
                                aria-haspopup='menu'
                                aria-expanded={openSessionOptionsMenuId === menuId}
                                disabled={isDeletingDocument}
                            >
                                <span style={styles.sessionOptionsEllipsis}>…</span>
                            </button>
                            {openSessionOptionsMenuId === menuId && (
                                <div style={styles.sessionOptionsMenu} role='menu'>
                                    <button
                                        type='button'
                                        onClick={() => void handleDeleteTimelineDocument(document)}
                                        style={styles.sessionOptionsMenuItem}
                                        role='menuitem'
                                        disabled={isDeletingDocument}
                                    >
                                        {isDeletingDocument ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            )}
                        </div>
                        <button
                            type='button'
                            onClick={() => handleToggleTimelineDocumentCollapsed(document.id)}
                            style={styles.sessionCollapseButton}
                            aria-label={isDocumentCollapsed ? 'Expand clinical note' : 'Collapse clinical note'}
                            aria-expanded={!isDocumentCollapsed}
                            title={isDocumentCollapsed ? 'Expand clinical note' : 'Collapse clinical note'}
                        >
                            <span
                                style={{
                                    ...styles.sessionCollapseIcon,
                                    transform: isDocumentCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                                }}
                            >
                                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                                    <polyline
                                        points='6 9 12 15 18 9'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
                {!isDocumentCollapsed && (
                    <div style={styles.timelineClinicalNoteBody}>
                        {!!notesStatusLabel && (
                            <div style={styles.timelineClinicalNoteStatusRow}>
                                <span style={{
                                    ...styles.sessionNotesStatus,
                                    ...(notesError ? styles.sessionNotesStatusError : {}),
                                }}>
                                    {notesStatusLabel}
                                </span>
                            </div>
                        )}
                        <ClinicalNotesEditor
                            value={clinicalNotesDraft}
                            onChange={(nextClinicalNotes) => handleTimelineNotesChange(
                                document.id,
                                nextClinicalNotes,
                                savedClinicalNotes
                            )}
                            onBlur={(nextClinicalNotes) => handleTimelineNotesBlur(document.id, nextClinicalNotes, savedClinicalNotes)}
                            markerTimestampMs={0}
                            placeholder={isTeamTimeline ? 'Add notes for this team member.' : 'Add standalone clinical notes for this client.'}
                            showMarkerControls={false}
                        />
                        {!!notesError && (
                            <div style={styles.sessionNotesError}>{notesError}</div>
                        )}
                    </div>
                )}
            </section>
        );
    };

    const renderUploadedFileTimelineItem = (item: Extract<TimelineItem, { kind: 'document' }>) => {
        // Render uploaded client files with preview where the reusable file viewer supports it.
        const { document } = item;
        const title = getTimelineDocumentTitle(document);
        const menuId = `document:${document.id}`;
        const isDeletingDocument = !!deletingTimelineDocumentIds[document.id];
        const isDocumentCollapsed = collapsedTimelineDocumentIds.has(document.id);
        const documentDate = getTimelineDocumentDate(document);
        const fileUrl = timelineFileUrlsById[document.id];
        const fileError = timelineFileUrlErrorsById[document.id];
        const documentHeaderStyle = {
            ...styles.sessionTranscriptHeader,
            ...(isDocumentCollapsed ? styles.sessionTranscriptHeaderCollapsed : {}),
        };

        return (
            <section key={item.key} aria-label={'Uploaded file: ' + title}
                ref={uploadedTimelineFile?.kind === 'document' && uploadedTimelineFile.id === document.id ? uploadedTimelineFileRef : undefined}
                style={{ ...styles.sessionTranscriptItem, ...(uploadedTimelineFile?.kind === 'document' && uploadedTimelineFile.id === document.id ? { outline: '2px solid #817ee4', outlineOffset: '2px' } : {}) }}>
                <div style={documentHeaderStyle}>
                    <div style={styles.sessionTitleGroup}>
                        {canGenerateClientReports && (
                            <input
                                type='checkbox'
                                checked={selectedReportTimelineItemKeys.has(item.key)}
                                onChange={(event) => handleToggleReportTimelineItemSelection(item.key, event.currentTarget.checked)}
                                style={styles.sessionSelectCheckbox}
                                aria-label={`Select ${title} for report`}
                            />
                        )}
                        {renderTimelineTypeIcon('file')}
                        <h2 style={styles.sessionDateTitle}>
                            <InlineEditableTimelineField
                                displayValue={formatTimelineDocumentDate(document) || 'Document date unavailable'}
                                editValue={formatDateTimeLocalValue(documentDate)}
                                inputType='datetime-local'
                                inputStep={60}
                                ariaLabel={`Uploaded file date for ${title}`}
                                displayStyle={styles.sessionDateButton}
                                inputStyle={styles.sessionDateInput}
                                onSave={(draft) => handleSaveTimelineDocumentDateDraft(document, draft)}
                                saveErrorMessage='Could not save the uploaded file date.'
                            />
                        </h2>
                        <span style={styles.sessionTitleSeparator}>{' – '}</span>
                        <InlineEditableTimelineField
                            displayValue={title}
                            editValue={title}
                            ariaLabel={`Uploaded file title for ${title}`}
                            displayStyle={styles.sessionTitleButton}
                            inputStyle={styles.sessionTitleInput}
                            onSave={(draft) => handleSaveTimelineDocumentTitleDraft(document, draft)}
                            saveErrorMessage='Could not save the uploaded file title.'
                        />
                    </div>
                    <div style={styles.sessionHeaderActions}>
                        <div style={styles.sessionOptionsContainer}>
                            <button
                                type='button'
                                onClick={() => setOpenSessionOptionsMenuId((openMenuId) =>
                                    openMenuId === menuId ? null : menuId
                                )}
                                style={styles.sessionOptionsButton}
                                aria-label='Timeline file options'
                                aria-haspopup='menu'
                                aria-expanded={openSessionOptionsMenuId === menuId}
                                disabled={isDeletingDocument}
                            >
                                <span style={styles.sessionOptionsEllipsis}>…</span>
                            </button>
                            {openSessionOptionsMenuId === menuId && (
                                <div style={styles.sessionOptionsMenu} role='menu'>
                                    <button
                                        type='button'
                                        onClick={() => handleDownloadTimelineFile(document)}
                                        style={{
                                            ...styles.sessionOptionsMenuItem,
                                            ...styles.sessionOptionsMenuItemNeutral,
                                        }}
                                        role='menuitem'
                                        disabled={isDeletingDocument || !document.storagePath}
                                    >
                                        Download
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => void handleDeleteTimelineDocument(document)}
                                        style={styles.sessionOptionsMenuItem}
                                        role='menuitem'
                                        disabled={isDeletingDocument}
                                    >
                                        {isDeletingDocument ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            )}
                        </div>
                        <button
                            type='button'
                            onClick={() => handleToggleTimelineDocumentCollapsed(document.id)}
                            style={styles.sessionCollapseButton}
                            aria-label={isDocumentCollapsed ? 'Expand uploaded file' : 'Collapse uploaded file'}
                            aria-expanded={!isDocumentCollapsed}
                            title={isDocumentCollapsed ? 'Expand uploaded file' : 'Collapse uploaded file'}
                        >
                            <span
                                style={{
                                    ...styles.sessionCollapseIcon,
                                    transform: isDocumentCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                                }}
                            >
                                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                                    <polyline
                                        points='6 9 12 15 18 9'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
                {!isDocumentCollapsed && (
                    <>
                        <div style={styles.timelineFileMeta}>
                            <span>{document.originalName || 'Uploaded file'}</span>
                            <span>{document.contentType || 'Unknown type'}</span>
                            <span>{formatFileSize(document.sizeBytes)}</span>
                        </div>
                        <div style={styles.timelineFileViewer}>
                            {fileUrl ? (
                                <FileViewer
                                    file={{
                                        url: fileUrl,
                                        name: document.originalName || title,
                                        contentType: document.contentType,
                                    }}
                                    height={320}
                                />
                            ) : fileError ? (
                                <div style={styles.sessionNotesError}>{fileError}</div>
                            ) : (
                                <div style={styles.helperText}>Loading file preview...</div>
                            )}
                        </div>
                        {canGenerateClientReports && selectedClient.id && props.onGetReportAttachmentContent && props.onSaveReportAttachmentContent && (
                            <ReportAttachmentContentPanel
                                key={JSON.stringify([authenticatedUserId, timelineProviderId, selectedClient.id, document.id, document.storagePath, document.sizeBytes])}
                                clientId={selectedClient.id}
                                documentId={document.id}
                                title={title}
                                onGetContent={props.onGetReportAttachmentContent}
                                onSaveContent={props.onSaveReportAttachmentContent}
                            />
                        )}
                    </>
                )}
            </section>
        );
    };

    const renderUnknownTimelineItem = (item: TimelineItem) => {
        // Provide a safe fallback so future timeline kinds do not break the client record page.
        return (
            <section key={item.key} style={styles.sessionTranscriptItem}>
                <div style={styles.sessionTranscriptHeader}>
                    <div style={styles.sessionTitleGroup}>
                        <h2 style={styles.sessionDateTitle}>Unsupported timeline item</h2>
                    </div>
                </div>
                <div style={styles.helperText}>This timeline document type is not supported yet.</div>
            </section>
        );
    };

    const timelineRenderContext: TimelineItemRenderContext = {
        renderAudioSession: renderAudioSessionTimelineItem,
        renderClinicalNote: renderClinicalNoteTimelineItem,
        renderUploadedFile: renderUploadedFileTimelineItem,
        renderUnknown: renderUnknownTimelineItem,
    };
    const renderTimelineItem = (item: TimelineItem) => {
        const handler = TIMELINE_ITEM_HANDLERS.find((candidateHandler) => candidateHandler.canRender(item));
        return handler ? handler.render(item, timelineRenderContext) : timelineRenderContext.renderUnknown(item);
    };

    const timelineTabContent = (
        <div style={styles.sessionsList}>
            {uploadedTimelineFile?.context === uploadContextKey && (
                <div role='status' style={{ padding: '12px 16px', borderRadius: '8px', background: '#edf7ef', color: '#245d32' }}>
                    {uploadedTimelineFile.kind === 'media' ? 'File uploaded. Your transcript is being prepared.' : uploadedTimelineFile.name + ' added to the timeline.'}
                </div>
            )}
            {canGenerateClientReports && !!clientReportGenerationError && (
                <div style={styles.reportGenerationError}>{clientReportGenerationError}</div>
            )}
            {timelineItems.length === 0 ? (
                <div style={styles.emptySessionsMessage}>No timeline items yet.</div>
            ) : (
                timelineItems.map(renderTimelineItem)
            )}
        </div>
    );

    // Show generated client-level reports in most-recent-first order.
    const reportsTabContent = (
        <div style={styles.reportsList}>
            {clientReports.length === 0 ? (
                <div style={styles.emptySessionsMessage}>No reports generated yet.</div>
            ) : (
                clientReports.map((report, reportIndex) => {
                    const savedReportText = getReportEditorText(report);
                    const reportTextDraft = reportTextById[report.id] ?? savedReportText;
                    const reportHasText = !!getReportText(report);
                    const reportTitle = `${formatReportDate(report) || 'Report date unavailable'} - ${getReportTitle(report)}`;
                    const reportError = report.error || 'Could not generate this report.';
                    const isDeletingReport = !!deletingReportIds[report.id];
                    const isSavingReportText = !!savingReportTextById[report.id];
                    const reportTextError = reportTextErrorsById[report.id];
                    const reportTextStatus = reportTextSaveStatusById[report.id];
                    const hasReportTextChanges = reportTextDraft !== savedReportText;
                    const reportTextStatusLabel = reportTextError ?
                        'Could not save' :
                        isSavingReportText || reportTextStatus === 'saving' ?
                            'Saving...' :
                            reportTextStatus === 'pending' || hasReportTextChanges ?
                                'Unsaved changes' :
                                reportTextStatus === 'saved' ?
                                    'Saved' :
                                    '';

                    return (
                        <CollapsiblePanel
                            key={report.id}
                            title={reportTitle}
                            defaultCollapsed={reportIndex > 0}
                            headerActions={(
                                <TableRowEllipsisMenuButton
                                    ariaLabel={`Options for ${getReportTitle(report)}`}
                                    options={[{
                                        label: isDeletingReport ? 'Deleting...' : 'Delete',
                                        destructive: true,
                                        disabled: isDeletingReport,
                                        onClick: () => void handleDeleteReport(report),
                                    }, {
                                        label: 'Download',
                                        disabled: isDeletingReport || !reportHasText || isSavingReportText || hasReportTextChanges,
                                        onClick: () => handleDownloadReport(report),
                                    }]}
                                />
                            )}
                        >
                            {!!report.attachmentWarnings?.length && (
                                <div style={styles.reportAttachmentWarnings}>
                                    <strong>File content needs review</strong>
                                    <ul>{report.attachmentWarnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
                                </div>
                            )}
                            {report.status === 'processing' ? (
                                <div style={styles.helperText} role='status'>{report.attachmentProgress || 'Report processing...'}</div>
                            ) : report.status === 'error' ? (
                                <div style={styles.reportGenerationError}>{reportError}</div>
                            ) : (
                                <div style={styles.reportEditorPanel}>
                                    {!!reportTextStatusLabel && (
                                        <div style={styles.reportEditorStatusRow}>
                                            <span style={{
                                                ...styles.sessionNotesStatus,
                                                ...(reportTextError ? styles.sessionNotesStatusError : {}),
                                            }}>
                                                {reportTextStatusLabel}
                                            </span>
                                        </div>
                                    )}
                                    <RichTextEditor
                                        value={reportTextDraft}
                                        onChange={(nextReportText: string) => handleReportTextChange(
                                            report.id,
                                            nextReportText,
                                            savedReportText
                                        )}
                                        onBlur={(nextReportText: string) => handleReportTextBlur(report.id, nextReportText, savedReportText)}
                                        disabled={isDeletingReport}
                                        placeholder='No report text available.'
                                        ariaLabel={`Report text for ${getReportTitle(report)}`}
                                        minHeight='280px'
                                        maxHeight='none'
                                        editorStyle={{
                                            fontSize: `${Math.round(14 * reportTextZoom * 10) / 10}px`,
                                        }}
                                    />
                                    {!!reportTextError && (
                                        <div style={styles.sessionNotesError}>{reportTextError}</div>
                                    )}
                                </div>
                            )}
                        </CollapsiblePanel>
                    );
                })
            )}
        </div>
    );

    return (
        <div style={styles.mainContent}>
            <div style={styles.toolbarRow}>
                <Button
                    onClick={onBack}
                    label='Back'
                    icon={require('./assets/icons/back_normal.svg').default}
                    type='secondary'
                />
            </div>

            <div style={styles.titleRow}>
                <h1 style={styles.title}>{displayName}</h1>
                {headerActions}
            </div>

            <TabsView
                key={`${timelineSubjectKey}-${tabs.length}-${visibleSelectedTabsItemIndex}`}
                options={tabs}
                selectedTabsItemIndex={visibleSelectedTabsItemIndex}
                style={styles.clientRecordTabs}
            />

            <div style={styles.tabContentScroll}>
                {selectedTabId === 'timeline' ?
                    timelineTabContent :
                    selectedTabId === 'reports' ?
                        reportsTabContent :
                        selectedTabId === 'content' ?
                            <ClientContentPanel
                                key={JSON.stringify([timelineSubjectKey, timelineProviderId, authenticatedUserId])}
                                state={clientContent.state}
                                headerActionContainer={contentHeaderActionContainer}
                                onRetry={clientContent.retry}
                                loadPlanCatalogue={props.onLoadClientPlanCatalogue && selectedClientId ?
                                    () => props.onLoadClientPlanCatalogue!(selectedClientId) : undefined}
                                onSubmitPlan={props.onSubmitClientPlanOverride && selectedClientId ?
                                    (goalIds) => props.onSubmitClientPlanOverride!(selectedClientId, goalIds) : undefined}
                                onGeneratePlan={props.onGenerateClientPlan && selectedClientId ?
                                    (instructions) => props.onGenerateClientPlan!(selectedClientId, instructions) : undefined}
                            /> :
                            recordTabContent}
            </div>

            {!isTeamTimeline && timelineSubjectId && uploadDialogContext?.context === uploadContextKey &&
                (uploadDialogContext.kind === 'media' ? canGenerateClientReports : canUseTimelineDocuments) && (
                <TimelineUploadDialog
                    key={uploadContextKey + uploadDialogContext.kind}
                    kind={uploadDialogContext.kind}
                    clientName={displayName}
                    onClose={() => setUploadDialogContext(null)}
                    onUpload={(file, options) => uploadDialogContext.kind === 'media' ? onImportSessionFile({
                        clientId: selectedClient.id!, clientName: displayName,
                    }, file, options) : onUploadTimelineFile({
                        clientId: selectedClient.id,
                        clientName: displayName,
                        title: file.name,
                        healthcareProviderId: timelineProviderId,
                        providerId: timelineProviderId,
                    }, file, options)}
                    onUploaded={(id, name) => {
                        setUploadDialogContext(null);
                        setUploadedTimelineFile({ id, name, context: uploadContextKey, kind: uploadDialogContext.kind });
                        setSelectedTabsItemIndex(timelineTabIndex);
                    }}
                />
            )}

            {canGenerateClientReports && showGenerateReportDialog && (
                <div style={styles.generateReportDialogOverlay}>
                    <div style={styles.generateReportDialog}>
                        <h3 style={styles.generateReportDialogTitle}>Generate report</h3>
                        {selectedReportTimelineItems.some((item) => item.kind === 'document' && item.document.kind === 'file') && (
                            <p style={styles.helperText}>The contents of selected files will be read and included as evidence. New files may take longer to process.</p>
                        )}
                        <div style={styles.generateReportFormField}>
                            <label style={styles.generateReportLabel}>Report template</label>
                            <select
                                value={selectedReportTemplateId}
                                onChange={(event) => setSelectedReportTemplateId(event.target.value)}
                                style={styles.generateReportSelect}
                                disabled={isGeneratingClientReport}
                            >
                                <option value=''>None</option>
                                {sortedReportTemplates.map((reportTemplate) => (
                                    <option key={reportTemplate.id} value={reportTemplate.id}>
                                        {reportTemplate.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.generateReportDialogButtons}>
                            <Button
                                onClick={() => {
                                    if (!isGeneratingClientReport) {
                                        setShowGenerateReportDialog(false);
                                        setSelectedReportTemplateId('');
                                    }
                                }}
                                label='Cancel'
                                type='secondary'
                                disabled={isGeneratingClientReport}
                            />
                            <Button
                                onClick={() => handleGenerateSelectedSessionsReport(selectedReportTemplate)}
                                label={isGeneratingClientReport ? 'Generating...' : 'Generate'}
                                type='primary'
                                disabled={isGeneratingClientReport}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    reportAttachmentWarnings: {
        padding: '12px',
        marginBottom: '12px',
        borderRadius: '6px',
        backgroundColor: '#fff7db',
        color: '#6b5100',
        fontSize: '14px',
    },
    mainContent: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        padding: '20px',
        overflow: 'hidden',
        backgroundColor: Theme.Colours.background,
    },
    toolbarRow: {
        display: 'flex',
        alignItems: 'flex-start',
        zIndex: 2,
        flexShrink: 0,
        padding: '0 0 12px 0',
        backgroundColor: Theme.Colours.background,
    },
    actionButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: '0px',
        flexWrap: 'wrap',
    },
    reportZoomButton: {
        width: '42px',
        height: '42px',
        padding: 0,
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'opacity 120ms ease',
    },
    reportZoomButtonDisabled: {
        opacity: 0.45,
        cursor: 'not-allowed',
    },
    hiddenFileInput: {
        display: 'none',
    },
    titleRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap',
        flexShrink: 0,
        marginTop: '16px',
        marginBottom: '20px',
    },
    title: {
        ...Theme.Fonts.h1,
        margin: 0,
        color: Theme.Colours.text,
    },
    clientRecordTabs: {
        flexShrink: 0,
        margin: '0 0 20px 0',
    },
    tabContentScroll: {
        // Keep the client header and tab controls fixed while each tab body scrolls independently.
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        paddingRight: '4px',
    },
    panelsLayout: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    topRow: {
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
    },
    detailsColumn: {
        flex: '1 1 560px',
        minWidth: '320px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    relationshipColumn: {
        flex: '0 1 320px',
        width: '320px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        maxWidth: '100%',
    },
    detailsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
    },
    engagementContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
    },
    engagementChartsRow: {
        display: 'flex',
        gap: '24px',
        flexWrap: 'wrap',
    },
    engagementChartColumn: {
        flex: '1 1 420px',
        minWidth: '320px',
    },
    assessmentsContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    fullWidthPanels: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '100%',
    },
    sponsorshipContent: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '20px',
        flexWrap: 'wrap',
    },
    sponsorshipSummary: {
        display: 'flex',
        flex: '1 1 520px',
        flexDirection: 'column',
        gap: '8px',
    },
    sponsorshipError: {
        ...Theme.Fonts.body,
        color: '#8a1c12',
        fontWeight: 600,
    },
    assessmentSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '18px 20px',
        borderRadius: '12px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
    },
    assessmentTitle: {
        ...Theme.Fonts.title2,
        margin: 0,
        color: Theme.Colours.text,
        fontWeight: 600,
    },
    assessmentSplit: {
        display: 'flex',
        gap: '24px',
        alignItems: 'stretch',
        flexWrap: 'wrap',
    },
    assessmentChartColumn: {
        flex: '1 1 360px',
        minWidth: '320px',
    },
    assessmentTextColumn: {
        flex: '1 1 320px',
        minWidth: '280px',
        padding: '16px 18px',
        borderRadius: '12px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.workArea,
        display: 'flex',
        alignItems: 'flex-start',
    },
    assessmentTextContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%',
    },
    assessmentDateLabel: {
        ...Theme.Fonts.body,
        fontSize: '12px',
        fontWeight: 600,
        color: 'rgba(0, 0, 0, 0.55)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        alignSelf: 'flex-end',
        textAlign: 'right',
    },
    assessmentText: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        width: '100%',
    },
    clientDetailsContent: {
        paddingLeft: '32px',
        paddingRight: '24px',
    },
    detailRow: {
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
    },
    detailLabel: {
        ...Theme.Fonts.title2,
        fontWeight: 600,
        color: Theme.Colours.text,
        minWidth: '170px',
        paddingTop: '10px',
    },
    detailValue: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        flex: 1,
        minWidth: '220px',
        padding: '10px 0',
        wordBreak: 'break-word',
    },
    detailInput: {
        ...Theme.Fonts.body,
        flex: 1,
        marginLeft: 0,
        minWidth: '220px',
        padding: '10px 14px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box',
    },
    detailPhoneInputGroup: {
        flex: 1,
        minWidth: '220px',
        marginLeft: 0,
    },
    detailSelect: {
        ...Theme.Fonts.body,
        flex: 1,
        marginLeft: 0,
        minWidth: '220px',
        padding: '10px 14px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box',
    },
    speechNeedsCheckboxList: {
        flex: 1,
        minWidth: '220px',
        maxHeight: '180px',
        overflowY: 'auto' as const,
        padding: '8px 12px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        boxSizing: 'border-box' as const,
    },
    speechNeedCheckboxLabel: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minHeight: '34px',
        cursor: 'pointer',
    },
    detailTextarea: {
        ...Theme.Fonts.body,
        flex: 1,
        minWidth: '220px',
        minHeight: '120px',
        padding: '10px 14px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box',
        resize: 'vertical',
    },
    checkboxLabel: {
        ...Theme.Fonts.body,
        flex: 1,
        minWidth: '220px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: Theme.Colours.text,
        padding: '10px 0',
    },
    checkboxInput: {
        margin: 0,
        width: '16px',
        height: '16px',
        flexShrink: 0,
    },
    relatedList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
    },
    relatedItemButton: {
        appearance: 'none',
        WebkitAppearance: 'none',
        width: '100%',
        margin: 0,
        padding: '14px 16px',
        borderRadius: '10px',
        backgroundColor: Theme.Colours.background,
        border: Theme.Colours.border,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, transform 0.2s ease',
    },
    relatedItemContent: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flex: 1,
    },
    relatedChevron: {
        color: Theme.Colours.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    relatedName: {
        ...Theme.Fonts.title2,
        color: Theme.Colours.text,
        fontWeight: 600,
    },
    relatedMeta: {
        ...Theme.Fonts.body,
        color: 'rgba(0, 0, 0, 0.7)',
        marginTop: '4px',
    },
    deleteSection: {
        marginTop: '32px',
        paddingTop: '20px',
        borderTop: Theme.Colours.border,
    },
    deleteButton: {
        backgroundColor: '#ffebee',
        color: '#d32f2f',
        border: '1px solid #ef5350',
    },
    sessionsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        paddingBottom: '20px',
    },
    reportsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        paddingBottom: '20px',
    },
    reportGenerationError: {
        ...Theme.Fonts.body,
        padding: '12px 14px',
        color: '#d32f2f',
        border: '1px solid rgba(211, 47, 47, 0.26)',
        borderRadius: '6px',
        backgroundColor: '#ffebee',
    },
    emptySessionsMessage: {
        ...Theme.Fonts.body,
        padding: '24px',
        color: Theme.Colours.text,
        opacity: 0.75,
        textAlign: 'center',
        border: Theme.Colours.border,
        borderRadius: '8px',
        backgroundColor: Theme.Colours.workArea,
    },
    sessionTranscriptItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '18px 20px',
        border: Theme.Colours.border,
        borderRadius: '8px',
        backgroundColor: Theme.Colours.workArea,
    },
    sessionTranscriptHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap',
        paddingBottom: '12px',
        borderBottom: Theme.Colours.border,
    },
    sessionTranscriptHeaderCollapsed: {
        paddingBottom: 0,
        borderBottom: 'none',
    },
    sessionTitleGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        minWidth: 0,
    },
    sessionHeaderActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flexShrink: 0,
    },
    sessionDateTitle: {
        ...Theme.Fonts.title2,
        margin: 0,
        color: Theme.Colours.text,
        fontWeight: 700,
    },
    sessionDateButton: {
        ...Theme.Fonts.title2,
        appearance: 'none',
        WebkitAppearance: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'text',
        fontWeight: 700,
        margin: 0,
        padding: 0,
        textAlign: 'left',
    },
    sessionDateInput: {
        ...Theme.Fonts.title2,
        minWidth: '230px',
        maxWidth: '100%',
        padding: '6px 8px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        fontWeight: 700,
    },
    sessionSelectCheckbox: {
        margin: '0 10px 0 0',
        width: '18px',
        height: '18px',
        flexShrink: 0,
    },
    timelineTypeIcon: {
        width: '30px',
        height: '30px',
        borderRadius: '7px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    timelineTypeIconSession: {
        backgroundColor: 'rgba(220, 38, 38, 0.10)',
        color: '#b91c1c',
    },
    timelineTypeIconNote: {
        backgroundColor: 'rgba(124, 58, 237, 0.10)',
        color: '#6d28d9',
    },
    timelineTypeIconFile: {
        backgroundColor: 'rgba(2, 132, 199, 0.10)',
        color: '#0369a1',
    },
    sessionTitleSeparator: {
        ...Theme.Fonts.title2,
        color: Theme.Colours.text,
        fontWeight: 700,
        margin: 0,
        whiteSpace: 'pre',
    },
    sessionTitleButton: {
        ...Theme.Fonts.title2,
        appearance: 'none',
        WebkitAppearance: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'text',
        fontWeight: 700,
        margin: 0,
        padding: 0,
        textAlign: 'left',
    },
    sessionTitleInput: {
        ...Theme.Fonts.title2,
        minWidth: '220px',
        maxWidth: '100%',
        padding: '6px 38px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        fontWeight: 700,
    },
    sessionOptionsContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    sessionOptionsButton: {
        ...Theme.Fonts.body,
        width: '34px',
        height: '34px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        cursor: 'pointer',
        fontSize: '18px',
        lineHeight: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
    },
    sessionOptionsEllipsis: {
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: 1,
        transform: 'translateY(-4px)',
    },
    sessionCollapseButton: {
        ...Theme.Fonts.body,
        width: '34px',
        height: '34px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
    },
    sessionCollapseIcon: {
        color: Theme.Colours.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s ease',
    },
    sessionOptionsMenu: {
        position: 'absolute',
        top: '40px',
        right: 0,
        zIndex: 4,
        minWidth: '150px',
        padding: '6px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.12)',
    },
    sessionOptionsMenuItem: {
        ...Theme.Fonts.body,
        width: '100%',
        appearance: 'none',
        WebkitAppearance: 'none',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: '#d32f2f',
        cursor: 'pointer',
        padding: '8px 10px',
        textAlign: 'left',
        fontWeight: 600,
    },
    sessionOptionsMenuItemNeutral: {
        color: Theme.Colours.text,
    },
    timelineFileMeta: {
        ...Theme.Fonts.body,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        color: 'rgba(0, 0, 0, 0.62)',
        fontSize: '13px',
        fontWeight: 600,
    },
    timelineFileViewer: {
        border: Theme.Colours.border,
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: Theme.Colours.background,
    },
    sessionNotesSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '12px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
    },
    timelineClinicalNoteBody: {
        // Standalone note cards already have the outer timeline frame, so keep the editor unframed.
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    timelineClinicalNoteStatusRow: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    sessionNotesHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap',
    },
    sessionNotesLabel: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        fontWeight: 700,
    },
    sessionNotesStatus: {
        ...Theme.Fonts.body,
        color: 'rgba(0, 0, 0, 0.58)',
        fontSize: '13px',
        fontWeight: 600,
    },
    sessionNotesStatusError: {
        color: '#d32f2f',
    },
    sessionNotesError: {
        ...Theme.Fonts.body,
        color: '#d32f2f',
        fontSize: '13px',
    },
    sessionSummarySection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
    },
    generateSummaryButton: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        height: '42px',
        padding: '8px 16px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        cursor: 'pointer',
        fontWeight: 600,
    },
    generateSummaryButtonDisabled: {
        cursor: 'wait',
        opacity: 0.62,
    },
    sessionSummaryError: {
        ...Theme.Fonts.body,
        color: '#d32f2f',
        fontSize: '13px',
    },
    summaryMarkdownPanel: {
        ...Theme.Fonts.body,
        width: '100%',
        minHeight: '260px',
        boxSizing: 'border-box',
        maxHeight: '620px',
        overflowY: 'auto',
        padding: '12px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        lineHeight: 1.5,
    },
    reportEditorPanel: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    reportEditorStatusRow: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    generateReportDialogOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    generateReportDialog: {
        display: 'flex',
        flexDirection: 'column',
        width: 'min(440px, calc(100vw - 40px))',
        padding: '28px',
        borderRadius: '10px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.workArea,
        boxShadow: '0 10px 34px rgba(0, 0, 0, 0.18)',
        boxSizing: 'border-box',
    },
    generateReportDialogTitle: {
        ...Theme.Fonts.h2,
        margin: '0 0 22px 0',
        color: Theme.Colours.text,
    },
    generateReportFormField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    generateReportLabel: {
        ...Theme.Fonts.body,
        fontWeight: 700,
        color: Theme.Colours.text,
    },
    generateReportSelect: {
        ...Theme.Fonts.body,
        width: '100%',
        height: '42px',
        marginLeft: '-4px',
        padding: '8px 0px 8px 12px',
        borderRadius: '6px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box',
    },
    generateReportDialogButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '28px',
    },
    sessionTranscriptBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxHeight: '546px',
        overflowY: 'auto',
        paddingRight: '4px',
    },
    transcriptToggleRow: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
    transcriptToggleButton: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        cursor: 'pointer',
        fontWeight: 600,
    },
    transcriptToggleChevron: {
        fontSize: '14px',
        lineHeight: 1,
        width: '14px',
        textAlign: 'center',
    },
    transcriptTurnCount: {
        color: 'rgba(0, 0, 0, 0.55)',
        fontSize: '12px',
        fontWeight: 500,
    },
    sessionPlaceholderText: {
        ...Theme.Fonts.body,
        margin: 0,
        color: Theme.Colours.text,
        opacity: 0.72,
    },
    transcriptPlaceholderContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
    },
    retryTranscriptButton: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        padding: '8px 12px',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: Theme.Colours.buttonBackground,
        color: Theme.Colours.buttonText,
        cursor: 'pointer',
        fontWeight: 600,
    },
    retryTranscriptButtonDisabled: {
        cursor: 'wait',
        opacity: 0.62,
    },
    transcriptRetryError: {
        ...Theme.Fonts.body,
        margin: 0,
        color: '#d32f2f',
        fontSize: '13px',
    },
    utteranceParagraph: {
        ...Theme.Fonts.body,
        margin: 0,
        display: 'flex',
        gap: '10px',
        alignItems: 'baseline',
        position: 'relative',
        padding: '10px 48px 10px 12px',
        borderLeft: '4px solid rgba(0, 0, 0, 0.18)',
        borderRadius: '6px',
        color: Theme.Colours.text,
        backgroundColor: Theme.Colours.background,
        lineHeight: 1.55,
    },
    therapistUtterance: {
        borderLeftColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.08)',
    },
    clientUtterance: {
        borderLeftColor: '#f97316',
        backgroundColor: 'rgba(249, 115, 22, 0.1)',
    },
    activeUtterance: {
        backgroundColor: '#fff3bf',
        boxShadow: '0 0 0 2px rgba(217, 144, 0, 0.22)',
    },
    utteranceSpeakerLabel: {
        flex: '0 0 auto',
        minWidth: '78px',
        fontSize: '12px',
        fontWeight: 700,
        textTransform: 'uppercase',
        color: 'rgba(0, 0, 0, 0.62)',
    },
    therapistSpeakerLabel: {
        color: '#1d4ed8',
    },
    clientSpeakerLabel: {
        color: '#c2410c',
    },
    utteranceText: {
        flex: 1,
        minWidth: 0,
    },
    utteranceOptionsContainer: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: 'inline-flex',
        alignItems: 'center',
    },
    utteranceOptionsButton: {
        ...Theme.Fonts.body,
        width: '28px',
        height: '28px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        padding: 0,
        pointerEvents: 'none',
        transition: 'opacity 120ms ease',
    },
    utteranceOptionsButtonVisible: {
        opacity: 1,
        pointerEvents: 'auto',
    },
    utteranceOptionsEllipsis: {
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: 1,
        transform: 'translateY(-3px)',
    },
    utteranceOptionsMenu: {
        position: 'absolute',
        top: '34px',
        right: 0,
        zIndex: 5,
        minWidth: '280px',
        padding: '6px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.12)',
    },
    utteranceOptionsMenuItem: {
        ...Theme.Fonts.body,
        width: '100%',
        boxSizing: 'border-box',
        appearance: 'none',
        WebkitAppearance: 'none',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        padding: '8px 10px',
        textAlign: 'left',
        fontWeight: 600,
    },
    utteranceOptionsMenuItemWithSubmenu: {
        ...Theme.Fonts.body,
        width: '100%',
        boxSizing: 'border-box',
        appearance: 'none',
        WebkitAppearance: 'none',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '8px 10px',
        textAlign: 'left',
        fontWeight: 600,
    },
    utteranceOptionsMenuItemHighlighted: {
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        boxShadow: 'inset 0 0 0 999px rgba(129, 126, 228, 0.14)',
        overflow: 'hidden',
    },
    utteranceSubmenuChevron: {
        fontSize: '18px',
        lineHeight: 1,
        opacity: 0.6,
    },
    utterancePeopleMenu: {
        position: 'absolute',
        top: '6px',
        right: 'calc(100% + 6px)',
        zIndex: 6,
        minWidth: '220px',
        maxHeight: '260px',
        overflowY: 'auto',
        padding: '6px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.12)',
    },
    utterancePersonMenuItem: {
        ...Theme.Fonts.body,
        width: '100%',
        boxSizing: 'border-box',
        appearance: 'none',
        WebkitAppearance: 'none',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '8px 10px',
        textAlign: 'left',
        fontWeight: 600,
    },
    utterancePersonMeta: {
        color: 'rgba(0, 0, 0, 0.58)',
        fontSize: '12px',
        fontWeight: 500,
    },
    helperText: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        opacity: 0.75,
    },
};
